import React, { useMemo, useState, useCallback, useEffect, createContext, useContext } from 'preact/compat';

/**
 * This file contains constants used throughout the application.
 * It includes environment variables, product constants, and cart-related constants.
 */
const constants = {
    // Environments
    DEV_ENV: 'dev',
    UAT_ENV: 'uat',
    PROD_ENV: 'prod',
    MOCK_ENV: 'mock',
    // Product constants
    DEFAULT_MIN_LICENSES: '1',
    DEFAULT_MAX_LICENSES: '10',
    // Annual terms
    ANNUAL_SUBSCRIPTION_TERM: 'Yearly',
    ANNUAL_SUBSCRIPTION_DISPLAY_TERM: '1 Year Subscription',
    // Monthly terms
    MONTHLY_SUBSCRIPTION_TERM: 'Monthly',
    MONTHLY_SUBSCRIPTION_DISPLAY_TERM: '1 month',
    THREE_MONTHS_TRIAL_DISPLAY_TERM: '3 Month Trial',
    FREE_ONE_MONTH_TRIAL_DISPLAY_TERM: 'Trial (Try 1 Month free)',
    FREE_THREE_MONTHS_TRIAL_DISPLAY_TERM: 'Trial (Try 3 Months free)',
    // Three year terms
    THREE_YEAR_SUBSCRIPTION_DISPLAY_TERM: '3 Year Subscription',
    CART_BUTTON: 'cart',
    CART_ERROR_FETCHING_CART_DATA: 'fetchingCartData',
    CART_ERROR_INITIALIZING_CART: 'initializingCart',
    CART_ERROR_PRESERVING_GUEST_CART: 'preservingGuestCart',
    CART_CLEARED_AFTER_CHECKOUT: 'cartClearedAfterCheckout',
    // Product types
    PRODUCT: 'product',
    BUNDLE: 'bundle',
    DYNAMIC_KIT: 'kit',
    // Cart Errors
    CART_NOT_FOUND: 'cart not found',
    USER_CART_MISTMATCH_ERR: 'user cart mismatch',
    NO_CART_ID_ERROR: 'No cartId available, cart not initialized',
    SAME_PRODUCT_DIFFERENT_TERM: 'SAME_PRODUCT_DIFFERENT_TERM',
    // Cart constants
    CART_STORAGE_KEY_SUFFIX: 'cartId',
    ECOMMERCE_PRODUCT_FETCHED: 'productFetched',
    CART_COOKIE_MAX_AGE: 2592000,
    FULL_CART_QUERY_PARAM: 'fullCart',
};
const MIMICKED_ECBE_EMPTY_CART = {
    cartId: '',
    currency: '',
    createdDate: '',
    expiryDate: null,
    assistedCart: false,
    hasPreviousPOA: false,
    cartItems: [],
    promotionCodes: [],
    totalQuantity: 0,
    subTotal: 0,
    totalDiscountValue: 0,
    totalTax: 0,
    grandTotal: 0,
    selectedPaymentMethod: null,
    checkoutConfiguration: {
        hasEca: false,
        hasAX: false,
        hasFloating: false,
        hasNodeLocked: false,
        hasInsightHub: false,
        hasIndustrialEdge: false,
    },
    soldThrough: '',
    cartSource: '',
    portfolio: null,
    rejectionReason: [],
};
const MIMICKED_EMPTY_CART = {
    message: '',
    errorCode: '',
    currency: '',
    url: '',
    items: [],
    totalQuantity: 0,
    webkey: '',
    tax: 0,
    total: 0,
    discount: 0,
    subTotal: 0,
    accountUrl: '',
    billTo: {
        country: '',
        region: '',
        postalCode: '',
        address3: null,
        address1: '',
        city: '',
        vatNumber: null,
        firstName: '',
        lastName: '',
        email: '',
        address2: null,
        company: '',
        phone: '',
        id: '',
    },
    promotionCodes: [],
    isCartPaid: false,
    id: '',
};
/**
 * DISW Tracking Event Names for DEX Tracking
 */
const CART_EVENT_LIST = {
    CART_VIEWED: 'Cart Viewed',
    CHECKOUT_STARTED: 'Checkout Started',
    COUPON_ENTERED: 'Coupon Entered',
    COUPON_APPLIED: 'Coupon Applied',
    COUPON_DENIED: 'Coupon Denied',
    COUPON_REMOVED: 'Coupon Removed',
    PRODUCT_ADDED: 'Product Added',
    PRODUCT_REMOVED: 'Product Removed',
    CHECKOUT_STEP_VIEWED: 'Checkout Step Viewed',
    CHECKOUT_STEP_COMPLETED: 'Checkout Step Completed',
    ORDER_COMPLETED: 'Order Completed',
    PRODUCT_VIEWED: 'Product Viewed',
    PRODUCT_CLICKED: 'Product Clicked',
    PRODUCT_LIST_VIEWED: 'Product List Viewed',
};
/**
 * Represents which display subscription terms we support
 */
[
    constants.MONTHLY_SUBSCRIPTION_DISPLAY_TERM.toLowerCase(),
    constants.ANNUAL_SUBSCRIPTION_DISPLAY_TERM.toLowerCase(),
    constants.THREE_YEAR_SUBSCRIPTION_DISPLAY_TERM.toLowerCase(),
];
/**
 * Represents which subscription terms we support
 */
[constants.MONTHLY_SUBSCRIPTION_TERM.toLowerCase(), constants.ANNUAL_SUBSCRIPTION_TERM.toLowerCase()];

/******************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */
/* global Reflect, Promise, SuppressedError, Symbol, Iterator */


function __awaiter(thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
}

typeof SuppressedError === "function" ? SuppressedError : function (error, suppressed, message) {
    var e = new Error(message);
    return e.name = "SuppressedError", e.error = error, e.suppressed = suppressed, e;
};

/**
 * Abstract class which defines the transformer which is responsible for transforming objects
 * from upstream eCommerce platforms (DEX, ECBE, etc) into generic objects to be passed to the front-end to avoid
 * polluting our components with specific platform models and data.
 *
 */
class eCommerceTransformer {
}

/**
 * Transformer class for transforming ECBE objects into generic objects.
 * This class extends the eCommerceTransformer and implements the transformProduct
 * method to convert ECBE-specific objects into generic objects.
 */
class ECBETransformer extends eCommerceTransformer {
    /**
     * Transform a shopping cart from ECBE to our generic shopping cart format
     * @param cart - The ECBE shopping cart data
     * @returns The transformed generic shopping cart
     */
    transformShoppingCart(ecbeCart) {
        var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o, _p, _q, _r;
        if (!ecbeCart || typeof ecbeCart !== 'object') {
            return {
                cartId: '',
                currency: '',
                createdDate: '',
                expiryDate: null,
                assistedCart: false,
                hasPreviousPOA: false,
                cartItems: [],
                promotionCodes: [],
                totalQuantity: 0,
                subTotal: 0,
                totalDiscountValue: 0,
                totalTax: 0,
                grandTotal: 0,
                selectedPaymentMethod: null,
                checkoutConfiguration: {
                    hasEca: false,
                    hasAX: false,
                    hasFloating: false,
                    hasNodeLocked: false,
                    hasInsightHub: false,
                    hasIndustrialEdge: false,
                },
                soldThrough: '',
                cartSource: '',
                portfolio: null,
                rejectionReason: [],
            };
        }
        // Return the ECBE cart data as-is, since it already matches the EcbeCart interface
        return {
            cartId: (_a = ecbeCart.cartId) !== null && _a !== void 0 ? _a : '',
            currency: (_b = ecbeCart.currency) !== null && _b !== void 0 ? _b : '',
            createdDate: (_c = ecbeCart.createdDate) !== null && _c !== void 0 ? _c : '',
            expiryDate: (_d = ecbeCart.expiryDate) !== null && _d !== void 0 ? _d : null,
            assistedCart: (_e = ecbeCart.assistedCart) !== null && _e !== void 0 ? _e : false,
            hasPreviousPOA: (_f = ecbeCart.hasPreviousPOA) !== null && _f !== void 0 ? _f : false,
            cartItems: Array.isArray(ecbeCart.cartItems) ? ecbeCart.cartItems : [],
            promotionCodes: Array.isArray(ecbeCart.promotionCodes) ? ecbeCart.promotionCodes : [],
            totalQuantity: (_g = ecbeCart.totalQuantity) !== null && _g !== void 0 ? _g : 0,
            subTotal: (_h = ecbeCart.subTotal) !== null && _h !== void 0 ? _h : 0,
            totalDiscountValue: (_j = ecbeCart.totalDiscountValue) !== null && _j !== void 0 ? _j : 0,
            totalTax: (_k = ecbeCart.totalTax) !== null && _k !== void 0 ? _k : 0,
            grandTotal: (_l = ecbeCart.grandTotal) !== null && _l !== void 0 ? _l : 0,
            selectedPaymentMethod: (_m = ecbeCart.selectedPaymentMethod) !== null && _m !== void 0 ? _m : null,
            checkoutConfiguration: (_o = ecbeCart.checkoutConfiguration) !== null && _o !== void 0 ? _o : {
                hasEca: false,
                hasAX: false,
                hasFloating: false,
                hasNodeLocked: false,
                hasInsightHub: false,
                hasIndustrialEdge: false,
            },
            soldThrough: (_p = ecbeCart.soldThrough) !== null && _p !== void 0 ? _p : '',
            cartSource: (_q = ecbeCart.cartSource) !== null && _q !== void 0 ? _q : '',
            portfolio: (_r = ecbeCart.portfolio) !== null && _r !== void 0 ? _r : null,
            rejectionReason: Array.isArray(ecbeCart.rejectionReason) ? ecbeCart.rejectionReason : [],
        };
    }
    /**
     * Transform composite products from ECBE cart childProducts to generic composite products.
     * This method is used to transform products that are already in the cart and have their
     * cart-specific properties (pricing, quantity) already defined.
     *
     * @param items - Array of child products from ECBE cart response
     * @param compositeProductsLoading - Loading state flag for composite products
     * @returns Transformed composite products object formatted for display in the UI
     */
    transformCartCompositeProducts({ items, compositeProductsLoading }) {
        if (!items || items.length === 0) {
            return undefined;
        }
        const transformedComposites = items.map((childProduct) => {
            var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o, _p, _q, _r, _s, _t, _u, _v, _w;
            // Extract pricing information from the child product
            const pricingOption = {
                id: ((_a = childProduct.productDetails) === null || _a === void 0 ? void 0 : _a.productId) || '',
                price: ((_c = (_b = childProduct.pricing) === null || _b === void 0 ? void 0 : _b.subTotal) === null || _c === void 0 ? void 0 : _c.toString()) || '0',
                currency: ((_d = childProduct.pricing) === null || _d === void 0 ? void 0 : _d.currency) || 'USD',
                displayTerm: ((_f = (_e = childProduct.attributes) === null || _e === void 0 ? void 0 : _e.find((attr) => attr.name === 'Subscription Term')) === null || _f === void 0 ? void 0 : _f.displayValue) || 'Annual',
                autoRenew: ((_g = childProduct.pricing) === null || _g === void 0 ? void 0 : _g.autoRenew) || false,
                term: ((_h = childProduct.pricing) === null || _h === void 0 ? void 0 : _h.term) || 'annually upfront',
            };
            // Determine if the product is required based on kit type or minimum quantity
            const isRequired = ((_j = childProduct.productDetails) === null || _j === void 0 ? void 0 : _j.kitType) !== 'Add-on' || (((_l = (_k = childProduct.productDetails) === null || _k === void 0 ? void 0 : _k.quantityRules) === null || _l === void 0 ? void 0 : _l.minimum) && parseFloat(childProduct.productDetails.quantityRules.minimum) > 0);
            return {
                productName: ((_m = childProduct.productDetails) === null || _m === void 0 ? void 0 : _m.productName) || '',
                productId: ((_o = childProduct.productDetails) === null || _o === void 0 ? void 0 : _o.productId) || '',
                pricingOption,
                numberSeats: ((_p = childProduct.pricing) === null || _p === void 0 ? void 0 : _p.quantity) || 1,
                setNumberSeats: (seats) => {
                    // This will be overridden by the hook implementation
                },
                minimumQuantity: ((_r = (_q = childProduct.productDetails) === null || _q === void 0 ? void 0 : _q.quantityRules) === null || _r === void 0 ? void 0 : _r.minimum) || (isRequired ? '1' : '0'),
                maximumQuantity: ((_t = (_s = childProduct.productDetails) === null || _s === void 0 ? void 0 : _s.quantityRules) === null || _t === void 0 ? void 0 : _t.maximum) || '100',
                required: isRequired,
                // Additional properties from the generic Product interface
                name: ((_u = childProduct.productDetails) === null || _u === void 0 ? void 0 : _u.productName) || '',
                productSku: ((_v = childProduct.productDetails) === null || _v === void 0 ? void 0 : _v.productSku) || '',
                selectedPricingOption: pricingOption,
                pricingOptions: [pricingOption],
                productLoading: false,
                paymentOptions: ((_w = childProduct.productDetails) === null || _w === void 0 ? void 0 : _w.paymentOptions) || [],
                preRequisites: {},
                soldThrough: childProduct.soldThrough || '',
                portfolio: childProduct.portfolio || '',
                termsAndConditions: [],
                productTypology: childProduct.productTypology || { class: '', classification: '', type: '' },
                sellerInfo: childProduct.sellerInfo || null,
            };
        });
        return {
            items: transformedComposites,
            compositeProductsLoading,
        };
    }
    /**
     * Transform composite products directly from the catalog/API response.
     * This method processes the compositeProducts array directly from the product API response
     * when first loading a dynamic kit product. It handles the simpler structure defined by
     * the EcbeCompositeProduct interface, with fields like name, productId, and listPrice.
     *
     * @param compositeProducts - Array of composite products from ECBE product catalog response
     * @param compositeProductsLoading - Loading state flag for composite products
     * @returns Transformed composite products object formatted for display in the UI
     */
    transformCatalogCompositeProducts({ compositeProducts, compositeProductsLoading }) {
        if (!compositeProducts || compositeProducts.length === 0) {
            return undefined;
        }
        const transformedComposites = [];
        // Process each composite product
        compositeProducts.forEach((composite) => {
            var _a, _b;
            // Extract pricing information
            const pricingOption = {
                id: composite.productId,
                price: composite.listPrice || '0',
                currency: 'USD', // Currency MUST be set to avoid NumberFormat errors in price formatting
                displayTerm: 'Annual', // Default if not provided in the API
                autoRenew: true, // Default to true for subscription products
                term: 'annually upfront', // Default term
            };
            // Determine if the product is required
            // A product is required if it has a minimum quantity > 0
            const minimumQuantity = ((_a = composite.minimumQuantity) === null || _a === void 0 ? void 0 : _a.toString()) || '0';
            const isRequired = (composite.minimumQuantity || 0) > 0;
            // Create composite product
            const compositeProduct = {
                productName: composite.name,
                productId: composite.productId,
                pricingOption,
                numberSeats: composite.minimumQuantity || 1,
                setNumberSeats: (seats) => {
                    // This will be overridden by the hook implementation
                },
                minimumQuantity: minimumQuantity,
                maximumQuantity: ((_b = composite.maximumQuantity) === null || _b === void 0 ? void 0 : _b.toString()) || '100',
                required: isRequired,
                // Additional properties from the generic Product interface
                name: composite.name,
                productSku: composite.productId, // Using productId as SKU if SKU not provided
                selectedPricingOption: pricingOption,
                pricingOptions: [pricingOption],
                productLoading: false,
                paymentOptions: [],
                preRequisites: {},
                soldThrough: '', // Not provided in composite data
                portfolio: '', // Not provided in composite data
                termsAndConditions: [],
                productTypology: { class: '', classification: '', type: '' },
                sellerInfo: null, // Not provided in composite data
                currentCartQuantity: composite.currentCartQuantity || 0,
            };
            transformedComposites.push(compositeProduct);
        });
        return {
            items: transformedComposites,
            compositeProductsLoading,
        };
    }
    /**
     * Transform a payment option from ECBE to our generic payment option format.
     * This is a required implementation of the abstract method from eCommerceTransformer.
     *
     * @param paymentOption - The ECBE payment option data
     * @returns The transformed generic payment option
     */
    transformPaymentOption(paymentOption) {
        return undefined;
    }
    /**
     * Transform an ECBE product to a generic product.
     * @param object - The ECBE product API response to transform.
     * @returns The transformed generic product with minimal properties needed for the front-end.
     */
    transformProduct(object) {
        var _a, _b, _c;
        if (!object) {
            return undefined;
        }
        const minLicenses = ((_a = object.quantityRules) === null || _a === void 0 ? void 0 : _a.minimum) ? object.quantityRules.minimum : '1';
        const maxLicenses = ((_b = object.quantityRules) === null || _b === void 0 ? void 0 : _b.maximum) ? object.quantityRules.maximum : '100';
        const pricingOptions = (((_c = object.variations) === null || _c === void 0 ? void 0 : _c.items) || []).map((item) => {
            var _a, _b, _c;
            const subscriptionTermAttr = (_a = item.attributes) === null || _a === void 0 ? void 0 : _a.find((attr) => attr.name === 'Subscription Term');
            return {
                id: item.id || '',
                price: ((_b = item.pricing) === null || _b === void 0 ? void 0 : _b.value) || '0',
                currency: ((_c = item.pricing) === null || _c === void 0 ? void 0 : _c.currency) || 'USD',
                displayTerm: (subscriptionTermAttr === null || subscriptionTermAttr === void 0 ? void 0 : subscriptionTermAttr.displayValue) || '',
                autoRenew: true, // Update if you have a way to infer this
                term: (subscriptionTermAttr === null || subscriptionTermAttr === void 0 ? void 0 : subscriptionTermAttr.value) || '',
            };
        });
        const product = {
            name: object.name,
            minLicenses,
            maxLicenses,
            pricingOptions,
            productSku: object.productSku,
            paymentOptions: object.paymentOptions || [],
            preRequisites: object.preRequisites || {},
            soldThrough: object.soldThrough || '',
            portfolio: object.portfolio || '',
            termsAndConditions: object.termsAndConditions || [],
            productTypology: object.productTypology || { class: '', classification: '', type: '' },
            sellerInfo: object.sellerInfo || null,
            licenseType: object.licenseType || '', // e.g., "One Time"
            userType: object.userType || '', // e.g., "Floating" etc.
            // Add more properties as needed, but only include the ones that are necessary.
        };
        return product;
    }
    /**
     * Transform an ECBE pricing option to a generic pricing option.
     * @param item - The ECBE variation item to transform.
     * @returns The transformed generic pricing option.
     */
    transformPricingOption(item) {
        var _a, _b, _c;
        if (!item)
            return undefined;
        const subscriptionTermAttr = (_a = item.attributes) === null || _a === void 0 ? void 0 : _a.find((attr) => attr.name === 'Subscription Term');
        return {
            id: item.id || '',
            price: ((_b = item.pricing) === null || _b === void 0 ? void 0 : _b.value) || '0',
            currency: ((_c = item.pricing) === null || _c === void 0 ? void 0 : _c.currency) || 'USD',
            displayTerm: (subscriptionTermAttr === null || subscriptionTermAttr === void 0 ? void 0 : subscriptionTermAttr.displayValue) || '',
            autoRenew: true, // Update if you have a way to infer this
            term: (subscriptionTermAttr === null || subscriptionTermAttr === void 0 ? void 0 : subscriptionTermAttr.value) || '',
        };
    }
}

/**
 * Fire the check-out started event when the user indicates they would like to proceed to check-out.
 * Note that no parameters are required. The cart analytics is context aware and will send the current shopping cart
 * data.
 * @param {Object} params
 * @param {*} [params.cartData] - The shopping cart data to be used for tracking. If not provided, uses the current shoppingCartData.
 * @returns {analytics}
 */
const checkoutStarted = ({ cartData }) => {
    try {
        const trackingPayload = Object.assign({ cart_id: cartData === null || cartData === void 0 ? void 0 : cartData.id, currency: cartData === null || cartData === void 0 ? void 0 : cartData.currency, discount: formatPrice$1(cartData === null || cartData === void 0 ? void 0 : cartData.discount), order_id: '', products: createProductTrackingObject(cartData), revenue: formatPrice$1(cartData === null || cartData === void 0 ? void 0 : cartData.subTotal) }, ((cartData === null || cartData === void 0 ? void 0 : cartData.promotionCodes[0]) && { coupon: cartData === null || cartData === void 0 ? void 0 : cartData.promotionCodes[0] }));
        return sendAnalytics({ event: CART_EVENT_LIST.CHECKOUT_STARTED, payload: trackingPayload });
    }
    catch (error) {
        console.error(`Something went wrong tracking 'Checkout Started' event : ${error}`);
    }
};
/**
 * Tracks when a user clicks on a product.
 *
 * @param {Object} params - The parameters for tracking the product click event.
 * @param {*} params.productData - The product data the user clicked.
 * @returns {void | analytics} Returns the result of sendAnalytics or void if no productData is provided.
 */
const productClicked = ({ productData }) => {
    var _a;
    if (!productData) {
        return;
    }
    try {
        const trackingPayload = {
            name: productData.name,
            sku: productData.sku,
            image_url: (_a = productData === null || productData === void 0 ? void 0 : productData.image) === null || _a === void 0 ? void 0 : _a.url,
            url: getCurrentUrl(),
            product_id: productData.sku,
            quantity: productData === null || productData === void 0 ? void 0 : productData.quantity,
        };
        return sendAnalytics({ event: CART_EVENT_LIST.PRODUCT_CLICKED, payload: trackingPayload });
    }
    catch (error) {
        console.error(`Something went wrong tracking 'Product Clicked' event : ${error}`);
    }
};
/**
 * Tracks when a user views a list of products.
 *
 * @param {Object} params - The parameters for tracking the product list view event.
 * @param {any[]} params.productData - The array of product data objects the user viewed.
 * @param {string} params.contentId - Identifier of the list in Contentful.
 * @returns {void | analytics} Returns the result of sendAnalytics or void if no productData is provided.
 */
const productListViewed = ({ productData, contentId }) => {
    if (!productData) {
        return;
    }
    try {
        const trackingPayload = {
            list_id: contentId,
            products: productData,
        };
        return sendAnalytics({ event: CART_EVENT_LIST.PRODUCT_LIST_VIEWED, payload: trackingPayload });
    }
    catch (error) {
        console.error(`Something went wrong tracking 'Product List Viewed' event : ${error}`);
    }
};
/**
 * Sends a tracking event when a promotion is accepted.
 *
 * @param {Object} params - The parameters for tracking the coupon applied event.
 * @param {string} params.couponName - The name or code of the coupon applied.
 * @param {*} [params.cartData] - The shopping cart data to be used for tracking.
 * @returns {void | analytics} Returns the result of sendAnalytics or void if tracking fails.
 */
const couponApplied = ({ couponName, cartData }) => {
    try {
        const trackingPayload = {
            cart_id: cartData === null || cartData === void 0 ? void 0 : cartData.id,
            coupon_id: couponName,
            coupon_name: couponName,
            order_id: '',
            discount: formatPrice$1(cartData === null || cartData === void 0 ? void 0 : cartData.discount),
        };
        return sendAnalytics({ event: CART_EVENT_LIST.COUPON_APPLIED, payload: trackingPayload });
    }
    catch (error) {
        console.error(`Something went wrong tracking 'Coupon Applied' event : ${error}`);
    }
};
/**
 * Sends a tracking event when a promotion code is denied.
 *
 * @param {Object} params - The parameters for tracking the coupon denied event.
 * @param {string} params.couponName - The name or code of the coupon denied.
 * @param {string} [params.reason] - The reason the coupon was denied (optional).
 * @param {*} [params.cartData] - The shopping cart data to be used for tracking.
 * @returns {void | analytics} Returns the result of sendAnalytics or void if tracking fails.
 */
const couponDenied = ({ couponName, reason, cartData }) => {
    try {
        const trackingPayload = {
            cart_id: cartData === null || cartData === void 0 ? void 0 : cartData.id,
            coupon_id: couponName,
            coupon_name: couponName,
            order_id: '',
            reason: reason || (cartData === null || cartData === void 0 ? void 0 : cartData.message),
        };
        return sendAnalytics({ event: CART_EVENT_LIST.COUPON_DENIED, payload: trackingPayload });
    }
    catch (error) {
        console.error(`Something went wrong tracking 'Coupon Denied' event : ${error}`);
    }
};
/**
 * Sends a tracking event when a checkout step is viewed.
 *
 * @param {Object} params - The parameters for tracking the checkout step viewed event.
 * @param {number} params.stepNumber - The number of the checkout step (0 = customerContactAndPayment, 1 = orderReview, 2 = orderProcessed).
 * @param {string} [params.selectedPaymentOption] - The payment option selected on a particular checkout step (if applicable).
 * @param {*} [params.cartData] - The shopping cart data to be used for tracking.
 * @returns {void | analytics} Returns the result of sendAnalytics or void if tracking fails.
 */
const checkoutStepViewed = ({ stepNumber, selectedPaymentOption = '', cartData }) => {
    if (!cartData) {
        return;
    }
    try {
        const trackingPayload = {
            checkout_id: cartData === null || cartData === void 0 ? void 0 : cartData.id,
            step: stepNumber + 1,
            payment_method: selectedPaymentOption,
        };
        return sendAnalytics({ event: CART_EVENT_LIST.CHECKOUT_STEP_VIEWED, payload: trackingPayload });
    }
    catch (error) {
        console.error(`Something went wrong tracking 'Checkout Step Viewed' event : ${error}`);
    }
};
/**
 * Sends a tracking event when a checkout step is completed.
 *
 * @param {Object} params - The parameters for tracking the checkout step completed event.
 * @param {number} params.stepNumber - The number of the checkout step (0 = customerContactAndPayment, 1 = orderReview, 2 = orderProcessed).
 * @param {string} [params.selectedPaymentOption] - The payment option selected on a particular checkout step (if applicable).
 * @param {*} [params.cartData] - The shopping cart data to be used for tracking.
 * @returns {void | analytics} Returns the result of sendAnalytics or void if tracking fails.
 */
const checkoutStepCompleted = ({ stepNumber, selectedPaymentOption = '', cartData }) => {
    if (!cartData) {
        return;
    }
    try {
        const trackingPayload = {
            checkout_id: cartData === null || cartData === void 0 ? void 0 : cartData.id,
            step: stepNumber + 1,
            payment_method: selectedPaymentOption,
        };
        return sendAnalytics({ event: CART_EVENT_LIST.CHECKOUT_STEP_COMPLETED, payload: trackingPayload });
    }
    catch (error) {
        console.error(`Something went wrong tracking 'Checkout Step Completed' event : ${error}`);
    }
};
/**
 * Fires the order completed tracking event once the user has successfully checked out.
 *
 * @param {Object} params - The parameters for tracking the order completed event.
 * @param {string} params.orderId - The ID of the completed order.
 * @param {*} [params.cartData] - The shopping cart data to be used for tracking.
 * @returns {void | analytics} Returns the result of sendAnalytics or void if tracking fails.
 */
const orderCompleted = ({ orderId, cartData }) => {
    if (!cartData) {
        return;
    }
    try {
        const trackingPayload = Object.assign({ cart_id: cartData === null || cartData === void 0 ? void 0 : cartData.id, order_id: orderId, products: createProductTrackingObject(cartData), revenue: formatPrice$1(cartData === null || cartData === void 0 ? void 0 : cartData.subTotal), discount: formatPrice$1(cartData === null || cartData === void 0 ? void 0 : cartData.discount), currency: cartData === null || cartData === void 0 ? void 0 : cartData.currency, email: cartData === null || cartData === void 0 ? void 0 : cartData.webkey, subtotal: formatPrice$1(cartData === null || cartData === void 0 ? void 0 : cartData.subTotal), tax: formatPrice$1(cartData === null || cartData === void 0 ? void 0 : cartData.tax), total: formatPrice$1(cartData === null || cartData === void 0 ? void 0 : cartData.total) }, ((cartData === null || cartData === void 0 ? void 0 : cartData.promotionCodes[0]) && { coupon: cartData === null || cartData === void 0 ? void 0 : cartData.promotionCodes[0] }));
        return sendAnalytics({ event: CART_EVENT_LIST.ORDER_COMPLETED, payload: trackingPayload });
    }
    catch (error) {
        console.error(`Something went wrong tracking 'Order Completed' event : ${error}`);
    }
};
/**
 * Make the analytics tracking call, which is what actually sends the data downstream via POST request.
 *
 * @param {Object} params - The parameters for the analytics tracking call.
 * @param {string} params.event - The name of the event to track.
 * @param {any} params.payload - The payload data to send with the event.
 * @returns {analytics | null} Returns the result of the analytics track call, or null if analytics is not enabled.
 */
const sendAnalytics = ({ event, payload }) => {
    if (window.analytics && window.analytics.track) {
        return window.analytics.track(event, payload);
    }
    console.warn('eCommerce analytics are not enabled!');
    return null;
};
/**
 * Utility function to help create the products array object that the downstream tracking is expecting.
 * @param {*} [params.cartData] - The shopping cart data to be used for tracking.
 * @returns an array of product objects with desired tracking properties.
 */
const createProductTrackingObject = ({ cartData, }) => {
    var _a, _b;
    return ((_b = (_a = cartData === null || cartData === void 0 ? void 0 : cartData.items) === null || _a === void 0 ? void 0 : _a.map((cartItem, index) => {
        var _a, _b, _c, _d, _e;
        return ({
            name: (_a = cartItem.product) === null || _a === void 0 ? void 0 : _a.name,
            position: parseInt(index !== null && index !== void 0 ? index : 0, 10),
            price: formatPrice$1((_c = (_b = cartItem.product.pricingOptions) === null || _b === void 0 ? void 0 : _b.find((po) => po.autoRenew === true)) === null || _c === void 0 ? void 0 : _c.price),
            product_id: (_d = cartItem.product) === null || _d === void 0 ? void 0 : _d.productSku,
            quantity: parseInt(cartItem.quantity, 10),
            sku: (_e = cartItem.product) === null || _e === void 0 ? void 0 : _e.productSku,
        });
    })) !== null && _b !== void 0 ? _b : []);
};
/**
 * Formats the price for analytics tracking.
 * If the input price is 0, undefined, null, or not a number, returns 0.
 * Otherwise, returns the parsed float value of the price.
 *
 * @param {Object} params - The parameters object.
 * @param {number | string | undefined | null} params.price - The price to format.
 * @returns {number} The formatted price or 0 if the input is invalid.
 */
const formatPrice$1 = ({ price }) => {
    if (price !== undefined && price !== null && !isNaN(Number(price))) {
        return parseFloat(price);
    }
    return 0;
};
/**
 * Gets the current path URL.
 *
 * @param {Object} [params] - Optional parameters.
 * @param {string} [params.href] - Optional href to use instead of window.location.href (useful for testing).
 * @returns {string} The current URL's origin and pathname.
 */
const getCurrentUrl = ({ href } = {}) => {
    const currentUrl = href || window.location.href;
    const url = new URL(currentUrl);
    return `${url.origin}${url.pathname}`;
};

const { disw = {} } = typeof window !== 'undefined' ? window : {};
/**
 * Return the authenticated user's email address.
 *
 * @returns The user's email address as a string
 */
function getWebkeyEmail() {
    var _a, _b;
    return (_b = (_a = disw.account.getAccount()) === null || _a === void 0 ? void 0 : _a.user) === null || _b === void 0 ? void 0 : _b.email;
}

/**
 * @fileoverview Guest session management utilities for eCommerce applications.
 *
 * This module provides functionality to manage guest user sessions in localStorage,
 * including session creation, validation, expiration handling, and country management.
 * Guest sessions are used to maintain shopping cart state and user preferences
 * for anonymous users before they authenticate.
 *
 */
/** LocalStorage key for persisting guest session data */
const STORAGE_KEY = 'ecommerce_guest_session';
/** Default session expiry duration in hours */
const SESSION_EXPIRY_HOURS = 24;
/**
 * Session expiry duration in milliseconds.
 * Calculated as: 24 hours × 60 minutes/hour × 60 seconds/minute × 1000 ms/second = 86,400,000 ms
 */
const SESSION_EXPIRY_MS = SESSION_EXPIRY_HOURS * 60 * 60 * 1000;
/**
 * Calculates the expiry timestamp for a guest session.
 *
 * Uses a sliding window approach where each access to the session
 * extends its lifetime by the full expiry duration.
 *
 * @param currentTime - Current timestamp in milliseconds (defaults to Date.now())
 * @returns Expiry timestamp in milliseconds
 *
 * @example
 * ```typescript
 * const expiryTime = calculateExpiryTime();
 * console.log(new Date(expiryTime)); // 24 hours from now
 * ```
 */
function calculateExpiryTime(currentTime = Date.now()) {
    return currentTime + SESSION_EXPIRY_MS;
}
/**
 * Validates a guest session object for structural integrity and expiration.
 *
 * Performs comprehensive validation including:
 * - Null/undefined checks
 * - Type validation for all required fields
 * - Expiration time validation
 * - Optional field type checking
 *
 * @param session - The guest session object to validate
 * @param currentTime - Current timestamp in milliseconds for expiry comparison
 * @returns Validation result with success status and optional error message
 *
 * @example
 * ```typescript
 * const session = getStoredSession();
 * const validation = validateGuestSession(session, Date.now());
 * if (!validation.isValid) {
 *   console.error('Invalid session:', validation.error);
 * }
 * ```
 */
function validateGuestSession(session, currentTime) {
    // Check for null or undefined session
    if (!session) {
        return { isValid: false, error: 'Guest session metadata is missing' };
    }
    // Validate required UUID field
    if (!session.uuid || typeof session.uuid !== 'string') {
        return { isValid: false, error: `Missing or invalid UUID: ${session.uuid}` };
    }
    // Validate optional sessionId field type if present
    if (session.sessionId !== undefined && typeof session.sessionId !== 'string') {
        return { isValid: false, error: `sessionId must be string: ${session.sessionId}` };
    }
    // Validate expiresAt field type if present
    if (session.expiresAt && typeof session.expiresAt !== 'number') {
        return { isValid: false, error: `expiresAt must be number: ${session.expiresAt}` };
    }
    // Check if session has expired
    if (session.expiresAt && currentTime > session.expiresAt) {
        return { isValid: false, error: 'Guest session expired' };
    }
    return { isValid: true };
}
/**
 * Creates a new guest session with secure UUID and current country information.
 *
 * Generates a cryptographically secure UUID using the Web Crypto API and
 * initializes the session with current country data from the disw utility.
 * The session is automatically persisted to localStorage.
 *
 * @param currentTime - Current timestamp in milliseconds for expiry calculation
 * @returns Promise resolving to newly created guest session metadata
 *
 * @throws Will log a warning if localStorage storage fails, but continues execution
 *
 * @example
 * ```typescript
 * const newSession = await createNewGuestSession(Date.now());
 * console.log(newSession.uuid); // "550e8400-e29b-41d4-a716-446655440000"
 * ```
 */
function createNewGuestSession(currentTime) {
    return __awaiter(this, void 0, void 0, function* () {
        // Get the current locale for the user's country
        const currentLocale = yield getCurrentUserCartLocale();
        // Create new session with secure UUID and current country
        const newSession = {
            uuid: crypto.randomUUID(), // Cryptographically secure UUID
            sessionId: undefined, // Will be set when user authenticates or starts checkout
            expiresAt: calculateExpiryTime(currentTime), // Set expiry 24 hours from now
            country: disw.country.getCountry(), // Get current country from disw utility
            locale: currentLocale, // Set the current user's locale
        };
        // Persist session to localStorage with error handling
        try {
            localStorage.setItem(STORAGE_KEY, JSON.stringify(newSession));
        }
        catch (error) {
            // Log warning but don't throw - session can still be used in memory
            console.warn('Failed to store new guest session:', error);
        }
        return newSession;
    });
}
/**
 * Retrieves the current guest session from localStorage with validation and expiry refresh.
 *
 * This function implements a sliding window expiration strategy where accessing
 * a valid session automatically extends its lifetime. If the session is invalid,
 * expired, or corrupted, it will be automatically cleared.
 *
 * @returns The current guest session metadata, or null if no valid session exists
 *
 * @example
 * ```typescript
 * const session = getGuestSession();
 * if (session) {
 *   console.log('Guest UUID:', session.uuid);
 *   console.log('Expires at:', new Date(session.expiresAt));
 * } else {
 *   console.log('No valid guest session found');
 * }
 * ```
 */
function getGuestSession() {
    try {
        // Attempt to retrieve stored session data
        const stored = localStorage.getItem(STORAGE_KEY);
        if (!stored) {
            return null; // No session exists
        }
        // Parse the stored JSON data
        const parsed = JSON.parse(stored);
        const currentTime = Date.now();
        // Validate session structure and expiration
        const validation = validateGuestSession(parsed, currentTime);
        if (!validation.isValid) {
            console.warn(`Invalid guest session: ${validation.error}`);
            clearGuestSession(); // Clean up invalid session
            return null;
        }
        // Session is valid - implement sliding window expiration
        // Update expiry time to extend session lifetime on access
        const updatedSession = Object.assign(Object.assign({}, parsed), { expiresAt: calculateExpiryTime(currentTime) });
        // Persist the updated expiry time
        try {
            localStorage.setItem(STORAGE_KEY, JSON.stringify(updatedSession));
        }
        catch (error) {
            console.warn('Failed to update session expiry:', error);
            // Continue execution - session is still valid even if expiry update failed
        }
        // Return original session for API compatibility
        // (some consumers may not expect the updated expiresAt)
        return parsed;
    }
    catch (error) {
        // Handle JSON parsing errors or other exceptions
        console.warn('Failed to parse guest session from localStorage:', error);
        clearGuestSession(); // Clean up corrupted session data
        return null;
    }
}
/**
 * Creates a new guest session and stores it in localStorage.
 *
 * This function generates a fresh guest session with a new UUID,
 * current country information, and appropriate expiry time.
 * Any existing session will be replaced.
 *
 * @returns Promise resolving to the newly created guest session metadata
 *
 * @example
 * ```typescript
 * const newSession = await createGuestSession();
 * console.log('New guest session created:', newSession.uuid);
 * ```
 */
function createGuestSession() {
    return __awaiter(this, void 0, void 0, function* () {
        const currentTime = Date.now();
        return yield createNewGuestSession(currentTime);
    });
}
/**
 * Gets the current guest session or creates a new one if none exists.
 *
 * This is the primary function for ensuring a guest session is always available.
 * It implements a "get or create" pattern that maintains session continuity
 * while handling edge cases like expiration or corruption gracefully.
 *
 * This function maintains backward compatibility with existing implementations
 * and is safe to call multiple times.
 *
 * @returns Promise resolving to a valid guest session metadata object (never null)
 *
 * @example
 * ```typescript
 * // Always returns a valid session
 * const session = await getOrCreateGuestSession();
 * console.log('Session UUID:', session.uuid);
 *
 * // Safe to call multiple times - won't create duplicates
 * const sameSession = await getOrCreateGuestSession();
 * console.log('Same UUID:', sameSession.uuid === session.uuid);
 * ```
 */
function getOrCreateGuestSession() {
    return __awaiter(this, void 0, void 0, function* () {
        // Try to get existing valid session first
        const existingSession = getGuestSession();
        if (existingSession) {
            return existingSession;
        }
        // No valid session exists, create a new one
        return yield createGuestSession();
    });
}
/**
 * Updates specific fields in the guest session with partial data.
 *
 * This function allows selective updates to session fields without replacing
 * the entire session. It handles country changes by creating a new session
 * if the country has changed, ensuring data consistency.
 *
 * The session expiry is automatically refreshed on any update to maintain
 * the sliding window behavior.
 *
 * @param guestData - Partial guest session data to merge with existing session
 *
 * @example
 * ```typescript
 * // Update just the sessionId when user starts checkout
 * await setGuestSession({ sessionId: 'checkout_abc123' });
 *
 * // Update multiple fields
 * await setGuestSession({
 *   sessionId: 'new_session',
 *   lastAuthState: true
 * });
 * ```
 */
function setGuestSession(guestData) {
    return __awaiter(this, void 0, void 0, function* () {
        var _a;
        try {
            // Get current session or create new one if none exists
            const currentSession = yield getOrCreateGuestSession();
            const currentCountry = disw.country.getCountry();
            const currentTime = Date.now();
            // Check if country has changed - if so, create new session
            if (((_a = currentSession.country) === null || _a === void 0 ? void 0 : _a.countrycode) !== currentCountry.countrycode) {
                clearGuestSession();
                yield createNewGuestSession(currentTime);
                return;
            }
            // Get the current locale for the user's country
            const currentLocale = yield getCurrentUserCartLocale();
            // Merge provided data with existing session
            const updatedSession = Object.assign(Object.assign(Object.assign({}, currentSession), guestData), { expiresAt: calculateExpiryTime(currentTime), locale: currentLocale });
            // Persist updated session to localStorage
            localStorage.setItem(STORAGE_KEY, JSON.stringify(updatedSession));
        }
        catch (error) {
            console.warn('Failed to update guest session in localStorage:', error);
        }
    });
}
/**
 * Removes the guest session from localStorage.
 *
 * This function should be called when:
 * - User successfully authenticates (login)
 * - User explicitly logs out
 * - Testing scenarios require a clean state
 *
 * The function handles localStorage errors gracefully and will not throw exceptions.
 *
 * @example
 * ```typescript
 * // Clear session after successful login
 * if (userLoggedIn) {
 *   clearGuestSession();
 * }
 *
 * // Clear session after checkout completion
 * if (orderCompleted) {
 *   clearGuestSession();
 * }
 * ```
 */
function clearGuestSession() {
    try {
        localStorage.removeItem(STORAGE_KEY);
        console.log('Guest session cleared from localStorage');
    }
    catch (error) {
        // Handle cases where localStorage is not available or throws errors
        console.warn('Failed to clear guest session:', error);
    }
}

/**
 * Loads ECBE cart data from the ECBE API. If cartId is not provided, it defaults to the current cart.
 *
 * @param {Object} params - The parameters for loading the cart.
 * @param {string} params.cartId - The id of the ECBE cart.
 * @returns {Promise<any>} Promise object representing the cart data as loaded from the ECBE API.
 */
const loadCartData = (_a) => __awaiter(void 0, [_a], void 0, function* ({ cartId }) {
    var _b, _c;
    const authenticated = (_c = (_b = disw === null || disw === void 0 ? void 0 : disw.account) === null || _b === void 0 ? void 0 : _b.getAccount()) === null || _c === void 0 ? void 0 : _c.authenticated;
    if (authenticated) {
        // CardID defaults to 'current' if not provided in fetchCartData
        return fetchCartData({ cartId }).then((data) => __awaiter(void 0, void 0, void 0, function* () {
            disw.cart.emitCartEvent(data, false);
            return data;
        }));
    }
    else {
        // CardID defaults to 'current' if not provided in fetchCartDataGuest
        return fetchCartDataGuest().then((guestData) => __awaiter(void 0, void 0, void 0, function* () {
            guestData && disw.cart.emitCartEvent(guestData.data, false);
            return guestData === null || guestData === void 0 ? void 0 : guestData.data;
        }));
    }
});
// /**
//  * Adds one or more items to the DEX cart. It is expected that the cart has already been initialized,
//  * otherwise there isn't a stored cartId to get the cart contents.
//  *
//  * @param {Object} params - The parameters for adding items to the cart.
//  * @param {string} params.env - The environment (env/prod) used for API calls.
//  * @param {string} params.cartId - The id of the DEX cart.
//  * @param {Array<{pricingId: string, sku: string, quantity: string | number}>} params.items - An array of item objects to add.
//  * @param {string | undefined} params.bundleSku - The SKU of the bundle being added to the cart, if applicable.
//  * @returns {Promise<any>} Promise object representing the cart data as returned from the DEX middleware.
//  */
// export const addItemToCart = async ({
//   env,
//   cartId,
//   items,
// }: {
//   env: string;
//   cartId: string;
//   items: {
//     parentProductId: string;
//     pricingId?: string;
//     sku?: string;
//     quantity: string | number;
//     productId?: string;
//     productClassification: string;
//   };
// }): Promise<any> => {
//   try {
//     // Use the ECBE utility function instead of direct fetch
//     const data = await addProductToCart({
//       parentProductId: items.parentProductId,
//       productId: items?.productId ?? '',
//       quantity: typeof items.quantity === 'string' ? parseInt(items.quantity, 10) : items.quantity,
//       productClassification: items.productClassification,
//     });
//     // Load the updated cart data to return consistent structure
//     return loadCartData({ cartId });
//   } catch (error) {
//     console.error('Error adding item to cart:', error);
//     throw error;
//   }
// };
const updateProductQuantity$1 = (_a) => __awaiter(void 0, [_a], void 0, function* ({ cartId, cartItemId, quantity }) {
    try {
        // Use the ECBE utility function instead of direct fetch
        const data = yield updateProductQuantity({ cartId, cartItemId, quantity });
        // This only updates the quantity of the product in the cart, and returns only the product that was changed
        return data;
    }
    catch (error) {
        console.error('Error updating product quantity:', error);
        throw error;
    }
});
/**
 * Removes one or more items from the DEX cart. It is expected that the cart has already been initialized,
 * otherwise there isn't a stored cartId to get the cart contents.
 *
 * @param {Object} params - The parameters for removing items from the cart.
 * @param {string} params.env - The environment (env/prod) used for API calls.
 * @param {string} params.cartId - The id of the DEX cart.
 * @param {string[]} params.cartItemIds - The cartItemIds for the items to remove from the cart.
 * @param {string | undefined} params.bundleSku - The SKU of the bundle being removed from the cart, if applicable.
 * @returns {Promise<any>} Promise object representing the cart data as returned from the DEX middleware.
 */
const removeItemFromCart$1 = (_a) => __awaiter(void 0, [_a], void 0, function* ({ cartId, cartItemId, bundleSku }) {
    try {
        // Use the ECBE utility function instead of direct fetch
        const data = yield removeItemFromCart({ cartId, cartItemId });
        return data;
    }
    catch (error) {
        console.error('Error removing item from cart:', error);
        throw error;
    }
});
/**
 * Applies a promotional code to the DEX cart. It is expected that the cart has already been initialized,
 * otherwise there isn't a stored cartId to get the cart contents.
 *
 * @param {Object} params - The parameters for applying the promotion code.
 * @param {string} params.env - The environment (env/prod) used for API calls.
 * @param {string} params.cartId - The id of the DEX cart.
 * @param {string} params.promotionCode - The promotion code to apply to the cart.
 * @returns {Promise<any>} Promise object representing the cart data as returned from the DEX middleware.
 */
const applyPromotionCodeToCart = (_a) => __awaiter(void 0, [_a], void 0, function* ({ promotionCode }) {
    var _b, _c;
    try {
        let cartId;
        const authenticated = (_c = (_b = disw === null || disw === void 0 ? void 0 : disw.account) === null || _b === void 0 ? void 0 : _b.getAccount()) === null || _c === void 0 ? void 0 : _c.authenticated;
        if (authenticated) {
            // Use the ECBE utility function instead of direct fetch
            const cartData = yield fetchCartData();
            cartId = cartData === null || cartData === void 0 ? void 0 : cartData.cartId;
            yield applyPromotionCode({ cartId, promotionCode });
        }
        else {
            yield applyPromotionCodeGuest({ promotionCode });
        }
        // Load the full cart data to return a consistent structure
        return loadCartData({ cartId });
    }
    catch (error) {
        console.error('Error applying promotion code:', error);
        throw error;
    }
});
/**
 * Adds or subtracts products from the shopping cart.
 *
 * @param {Object} params - The parameters for adding or subtracting products.
 * @param {string} params.cartItemId - The ID of the cart item to modify.
 * @param {string} params.cartId - The ID of the cart.
 * @param {number} params.numberSeats - The number of seats to add or subtract.
 * @returns {Promise<void>}
 */
const addOrSubtractProducts = (_a) => __awaiter(void 0, [_a], void 0, function* ({ cartItemId, cartId, numberSeats }) {
    var _b, _c;
    try {
        // @ts-ignore
        const authenticated = (_c = (_b = disw === null || disw === void 0 ? void 0 : disw.account) === null || _b === void 0 ? void 0 : _b.getAccount()) === null || _c === void 0 ? void 0 : _c.authenticated;
        // Sometimes there could be a string being passed we want to handle it
        const numberOfSeats = typeof numberSeats === 'string' ? parseInt(numberSeats) : numberSeats;
        if (authenticated) {
            console.log(`[addOrSubtractProducts] User is authenticated. Adding or subtracting products with args:`, { cartItemId, cartId, numberSeats });
            yield updateProductQuantity$1({ cartItemId, cartId, quantity: numberOfSeats });
            // Fetch and return the updated cart data
            return yield fetchCartData();
        }
        else {
            console.warn(`[addOrSubtractProducts] User is not authenticated. Adding or subtracting products with args:`, { cartItemId, cartId, numberSeats });
            // Update the cart with the new quantity for guest - to be implemented
            yield updateProductQuantityGuest({ cartItemId, quantity: numberOfSeats });
            // Fetch the updated guest cart data
            const guestCart = yield fetchCartDataGuest();
            // Fetch and return the updated cart data
            return guestCart === null || guestCart === void 0 ? void 0 : guestCart.data; // Return the guest cart data to match the cart structure of authenticated users
        }
    }
    catch (error) {
        console.error('Error in addOrSubtractProducts:', error);
        throw error;
    }
});
const addItemToShoppingCart = (_a) => __awaiter(void 0, [_a], void 0, function* ({ parentProductId, quantity, productClassification, productId, childProducts, }) {
    var _b, _c;
    try {
        const authenticated = (_c = (_b = disw === null || disw === void 0 ? void 0 : disw.account) === null || _b === void 0 ? void 0 : _b.getAccount()) === null || _c === void 0 ? void 0 : _c.authenticated;
        // Call the centralized ECBE utility function with all parameters
        if (authenticated) {
            return yield addProductToCart({
                parentProductId,
                productId,
                quantity,
                productClassification,
                childProducts,
            });
        }
        else {
            const guestData = yield addProductToCartGuest({
                parentProductId,
                productId,
                quantity,
                productClassification,
                childProducts,
            });
            return guestData === null || guestData === void 0 ? void 0 : guestData.data;
        }
    }
    catch (e) {
        console.error('🚀 ~ handleAddToCart: Error adding product to cart', e);
        throw e;
    }
});
const removeItem = (_a) => __awaiter(void 0, [_a], void 0, function* ({ cartId, cartItemId }) {
    var _b, _c, _d;
    const authenticated = (_c = (_b = disw === null || disw === void 0 ? void 0 : disw.account) === null || _b === void 0 ? void 0 : _b.getAccount()) === null || _c === void 0 ? void 0 : _c.authenticated;
    let cartData;
    if (authenticated) {
        cartData = yield removeItemFromCart$1({ cartId: cartId, cartItemId: cartItemId });
    }
    else {
        cartData = (_d = (yield removeItemFromCartGuest({ cartId: cartId, cartItemId: cartItemId }))) === null || _d === void 0 ? void 0 : _d.data;
    }
    return cartData;
});
/**
 * Removes a promotional code from the DEX cart. It is expected that the cart has already been initialized,
 * otherwise there isn't a stored cartId to get the cart contents.
 *
 * @param {Object} params - The parameters for removing the promotion code.
 * @param {string} params.env - The environment (env/prod) used for API calls.
 * @param {string} params.cartId - The id of the DEX cart.
 * @param {string} params.promotionCode - The promotion code to remove from the cart.
 * @returns {Promise<any>} Promise object representing the cart data as returned from the DEX middleware.
 */
const removePromotionCodeFromCart = (_a) => __awaiter(void 0, [_a], void 0, function* ({ env, cartId, promotionCodeId }) {
    var _b, _c;
    try {
        const authenticated = (_c = (_b = disw === null || disw === void 0 ? void 0 : disw.account) === null || _b === void 0 ? void 0 : _b.getAccount()) === null || _c === void 0 ? void 0 : _c.authenticated;
        if (authenticated) {
            yield removePromotionCode({ cartId, promotionCodeId });
        }
        else {
            yield removePromotionCodeGuest({ cartId, promotionCodeId });
        }
        // Load the full cart data to return a consistent structure
        return loadCartData({ cartId });
    }
    catch (error) {
        console.error('Error removing promotion code:', error);
        throw error;
    }
});
/**
 * Handles preserving guest cart data when a user authenticates.
 * This function fetches the current guest cart data to get the cart ID,
 * then calls the preserve API to transfer the cart to the authenticated user.
 * Returns the transformed cart data for the caller to emit cart events.
 * Handles cases where the guest cart has already been cleared.
 *
 * @returns {Promise<DexShoppingCart | null>} Promise that resolves to the preserved cart data or null if failed.
 */
const handlePreserveGuestCartData = () => __awaiter(void 0, void 0, void 0, function* () {
    var _a, _b, _c;
    try {
        // Get the current guest cart data to obtain the cart ID
        let guestCartResponse;
        try {
            console.log('[useShoppingCart] Fetching guest cart data');
            guestCartResponse = yield fetchCartDataGuest();
        }
        catch (error) {
            console.error('[useShoppingCart] Error fetching guest cart data:', error);
            // Re-throw other errors
            throw error;
        }
        const guestCartId = guestCartResponse === null || guestCartResponse === void 0 ? void 0 : guestCartResponse.data.cartId;
        if (!guestCartId) {
            console.warn('[useShoppingCart] ⚠️ No guest cart ID found to preserve');
            return null;
        }
        console.log('[useShoppingCart] Preserving guest cart data');
        const preserveCartResponse = yield preserveCartDataGuest({ cartId: guestCartId });
        // Check if the preserve cart operation was successful
        if (((_a = preserveCartResponse.data) === null || _a === void 0 ? void 0 : _a.success) && ((_c = (_b = preserveCartResponse.data) === null || _b === void 0 ? void 0 : _b.data) === null || _c === void 0 ? void 0 : _c.numberOfProductsWithSuccess) > 0) {
            console.log('✅ Guest cart preserved successfully, clearing guest session');
            // Clear the guest session only if the preserve guest cart passed
            clearGuestSession();
        }
        else {
            console.warn('⚠️ No guest cart items to preserve');
            return null;
        }
    }
    catch (error) {
        console.error('❌ Error preserving guest cart data:', error);
        throw error; // Re-throw the error so it can be caught by the calling function
    }
});

// Base domain for the DISW Middleware (production).
const BASE_API_CDN_URL = 'https://api.sw.siemens.com';
// Base domain for the DISW Middlware (development) - note we're temporarily using the AWS provided hostname to get around IP restriction which restricts some of our team.
const BASE_API_CDN_URL_DEV = 'https://21hhjddvkg.execute-api.us-east-2.amazonaws.com';
// Base domain for the DISW middleware (UAT environment)
const BASE_API_CDN_URL_UAT = 'https://ekdi80rux6.execute-api.us-east-2.amazonaws.com';
// These URLs are for the DISW Middleware
const MIDDLEWARE_API_URL = `${BASE_API_CDN_URL}/v1`;
const MIDDLEWARE_API_URL_DEV = `${BASE_API_CDN_URL_DEV}/v1`;
const MIDDLEWARE_API_URL_UAT = `${BASE_API_CDN_URL_UAT}/v1`;
const MIDDLEWARE_API_URL_MOCK = 'https://8d5fe0eb-1bbc-4761-8c10-24e86337d7f9.mock.pstmn.io/dex-services-svc';
// These URLs are specific to the DEX Services API
const DEX_SERVICES_API_URL = `${MIDDLEWARE_API_URL}/dex-services`;
const DEX_SERVICES_API_URL_DEV = `${MIDDLEWARE_API_URL_DEV}/dex-services`;
const DEX_SERVICES_API_URL_UAT = `${MIDDLEWARE_API_URL_UAT}/dex-services`;
const DEX_SERVICES_API_URL_MOCK = 'https://8d5fe0eb-1bbc-4761-8c10-24e86337d7f9.mock.pstmn.io/dex-services-svc';
/**
 * Returns the appropriate DISW Middleware / DEX Middleware URL for the given environment and options.
 *
 * @param {Object} params - The parameters object.
 * @param {string} params.env - The environment to get the URL for. Can be one of "dev", "uat", "prod", or "mock".
 * @param {boolean} [params.dexServices=false] - Whether the URL is for DEX services or just the middleware in general.
 * @returns {string} The environment specific URL.
 */
const _getMiddlewareUrl = ({ env, dexServices = false }) => {
    if (env === constants.PROD_ENV) {
        return dexServices ? DEX_SERVICES_API_URL : MIDDLEWARE_API_URL;
    }
    else if (env === constants.UAT_ENV) {
        return dexServices ? DEX_SERVICES_API_URL_UAT : MIDDLEWARE_API_URL_UAT;
    }
    else if (env === constants.MOCK_ENV) {
        return dexServices ? DEX_SERVICES_API_URL_MOCK : MIDDLEWARE_API_URL_MOCK;
    }
    // default to DEV
    return dexServices ? DEX_SERVICES_API_URL_DEV : MIDDLEWARE_API_URL_DEV;
};
/**
 * Get the DEX Services URL for the specified environment.
 *
 * @param {Object} params - The parameters object.
 * @param {Environment} params.env - The environment to get the URL for. Can be one of "dev", "uat", "prod", or "mock".
 * @returns {string} The DEX Services Middleware URL for the given environment.
 */
const getDEXServicesAPIURL = ({ env }) => {
    return _getMiddlewareUrl({ env, dexServices: true });
};
/**
 * Maps ECBE environment types to their corresponding base API URLs.
 * Contains mappings for feature, dev, uat, and prod environments.
 */
const ECBE_ENV_MAP = {
    feature: 'https://tk68uhypkk.execute-api.us-east-2.amazonaws.com/ecbe',
    dev: 'https://qumpqqt6z8.execute-api.us-east-2.amazonaws.com/ecbe', //'https://api.dev.swi.siemens.com/ecbe',
    uat: 'https://g53sshpw20.execute-api.us-east-2.amazonaws.com/ecbe', //'https://api.stage.swi.siemens.com/ecbe',
    prod: 'https://api.sw.siemens.com/ecbe',
};
/**
 * Returns the base URL for the ECBE API based on the environment.
 * Note that only the feature environment uses the AWS API Gateway domain, while dev, uat and prod all
 * use the domain service aggregator.
 *
 * @param env - The ECBE environment to use (feature, dev, uat, or prod)
 * @returns The corresponding base URL for the specified environment
 */
function getECBEBaseUrl(env) {
    return ECBE_ENV_MAP[env] || ECBE_ENV_MAP['feature'];
}
/**
 * Get the DISW Middleware API URL for the specified environment.
 *
 * @param {Object} params - The parameters object.
 * @param {Environment} params.env - The environment to get the URL for. Can be one of "dev", "uat", "prod", or "mock".
 * @returns {string} The DISW Middleware API URL for the given environment.
 */
const getMiddlewareAPIURL = ({ env }) => {
    return _getMiddlewareUrl({ env, dexServices: false });
};

/**
 * Utilities for getting and setting cookies.
 */
const COOKIE_SEPARATOR = '; ';
const COOKIE_KEY_VALUE_SEPARATOR = '=';
const COOKIE_VALUE_INDEX = 1;
/**
 * Get the value of a cookie with the given key.
 *
 * @param {Object} params
 * @param {string} params.key - Cookie name.
 * @param {string} [params.cookieString=document.cookie] - Cookie string to search (defaults to document.cookie).
 * @returns {string | undefined} Cookie value or undefined if it doesn't exist.
 */
const getCookieValue = ({ key, cookieString = document.cookie }) => {
    const foundCookie = cookieString
        .split(COOKIE_SEPARATOR)
        .find((cookie) => cookie.trimStart().startsWith(`${key}${COOKIE_KEY_VALUE_SEPARATOR}`));
    if (foundCookie) {
        return decodeURIComponent(foundCookie.split(COOKIE_KEY_VALUE_SEPARATOR)[COOKIE_VALUE_INDEX]);
    }
    // Otherwise we return undefined
    return undefined;
};

/**
 * Simple logger module which will only log out the console logs for all environments except for production
 * as to not expose them on the public web.
 *
 */
const logger = {
    isValidEnv() {
        try {
            return "prod" !== constants.PROD_ENV;
        }
        catch (error) {
            return true;
        }
    },
    log(message, options = '') {
        this.isValidEnv() && console.log(message, options);
    },
    warn(message) {
        this.isValidEnv() && console.warn(message);
    },
    error(message) {
        this.isValidEnv() && console.error(message);
    },
};

/**
 * Get the configured cart environment.
 * @returns {string} The configured cart environment.
 */
const getCartEnv = () => {
    return disw.cart.getCartEnv();
};
/**
 * Get the cartId from the cookie
 *
 * @param {Object} params - Object containing the key for the cookie
 * @param {string} params.key - The cookie key
 * @returns {string|undefined} Cookie value or undefined if it doesn't exist
 */
const _getCartCookieValue = ({ key }) => {
    return getCookieValue({ key });
};
/**
 * Get the cartId from cookie
 * @returns {string|undefined} CartId or undefined if it doesn't exist
 */
const _getCartId = () => {
    const storageItemKey = `${disw.country.getCountry().countrycode}_${constants.CART_STORAGE_KEY_SUFFIX}`;
    return _getCartCookieValue({ key: storageItemKey });
};
/**
 * Removes an item from the DEX cart.
 *
 * @param {Object} params - The parameters object.
 * @param {string} params.cartItemId - The id for the cart item to be removed.
 * @param {ICartItem} params.removedProduct - The cart item object being removed.
 * @param {string | undefined | null} params.promotionCode - The promotion code associated with the item, if any.
 * @param {number} params.itemIndex - The index of the item in the cart.
 *
 * @returns {Promise<any>} Promise object representing the cart data as returned from the DEX middleware.
 */
const removeFromCart = (_a) => __awaiter(void 0, [_a], void 0, function* ({ cartItemId, removedProduct, promotionCode, itemIndex, }) {
    const cartId = _getCartId();
    if (!cartId) {
        logger.warn(constants.NO_CART_ID_ERROR);
        throw constants.NO_CART_ID_ERROR;
    }
    try {
        // Track the removal of the cart item (not to be confused with the decrease operation). This represents moving the item with all licenses.
        // productRemoved({
        //   cartItemId,
        //   promotionCode,
        //   itemIndex,
        //   quantity: removedProduct?.quantity,
        //   cartData: await getCart(),
        // });
        // Remove the item from the shopping cart and capture the updated shopping cart data.
        const removedCartItem = yield removeItemFromCart$1({ cartId, cartItemId: cartItemId, bundleSku: undefined });
        if (removedCartItem) {
            const cartData = yield loadCartData({ cartId });
            // Let anyone else listening that the cart contents have changed
            if (cartData) {
                disw.cart.emitCartEvent(cartData, false);
            }
            return cartData;
        }
    }
    catch (error) {
        logger.error(error);
        // Throw the error so it can be caught
        throw error;
    }
});
/**
 * Applies a promotional code to the DEX cart.
 *
 * @param {Object} params - The parameters object.
 * @param {string} params.promotionCode - The promotion code to apply to the cart.
 * @returns {Promise<any>} Promise object representing the cart data as returned from the DEX middleware or undefined in the case of a fetch error.
 */
const applyPromotion = (_a) => __awaiter(void 0, [_a], void 0, function* ({ promotionCode }) {
    try {
        let newCartData = null;
        newCartData = yield applyPromotionCodeToCart({ promotionCode });
        if (newCartData) {
            couponApplied({ couponName: promotionCode, cartData: newCartData });
        }
        else {
            couponDenied({ couponName: promotionCode, reason: 'cartData.message', cartData: newCartData });
        }
        return newCartData;
    }
    catch (error) {
        logger.error(error);
        // Throw the error so it can be caught
        throw error;
    }
});
/**
 * Removes a promotional code from the DEX cart.
 *
 * @param {Object} params - The parameters object.
 * @param {string} params.promotionCode - The promotion code to remove from the cart.
 * @returns {Promise<any>} Promise object representing the cart data as returned from the DEX middleware.
 */
const removePromotion = (_a) => __awaiter(void 0, [_a], void 0, function* ({ cartId, promotionCodeId }) {
    try {
        // Remove the promotion code from the shopping cart.
        const cartData = yield removePromotionCodeFromCart({ env: getCartEnv(), cartId, promotionCodeId });
        return cartData;
    }
    catch (error) {
        logger.error(error);
        // Throw the error so it can be caught
        throw error;
    }
});
/**
 * Adds or subtracts one or multiple products from the DEX cart.
 *
 * @param {Object} params - The parameters object.
 * @param {any} params.parameter - An object or array of objects containing information about the product(s) to add or subtract.
 * @param {string | undefined} params.bundleSku - The SKU of the bundle, if a bundle is being added or removed from the cart.
 * @returns {Promise<any>} Promise object representing the cart data as returned from the DEX middleware.
 *
 * @throws {Error} Throws an error if the cart ID is not found or if there's an error during the add/subtract process.
 */
const addOrSubtractProductsFromCart = (_a) => __awaiter(void 0, [_a], void 0, function* ({ parameter, bundleSku }) {
    console.log('addOrSubtractProductsFromCart called with parameters:', parameter, bundleSku);
    try {
        const productUpdated = yield updateProductQuantity$1({ cartId: '', cartItemId: parameter.productPricingId, quantity: parameter.quantity });
        if (productUpdated) {
            // loadCartData already emits a cart event, so we don't need to emit it again
            yield loadCartData({ cartId: '' });
        }
        else {
            logger.error('Fatal DEX error has occurred, DEX has returned empty cart data with response code 2xx.');
            throw Error('Fatal DEX error has occurred');
        }
        return productUpdated;
        // return productUpdated;
    }
    catch (error) {
        logger.error(`Error adding item to cart ${error} '`);
        // disw.cart.emitCartEvent(null, context);
        throw error;
    }
});
/**
 * Changes fullcart open state, and passes params to analyics if cart is being opened
 * @param {Object} params - object containing the product information
 * @param {boolean} params.state if the cart should be open or not
 * @returns {void}
 */
const toggleFullCart = ({ state }) => {
    disw.cart.toggleFullCart(state);
};
/**
 * Changes minicart open state
 * @param {Object} params - object containing the product information
 * @param {boolean} params.state if the cart should be open or not
 * @returns {void}
 */
const toggleMiniCart = ({ state }) => {
    disw.cart.toggleMiniCart(state);
};
/**
 * Update the cart data with the new cart data.
 * @param {Object} params - object containing the product information
 * @param params.newCartData The new data for the cart
 * @returns {void}
 */
const updateCartData = ({ newCartData }) => {
    disw.cart.emitCartEvent(newCartData, false);
};
/** This is a function to return the URL for the Checkout Service Flow URL
 *
 * @return {string} string representing the base URL for the DEX Checkout Service
 *
 *  @example
 *     getCartCheckoutServiceUrl()
 */
const getCartCheckoutServiceUrl = () => {
    return `${getDEXServicesAPIURL({ env: getCartEnv() })}/checkout`;
};

const SHA_SECRET_KEY = '5a293177671c57174e243fe057b140eb6cef9d50e2fd91c674131371';
/**
 * This function is used to add a secret key to the string need to be hashed. Later the combination of that string and secret key is sent
 * to the sha256 hashing function.
 *
 * @param {string} stringToHashString to get hashed using the sha256 function
 * @param {string | undefined} secretKey The secret key to add to the string before hashing. If none is provided, will use the default secret key.
 * @returns hashed string
 */
function addSecretKeyAndHash(stringToHash, secretKey = SHA_SECRET_KEY) {
    return calculateSHA256Hash(secretKey + stringToHash);
}
/**
 * Secure Hash Algorithm (SHA256)
 * https://coursesweb.net/javascript/sha256-encrypt-hash_cs
 * Original code by Angel Marin, Paul Johnston. Converted from gibberish to TypeScript by GPT.
 **/
function calculateSHA256Hash(inputString) {
    // Constants for the SHA256 algorithm
    const bitsPerCharacter = 8;
    // Addition operation for 32-bit integers
    function addWords(x, y) {
        const lsw = (x & 0xffff) + (y & 0xffff);
        const msw = (x >> 16) + (y >> 16) + (lsw >> 16);
        return (msw << 16) | (lsw & 0xffff);
    }
    // Circular shift operation for 32-bit integers
    function circularShift(X, n) {
        return (X >>> n) | (X << (32 - n));
    }
    // Right shift operation for 32-bit integers
    function shiftRight(X, n) {
        return X >>> n;
    }
    // Choose function for SHA256 algorithm
    function choose(x, y, z) {
        return (x & y) ^ (~x & z);
    }
    // Majority function for SHA256 algorithm
    function majority(x, y, z) {
        return (x & y) ^ (x & z) ^ (y & z);
    }
    // Sigma0 function for SHA256 algorithm
    function sigma0256(x) {
        return circularShift(x, 2) ^ circularShift(x, 13) ^ circularShift(x, 22);
    }
    // Sigma1 function for SHA256 algorithm
    function sigma1256(x) {
        return circularShift(x, 6) ^ circularShift(x, 11) ^ circularShift(x, 25);
    }
    // Gamma0 function for SHA256 algorithm
    function gamma0256(x) {
        return circularShift(x, 7) ^ circularShift(x, 18) ^ shiftRight(x, 3);
    }
    // Gamma1 function for SHA256 algorithm
    function gamma1256(x) {
        return circularShift(x, 17) ^ circularShift(x, 19) ^ shiftRight(x, 10);
    }
    // Core SHA256 algorithm processing the message blocks
    function coreSHA256(messageWords, messageLength) {
        // Constants for the SHA256 algorithm
        const constants = [
            0x428a2f98, 0x71374491, 0xb5c0fbcf, 0xe9b5dba5, 0x3956c25b, 0x59f111f1, 0x923f82a4, 0xab1c5ed5, 0xd807aa98, 0x12835b01, 0x243185be, 0x550c7dc3, 0x72be5d74, 0x80deb1fe, 0x9bdc06a7, 0xc19bf174,
            0xe49b69c1, 0xefbe4786, 0xfc19dc6, 0x240ca1cc, 0x2de92c6f, 0x4a7484aa, 0x5cb0a9dc, 0x76f988da, 0x983e5152, 0xa831c66d, 0xb00327c8, 0xbf597fc7, 0xc6e00bf3, 0xd5a79147, 0x6ca6351, 0x14292967,
            0x27b70a85, 0x2e1b2138, 0x4d2c6dfc, 0x53380d13, 0x650a7354, 0x766a0abb, 0x81c2c92e, 0x92722c85, 0xa2bfe8a1, 0xa81a664b, 0xc24b8b70, 0xc76c51a3, 0xd192e819, 0xd6990624, 0xf40e3585, 0x106aa070,
            0x19a4c116, 0x1e376c08, 0x2748774c, 0x34b0bcb5, 0x391c0cb3, 0x4ed8aa4a, 0x5b9cca4f, 0x682e6ff3, 0x748f82ee, 0x78a5636f, 0x84c87814, 0x8cc70208, 0x90befffa, 0xa4506ceb, 0xbef9a3f7, 0xc67178f2
        ];
        // Initial hash values for SHA256
        const hashValues = [0x6a09e667, 0xbb67ae85, 0x3c6ef372, 0xa54ff53a, 0x510e527f, 0x9b05688c, 0x1f83d9ab, 0x5be0cd19];
        // Temporary array for processing message blocks
        const messageBlocks = new Array(64);
        // These variables represent the eight 32-bit words of the hash value.
        // At the beginning of processing each message block, they are initialized with the current hash values.
        // During the processing of each word within the message block, these values are updated based on various calculations, and at the end of processing the block, they are updated to the new hash values.
        let a, b, c, d, e, f, g, h;
        let T1, T2;
        // Padding the message and setting its length
        messageWords[messageLength >> 5] |= 0x80 << (24 - (messageLength % 32));
        messageWords[(((messageLength + 64) >> 9) << 4) + 15] = messageLength;
        // Processing each message block
        for (let i = 0; i < messageWords.length; i += 16) {
            a = hashValues[0];
            b = hashValues[1];
            c = hashValues[2];
            d = hashValues[3];
            e = hashValues[4];
            f = hashValues[5];
            g = hashValues[6];
            h = hashValues[7];
            // Processing each word within a message block
            for (let j = 0; j < 64; j++) {
                if (j < 16) {
                    messageBlocks[j] = messageWords[j + i];
                }
                else {
                    messageBlocks[j] = addWords(addWords(addWords(gamma1256(messageBlocks[j - 2]), messageBlocks[j - 7]), gamma0256(messageBlocks[j - 15])), messageBlocks[j - 16]);
                }
                // Calculating intermediate values T1 and T2
                T1 = addWords(addWords(addWords(addWords(h, sigma1256(e)), choose(e, f, g)), constants[j]), messageBlocks[j]);
                T2 = addWords(sigma0256(a), majority(a, b, c));
                // Updating variables for next iteration
                h = g;
                g = f;
                f = e;
                e = addWords(d, T1);
                d = c;
                c = b;
                b = a;
                a = addWords(T1, T2);
            }
            // Updating hash values after processing a message block
            hashValues[0] = addWords(a, hashValues[0]);
            hashValues[1] = addWords(b, hashValues[1]);
            hashValues[2] = addWords(c, hashValues[2]);
            hashValues[3] = addWords(d, hashValues[3]);
            hashValues[4] = addWords(e, hashValues[4]);
            hashValues[5] = addWords(f, hashValues[5]);
            hashValues[6] = addWords(g, hashValues[6]);
            hashValues[7] = addWords(h, hashValues[7]);
        }
        return hashValues;
    }
    // Converts a string to a binary array
    function stringToBinaryArray(inputString) {
        const binaryArray = [];
        const mask = (1 << bitsPerCharacter) - 1;
        for (let i = 0; i < inputString.length * bitsPerCharacter; i += bitsPerCharacter) {
            binaryArray[i >> 5] |= (inputString.charCodeAt(i / bitsPerCharacter) & mask) << (24 - (i % 32));
        }
        return binaryArray;
    }
    // Encodes a string to UTF-8 format
    function encodeUtf8(string) {
        string = string.replace(/\r\n/g, '\n');
        let utfText = '';
        // Iterating through each character in the string
        for (let n = 0; n < string.length; n++) {
            const charCode = string.charCodeAt(n);
            // Handling ASCII characters
            if (charCode < 128) {
                utfText += String.fromCharCode(charCode);
            }
            // Handling characters between 128 and 2048
            else if (charCode > 127 && charCode < 2048) {
                utfText += String.fromCharCode((charCode >> 6) | 192);
                utfText += String.fromCharCode((charCode & 63) | 128);
            }
            // Handling characters above 2048
            else {
                utfText += String.fromCharCode((charCode >> 12) | 224);
                utfText += String.fromCharCode(((charCode >> 6) & 63) | 128);
                utfText += String.fromCharCode((charCode & 63) | 128);
            }
        }
        return utfText;
    }
    // Converts a binary array to a hexadecimal string
    function binaryArrayToHex(binArray) {
        const hexTable = '0123456789abcdef';
        let result = '';
        // Iterating through each element in the binary array
        for (let i = 0; i < binArray.length * 4; i++) {
            result += hexTable.charAt((binArray[i >> 2] >> ((3 - (i % 4)) * 8 + 4)) & 0xf) + hexTable.charAt((binArray[i >> 2] >> ((3 - (i % 4)) * 8)) & 0xf);
        }
        return result;
    }
    // Encoding the input string in UTF-8 and calculating SHA256 hash
    inputString = encodeUtf8(inputString);
    return binaryArrayToHex(coreSHA256(stringToBinaryArray(inputString), inputString.length * bitsPerCharacter));
}

// Set API call timeout. Sometimes this stuff takes a little too damn long and you just need to give up and go touch grass.
const DEX_FETCH_TIMEOUT = 60 * 1000;
/**
 * Calls the DEX API using the Fetch API, handling authentication and request signing.
 *
 * This function automatically determines the correct authentication method based on the HTTP method (GET or POST),
 * adds the required authorization header, and builds the full API URL using the current cart environment.
 * It also applies a timeout to the request. Error handling is not included and should be handled by the caller.
 *
 * @param path - The URL path to call the DEX API, containing everything after 'dex-services/'.
 * @param options - Fetch options for the API call, such as method, headers, body, etc.
 * @returns A promise that resolves to the Fetch API Response object.
 *
 * @example
 * callDEXApi('/checkout/cart', { method: 'GET' })
 *   .then(response => response.json())
 *   .then(data => {
 *     // handle data
 *   }).catch(error => {
 *      // handle any errors
 *   })
 */
function callDEXApi(path, options) {
    var _a, _b;
    // Determine the authorization for the request.
    // If the request is a GET request, the API path will be hashed. If it is a POST request, the body will be hashed.
    const finalPath = path.startsWith('/') ? path : '/' + path;
    let authToken;
    if (((_a = options.method) === null || _a === void 0 ? void 0 : _a.toUpperCase()) === 'POST') {
        // The body should be a string
        authToken = addSecretKeyAndHash((_b = options.body) !== null && _b !== void 0 ? _b : '');
    }
    else {
        // Default to a GET request authentication
        authToken = addSecretKeyAndHash(finalPath);
    }
    // Build the full URL
    const cartEnvironment = getCartEnv();
    const fullApiUrl = `${getDEXServicesAPIURL({ env: cartEnvironment })}${finalPath}`;
    console.log(`[callDEXapi] fullApiUrl=${fullApiUrl}`);
    // Control timeout
    const controller = new AbortController();
    // Store the timeout ID so we can clear it later
    const timeoutId = setTimeout(() => {
        controller.abort();
        console.error(`The DEX request timed out after the maximum time of ${DEX_FETCH_TIMEOUT} milliseconds`);
    }, DEX_FETCH_TIMEOUT);
    // Return the request with timeout handling
    return fetch(fullApiUrl, Object.assign(Object.assign({}, options), { headers: Object.assign(Object.assign({}, options.headers), { Authorization: `Bearer ${authToken}` }), signal: controller.signal }))
        .then((response) => {
        // Clear the timeout since the request has completed
        clearTimeout(timeoutId);
        return response;
    })
        .catch((error) => {
        // Also clear the timeout on error
        clearTimeout(timeoutId);
        throw error; // Re-throw the error for the caller to handle
    });
}
/**
 * The maximum time to allow requests to ECBE to take before timing out, in milliseconds.
 */
const ECBE_FETCH_TIMEOUT = 250 * 1000;
/**
 * Makes an authenticated API call to the ECBE service.
 * This function handles:
 * - Path normalization (adding leading slash if missing). The API path should only contain everything after /api.
 * - Environment detection and URL construction
 * - Authentication via bearer token
 * - Request timeout handling
 * - JSON parsing
 * - Error handling and logging
 *
 * @param path - The API endpoint path to call (with or without leading slash)
 * @param options - Standard fetch RequestInit options for the request
 * @param endpointDescription - Optional description of the endpoint for better logging
 * @param [excludeToken=false] - Whether to exclude the bearer token from the request
 * @returns Promise resolving to either:
 *    - The parsed JSON response data (when successful and JSON is valid)
 *    - The raw Response object (when successful but JSON parsing fails)
 * @throws Will reject with:
 *    - The parsed JSON error response (when server returns error with valid JSON)
 *    - The parsing error (when server returns error with invalid JSON)
 *    - The fetch error (when request fails due to network issues or timeout)
 */
function callECBEApi(path, options, endpointDescription, excludeToken = false) {
    // Delete when we have proper authentication in place.
    var _a, _b, _c;
    // Get the authentication token if the user is logged in.
    // @ts-ignore
    const token = (_c = (_b = (_a = window === null || window === void 0 ? void 0 : window.disw) === null || _a === void 0 ? void 0 : _a.account) === null || _b === void 0 ? void 0 : _b.getAccount()) === null || _c === void 0 ? void 0 : _c.idToken;
    // Determine the authorization for the request.
    // If the request is a GET request, the API path will be hashed. If it is a POST request, the body will be hashed.
    const finalPath = path.startsWith('/') ? path : '/' + path;
    const description = endpointDescription || finalPath;
    // Build the full URL
    const env = getCartEnv();
    const fullApiUrl = `${getECBEBaseUrl(env)}/api${finalPath}`;
    // Control timeout
    const controller = new AbortController();
    // Store the timeout ID so we can clear it later
    const timeoutId = setTimeout(() => {
        controller.abort();
        console.error(`The ECBE request timed out after the maximum time of ${ECBE_FETCH_TIMEOUT} milliseconds`);
    }, ECBE_FETCH_TIMEOUT);
    // Build the headers
    const headers = Object.assign({}, options.headers);
    // If the token is available and we are not excluding it, add it to the headers.
    if (token && !excludeToken) {
        headers.Authorization = `Bearer ${token}`;
    }
    // Return a promise that handles the request lifecycle
    return new Promise((resolve, reject) => {
        fetch(fullApiUrl, Object.assign(Object.assign({}, options), { headers, signal: controller.signal }))
            .then((response) => {
            // Clear the timeout since the request has completed
            clearTimeout(timeoutId);
            if (response.ok) {
                response
                    .json()
                    .then(resolve)
                    .catch((error) => {
                    console.error(`Error parsing JSON response from ${description}: ${error}`);
                    // Resolve the full response so that the downstream functions can handle it as they see fit
                    resolve(response);
                });
            }
            else {
                console.error(`Unsuccessful ${response.status} response from ECBE API trying to ${description}`);
                response
                    .json()
                    .then((responseText) => {
                    reject(responseText);
                })
                    .catch((error) => {
                    // Handle case where response is not valid JSON
                    console.error(`Failed to parse error response: ${error}`);
                    // reject the full response so that the downstream functions can handle it as they see fit
                    reject(response);
                });
            }
        })
            .catch((error) => {
            // Also clear the timeout on error since we already have an error to handle
            clearTimeout(timeoutId);
            console.error(`Error in request to ${description} from ECBE API: ${error}`);
            reject(error);
        });
    });
}

/**
 * Verifies and normalizes a cart ID parameter.
 * If no valid cart ID is provided, defaults to 'current'.
 *
 * @param {string | undefined} cartId - The cart ID to verify
 * @returns {string} The normalized cart ID, or 'current' if none provided
 */
function verifyCartId(cartId) {
    if (!cartId || typeof cartId !== 'string' || cartId.trim() === '') {
        cartId = 'current'; // Default to 'current' if no cartId is provided
        console.warn('No cartId provided, defaulting to "current"');
    }
    return cartId;
}
/**
 * Extracts only the required metadata properties from the guest session.
 * Returns an object containing only uuid, sessionId, and locale properties.
 * This ensures we only send necessary data to the API and maintain consistency.
 *
 * @returns {Object} Object containing uuid, sessionId (optional), and locale (optional)
 */
function getGuestSessionMetadata() {
    const guestSession = getGuestSession();
    return {
        uuid: guestSession === null || guestSession === void 0 ? void 0 : guestSession.uuid,
        sessionId: guestSession === null || guestSession === void 0 ? void 0 : guestSession.sessionId,
        locale: guestSession === null || guestSession === void 0 ? void 0 : guestSession.locale,
    };
}
/**
 * Clears the cached country information from session storage.
 * This can be useful when you want to force a fresh fetch of country data.
 *
 * @returns {void}
 */
function clearCountryCache() {
    const COUNTRY_CACHE_KEY = 'ecbe_country_info_cache';
    const CACHE_EXPIRY_KEY = 'ecbe_country_info_cache_expiry';
    sessionStorage.removeItem(COUNTRY_CACHE_KEY);
    sessionStorage.removeItem(CACHE_EXPIRY_KEY);
    console.log('Country cache cleared');
}
/**
 * Session storage key for country currency mapping data
 */
const COUNTRY_CURRENCY_MAPPING_KEY = 'ecbe_country_info_cache';
/**
 * Gets the current user's currency based on their country code.
 * First ensures that country configuration data is available by calling fetchCommerceCountryConfig,
 * then uses window.disw.country.getCountry()?.countrycode to get the current country
 * and looks up the corresponding currency from session storage.
 *
 * @returns {Promise<string | undefined>} Promise resolving to the currency code (e.g., 'USD', 'EUR') for the user's country, or undefined if not found
 */
function getCurrentUserCurrency() {
    return __awaiter(this, void 0, void 0, function* () {
        var _a, _b, _c;
        const LOG_PREFIX = '[getCurrentUserCurrency]';
        try {
            // Ensure country configuration data is fetched and cached
            yield fetchCommerceCountryConfig();
            // Get the current country code from the global disw object
            const countryCode = (_c = (_b = (_a = window === null || window === void 0 ? void 0 : window.disw) === null || _a === void 0 ? void 0 : _a.country) === null || _b === void 0 ? void 0 : _b.getCountry()) === null || _c === void 0 ? void 0 : _c.countrycode;
            if (!countryCode) {
                console.warn(`${LOG_PREFIX} No country code found from window.disw.country.getCountry()`);
                return undefined;
            }
            // Get the country currency mapping from session storage
            const mappingDataString = sessionStorage.getItem(COUNTRY_CURRENCY_MAPPING_KEY);
            if (!mappingDataString) {
                console.warn(`${LOG_PREFIX} No country currency mapping data found in session storage.`);
                return undefined;
            }
            const mappingData = JSON.parse(mappingDataString);
            // Find the currency for the current country code
            const countryMapping = mappingData.find((mapping) => mapping.countryCode === countryCode);
            if (!countryMapping) {
                console.warn(`${LOG_PREFIX} No currency mapping found for country code: ${countryCode}`);
                return undefined;
            }
            console.log(`${LOG_PREFIX} Found currency ${countryMapping.currency} for country code ${countryCode}`);
            return countryMapping.currency;
        }
        catch (error) {
            console.error(`${LOG_PREFIX} Error getting current user currency:`, error);
            return undefined;
        }
    });
}
/**
 * Gets the current user's locale based on their country code.
 * First ensures that country configuration data is available by calling fetchCommerceCountryConfig,
 * then uses window.disw.country.getCountry()?.countrycode to get the current country
 * and looks up the corresponding locale from session storage.
 * Converts underscores to dashes in the locale (e.g., 'en_US' becomes 'en-US').
 *
 * @returns {Promise<string | undefined>} Promise resolving to the locale code (e.g., 'en-US', 'de-AT') for the user's country, or undefined if not found
 */
function getCurrentUserCartLocale() {
    return __awaiter(this, void 0, void 0, function* () {
        var _a, _b, _c, _d;
        const LOG_PREFIX = '[getCurrentUserCartLocale]';
        try {
            // Ensure country configuration data is fetched and cached
            yield fetchCommerceCountryConfig();
            // Get the current country code from the global disw object
            const countryCode = (_c = (_b = (_a = window === null || window === void 0 ? void 0 : window.disw) === null || _a === void 0 ? void 0 : _a.country) === null || _b === void 0 ? void 0 : _b.getCountry()) === null || _c === void 0 ? void 0 : _c.countrycode;
            if (!countryCode) {
                console.warn(`${LOG_PREFIX} No country code found from window.disw.country.getCountry()`);
                return undefined;
            }
            // Get the country currency mapping from session storage
            const mappingDataString = sessionStorage.getItem(COUNTRY_CURRENCY_MAPPING_KEY);
            if (!mappingDataString) {
                console.warn(`${LOG_PREFIX} No country currency mapping data found in session storage.`);
                return undefined;
            }
            const mappingData = JSON.parse(mappingDataString);
            // Find the locale for the current country code
            const countryMapping = mappingData.find((mapping) => mapping.countryCode === countryCode);
            if (!countryMapping) {
                console.warn(`${LOG_PREFIX} No locale mapping found for country code: ${countryCode}`);
                return undefined;
            }
            // Convert underscores to dashes in the locale
            const locale = (_d = countryMapping.locale) === null || _d === void 0 ? void 0 : _d.replace(/_/g, '-');
            console.log(`${LOG_PREFIX} Found locale ${locale} for country code ${countryCode}`);
            return locale;
        }
        catch (error) {
            console.error(`${LOG_PREFIX} Error getting current user cart locale:`, error);
            return undefined;
        }
    });
}
/**
 * Fetches and stores commerce country configuration data from the ECBE API.
 * Makes a GET request to retrieve country information and stores it in session storage
 * for improved performance and reduced API calls. This function is responsible for
 * pulling and caching the data that other functions can then use.
 *
 * @returns {Promise<void>} Promise that resolves when the data has been fetched and stored
 * @throws Will log errors but does not throw to allow graceful degradation
 */
function fetchCommerceCountryConfig() {
    return __awaiter(this, void 0, void 0, function* () {
        const COUNTRY_CACHE_KEY = 'ecbe_country_info_cache';
        const CACHE_EXPIRY_KEY = 'ecbe_country_info_cache_expiry';
        const CACHE_DURATION = 24 * 60 * 60 * 1000; // 24 hours in milliseconds
        try {
            // Check if we have cached data and if it's still valid
            const cachedData = sessionStorage.getItem(COUNTRY_CACHE_KEY);
            const cacheExpiry = sessionStorage.getItem(CACHE_EXPIRY_KEY);
            const now = Date.now();
            if (cachedData && cacheExpiry && now < parseInt(cacheExpiry, 10)) {
                // Data is already cached and valid, no need to fetch
                console.log('Country configuration data already cached and valid');
                return;
            }
            // Cache is expired or doesn't exist, fetch from API
            console.log('Fetching country configuration data from API');
            const responseData = yield callECBEApi('/v1/settings/countries/', {
                method: 'GET',
                headers: { 'Content-Type': 'application/json' },
            }, 'fetch commerce country config');
            // Access the countryInfo array from the response
            const countryInfo = responseData.countryInfo;
            if (!Array.isArray(countryInfo)) {
                console.error('Country info is not an array:', responseData);
                return;
            }
            // Cache the data with expiry
            sessionStorage.setItem(COUNTRY_CACHE_KEY, JSON.stringify(countryInfo));
            sessionStorage.setItem(CACHE_EXPIRY_KEY, (now + CACHE_DURATION).toString());
            console.log('Country configuration data successfully fetched and cached');
        }
        catch (error) {
            console.error('Error fetching commerce country config:', error);
        }
    });
}
/**
 * Fetches product information from the ECBE API.
 * Makes an authenticated GET request to retrieve detailed product data
 * including pricing, variations, descriptions, and configuration options.
 *
 * @param {Object} params - The parameters for fetching product data
 * @param {string} params.productId - The ECBE product ID to fetch
 * @param {string} [params.locale] - Optional locale string for localized product data (e.g., 'en_US')
 * @returns {Promise<EcbeProduct>} Promise resolving to the complete product data from the eCBE API
 * @throws Will reject with error details if the product cannot be fetched
 */
function fetchProductInfo(_a) {
    return __awaiter(this, arguments, void 0, function* ({ productId, locale }) {
        if (!locale) {
            throw new Error('Locale is required');
        }
        // callECBEApi now resolves directly to the JSON response
        return yield callECBEApi(`/v2/product/${productId}${locale ? `?locale=${locale.replace('-', '_')}` : ''}`, {
            method: 'GET',
            headers: { 'Content-Type': 'application/json' },
        }, 'fetch product info', true);
    });
}
/**
 * Fetches the current shopping cart data from the ECBE API.
 * Makes an authenticated GET request to retrieve cart information for the current user.
 * The cart ID defaults to 'current' if not specified.
 *
 * @param {Object} params - The parameters for fetching cart data
 * @param {string} [params.cartId] - The cart ID to fetch (defaults to 'current')
 * @returns {Promise<EcbeCart>} Promise resolving to the cart data containing items, totals, and pricing
 * @throws Will reject with error details if the API request fails
 */
function fetchCartData() {
    return __awaiter(this, arguments, void 0, function* ({ cartId } = {}) {
        // Verify and normalize the cartId
        cartId = verifyCartId(cartId);
        // callECBEApi now resolves directly to the JSON response
        // Current user is determined by the bearer token in the request
        return yield callECBEApi(`/v2/cart/${cartId}`, {
            method: 'GET',
            headers: { 'Content-Type': 'application/json' },
        }, 'fetch cart data');
    });
}
/**
 * Fetches the current guest shopping cart data from the ECBE API.
 * Makes an unauthenticated GET request to retrieve cart information for guest users.
 * The cart ID defaults to 'current' if not specified.
 *
 * @returns {Promise<EcbeCart>} Promise resolving to the guest cart data containing items, totals, and pricing
 * @throws Will reject with error details if the API request fails
 */
function fetchCartDataGuest() {
    return __awaiter(this, void 0, void 0, function* () {
        const guestSession = getGuestSession();
        // if there's no guestSession sessionId, don't make the API call and just return an empty cart
        if (!(guestSession === null || guestSession === void 0 ? void 0 : guestSession.sessionId)) {
            console.log('[useShoppingCartEcbe] No guest session ID found yet for session. Not loading cart as there is no guest cart to load.');
            return Promise.resolve(undefined);
        }
        else {
            console.log(`[useShoppingCartEcbe] Guest session found. Loading shopping cart for guest user.`, guestSession);
        }
        // Current user is determined by the bearer token in the request
        return yield callECBEApi(`/v1/cart/guest/current?uuid=${guestSession === null || guestSession === void 0 ? void 0 : guestSession.uuid}&sessionId=${guestSession === null || guestSession === void 0 ? void 0 : guestSession.sessionId}`, {
            method: 'GET',
            headers: { 'Content-Type': 'application/json' },
        }, 'Fetch guest cart data', true);
    });
}
/**
 * Fetches the current guest shopping cart data from the ECBE API.
 * Makes an unauthenticated GET request to retrieve cart information for guest users.
 * The cart ID defaults to 'current' if not specified.
 *
 * @param {Object} params - The parameters for fetching guest cart data
 * @param {string} [params.cartId] - The guest cart ID to fetch (defaults to 'current')
 * @returns {Promise<EcbeCart>} Promise resolving to the guest cart data containing items, totals, and pricing
 * @throws Will reject with error details if the API request fails
 */
function preserveCartDataGuest(_a) {
    return __awaiter(this, arguments, void 0, function* ({ cartId }) {
        // Verify and normalize the cartId
        cartId = verifyCartId(cartId);
        const guestSession = getGuestSession();
        console.log('🚀 ~ preserveCartDataGuest ~ guestSession:', guestSession);
        // Current user is determined by the bearer token in the request
        return yield callECBEApi(`/v1/cart/guest/preserve/${cartId}?uuid=${guestSession === null || guestSession === void 0 ? void 0 : guestSession.uuid}&sessionId=${guestSession === null || guestSession === void 0 ? void 0 : guestSession.sessionId}`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
        }, 'Preserve guest cart data');
    });
}
/**
 * Adds a product to the authenticated user's cart via the ECBE API.
 * Makes a POST request to add the specified product with quantity and classification
 * to the current user's shopping cart.
 *
 * @param {AddProductToCartParams} params - The parameters for adding a product to the cart
 * @param {string} [params.parentProductId] - The ID of the parent product (for variations)
 * @param {string} params.productId - The ID of the product to add
 * @param {number} params.quantity - The quantity of the product to add
 * @param {string} [params.productClassification] - The classification of the product
 * @param {Array} [params.childProducts] - Optional array of child products for Dynamic Kit products
 * @returns {Promise<AddProductToCartResponse>} Promise resolving to the response data from the eCBE API after adding the product
 * @throws Will reject with error details if the product cannot be added to the cart
 */
function addProductToCart(_a) {
    return __awaiter(this, arguments, void 0, function* ({ parentProductId, productId, quantity, productClassification, childProducts }) {
        const body = {
            parentProductId,
            productId,
            quantity,
            productClassification,
        };
        // Include childProducts if provided (for Dynamic Kit products)
        if (childProducts && childProducts.length > 0) {
            body.childProducts = childProducts;
        }
        return yield callECBEApi('/v1/cart/current/item', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(body),
        }, 'add product to cart');
    });
}
/**
 * Adds a product to the guest user's cart via the ECBE API.
 * Makes a POST request to add the specified product with quantity and classification
 * to a guest user's shopping cart. Includes a guest UUID cookie for session tracking.
 *
 * @param {AddProductToCartParams} params - The parameters for adding a product to the guest cart
 * @param {string} [params.parentProductId] - The ID of the parent product (for variations)
 * @param {string} params.productId - The ID of the product to add
 * @param {number} params.quantity - The quantity of the product to add
 * @param {string} [params.productClassification] - The classification of the product
 * @param {Array} [params.childProducts] - Optional array of child products for Dynamic Kit products
 * @returns {Promise<GuestAddProductToCartResponse>} Promise resolving to the response data from the eCBE API after adding the product
 * @throws Will reject with error details if the product cannot be added to the guest cart
 */
function addProductToCartGuest(_a) {
    return __awaiter(this, arguments, void 0, function* ({ parentProductId, productId, quantity, productClassification, childProducts }) {
        const body = {
            parentProductId,
            productId,
            quantity,
            productClassification,
        };
        // Include childProducts if provided (for Dynamic Kit products)
        if (childProducts && childProducts.length > 0) {
            body.childProducts = childProducts;
        }
        const guestParams = {
            metadata: getGuestSessionMetadata(),
            data: body,
        };
        // callECBEApi now resolves directly to the JSON response
        const response = yield callECBEApi('/v1/cart/guest/current/item', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(guestParams),
        }, 'Add product to guest cart', true);
        // Check response for guest session ID and verify guest uuid
        if (response.metadata) {
            const { uuid, sessionId } = response.metadata;
            const currentSession = getGuestSession();
            if (sessionId && uuid === (currentSession === null || currentSession === void 0 ? void 0 : currentSession.uuid)) {
                setGuestSession({ sessionId });
            }
        }
        return response;
    });
}
/**
 * Updates the quantity of a product in the authenticated user's cart via the ECBE API.
 * Makes a PATCH request to modify the quantity of an existing cart item.
 *
 * @param {Object} params - The parameters for updating product quantity
 * @param {string} [params.cartId] - The cart ID (defaults to 'current')
 * @param {string} params.cartItemId - The cart item ID to update
 * @param {number} params.quantity - The new quantity to set
 * @returns {Promise<UpdateProductQuantityResponse>} Promise resolving to the response data from the eCBE API after updating the item
 * @throws Will reject with error details if the product quantity cannot be updated
 */
function updateProductQuantity(_a) {
    return __awaiter(this, arguments, void 0, function* ({ cartId, cartItemId, quantity }) {
        // Verify and normalize the cartId
        cartId = verifyCartId(cartId);
        // make sure quantity is cast to an integer
        quantity = parseInt(quantity.toString(), 10);
        return yield callECBEApi(`/v1/cart/${cartId}/items/${cartItemId}`, {
            method: 'PATCH',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ quantity: quantity }),
        }, 'update product quantity in cart');
    });
}
/**
 * Retrieves and validates the prerequisites for a given cart by its ID.
 *
 * This function verifies and normalizes the provided `cartId`, then makes a POST request
 * to the ECBE API to validate the prerequisites for the specified cart. The response is
 * parsed as JSON and returned. If an error occurs during the process, it is logged and rethrown.
 *
 * @param {Object} params - The parameters object.
 * @param {string} [params.cartId] - The optional cart ID to validate prerequisites for.
 * @returns {Promise<CartPreRequisites>} A promise that resolves to the API response data containing the cart prerequisites.
 * @throws Will throw an error if the API call fails or if there is an issue parsing the response.
 */
function getCartPreRequisites(_a) {
    return __awaiter(this, arguments, void 0, function* ({ cartId }) {
        // Verify and normalize the cartId
        cartId = verifyCartId(cartId);
        return yield callECBEApi(`/v1/cart/${cartId}/validatePreRequisites`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
        }, 'get cart prerequisites');
    });
}
/**
 * Updates the quantity of a product in the guest user's cart via the ECBE API.
 * Makes a PATCH request to modify the quantity of an existing cart item for guest users.
 *
 * @param {Object} params - The parameters for updating product quantity in guest cart
 * @param {string} params.cartItemId - The cart item ID to update
 * @param {number} params.quantity - The new quantity to set
 * @returns {Promise<UpdateProductQuantityResponse>} Promise resolving to the response data from the eCBE API after updating the item
 * @throws Will reject with error details if the product quantity cannot be updated
 */
function updateProductQuantityGuest(_a) {
    return __awaiter(this, arguments, void 0, function* ({ cartItemId, quantity }) {
        const data = yield callECBEApi(`/v1/cart/guest/item/current/${cartItemId}`, {
            method: 'PATCH',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                metadata: getGuestSessionMetadata(),
                data: {
                    quantity: quantity,
                },
            }),
        }, 'update product quantity in guest cart', true);
        return data;
    });
}
/**
 * Removes an item from the authenticated user's cart via the ECBE API.
 * Makes a DELETE request to remove a specific cart item by its ID.
 *
 * @param {Object} params - The parameters for removing an item from the cart
 * @param {string} [params.cartId] - The cart ID (defaults to 'current')
 * @param {string} params.cartItemId - The cart item ID to remove
 * @returns {Promise<RemoveItemFromCartResponse>} Promise resolving to the response data from the eCBE API after removing the item
 * @throws Will reject with error details if the item cannot be removed
 */
function removeItemFromCart(_a) {
    return __awaiter(this, arguments, void 0, function* ({ cartId, cartItemId }) {
        // Verify and normalize the cartId
        cartId = verifyCartId(cartId);
        return yield callECBEApi(`/v1/cart/${cartId}/item/${cartItemId}`, {
            method: 'DELETE',
            headers: { 'Content-Type': 'application/json' },
        }, 'remove item from cart');
    });
}
/**
 * Removes an item from the guest user's cart via the ECBE API.
 * Makes a DELETE request to remove a specific cart item by its ID for guest users.
 *
 * @param {Object} params - The parameters for removing an item from the guest cart
 * @param {string} [params.cartId] - The guest cart ID (defaults to 'current')
 * @param {string} params.cartItemId - The cart item ID to remove
 * @returns {Promise<GuestRemoveItemFromCartResponse>} Promise resolving to the response data from the eCBE API after removing the item
 * @throws Will reject with error details if the item cannot be removed
 */
function removeItemFromCartGuest(_a) {
    return __awaiter(this, arguments, void 0, function* ({ cartId, cartItemId }) {
        // Verify and normalize the cartId
        cartId = verifyCartId(cartId);
        // Get the guest session metadata
        const guestSession = getGuestSession();
        return yield callECBEApi(`/v1/cart/guest/${cartId}/item/${cartItemId}?uuid=${guestSession === null || guestSession === void 0 ? void 0 : guestSession.uuid}&sessionId=${guestSession === null || guestSession === void 0 ? void 0 : guestSession.sessionId}`, {
            method: 'DELETE',
            headers: { 'Content-Type': 'application/json' },
        }, 'remove item from guest cart', true);
    });
}
/**
 * Applies a promotion code to the authenticated user's cart via the ECBE API.
 * Makes a POST request to add a discount coupon code to the shopping cart,
 * which may reduce the total price based on the promotion terms.
 *
 * @param {Object} params - The parameters for applying a promotion code
 * @param {string} [params.cartId] - The cart ID (defaults to 'current')
 * @param {string} params.promotionCode - The promotion code to apply
 * @returns {Promise<PromotionCodeResponse>} Promise resolving to the response data from the eCBE API after applying the promotion
 * @throws Will reject with error details if the promotion cannot be applied
 */
function applyPromotionCode(_a) {
    return __awaiter(this, arguments, void 0, function* ({ cartId, promotionCode }) {
        // Verify and normalize the cartId
        cartId = verifyCartId(cartId);
        return yield callECBEApi(`/v1/cart/${cartId}/couponCode`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ promotionCode }),
        }, 'apply promotion code to cart');
    });
}
/**
 * Applies a promotion code to the guest user's cart via the ECBE API.
 * Makes a POST request to add a discount coupon code to the guest shopping cart,
 * which may reduce the total price based on the promotion terms.
 *
 * @param {Object} params - The parameters for applying a promotion code to guest cart
 * @param {string} params.promotionCode - The promotion code to apply
 * @returns {Promise<PromotionCodeResponse>} Promise resolving to the response data from the eCBE API after applying the promotion
 * @throws Will reject with error details if the promotion cannot be applied
 */
function applyPromotionCodeGuest(_a) {
    return __awaiter(this, arguments, void 0, function* ({ promotionCode }) {
        return yield callECBEApi('/v1/cart/guest/coupon/current', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                metadata: getGuestSessionMetadata(),
                data: {
                    promotionCode: promotionCode,
                },
            }),
        }, 'apply promotion code to guest cart', true);
    });
}
/**
 * Removes a promotion code from the authenticated user's cart via the ECBE API.
 * Makes a DELETE request to remove a specific discount coupon code from the shopping cart
 * by its promotion code ID, which may increase the total price.
 *
 * @param {Object} params - The parameters for removing a promotion code
 * @param {string} [params.cartId] - The cart ID (defaults to 'current')
 * @param {string} params.promotionCodeId - The ID of the promotion code to remove
 * @returns {Promise<PromotionCodeResponse>} Promise resolving to the response data from the eCBE API after removing the promotion
 * @throws Will reject with error details if the promotion cannot be removed
 */
function removePromotionCode(_a) {
    return __awaiter(this, arguments, void 0, function* ({ cartId, promotionCodeId }) {
        // Verify and normalize the cartId
        cartId = verifyCartId(cartId);
        return yield callECBEApi(`/v1/cart/${cartId}/couponCode/${promotionCodeId}`, {
            method: 'DELETE',
            headers: { 'Content-Type': 'application/json' },
        }, 'remove promotion code from cart');
    });
}
/**
 * Removes a promotion code from the guest user's cart via the ECBE API.
 * Makes a POST request to remove a specific discount coupon code from the guest shopping cart
 * by its promotion code, which may increase the total price.
 *
 * @param {Object} params - The parameters for removing a promotion code from guest cart
 * @param {string} params.promotionCode - The promotion code to remove
 * @returns {Promise<PromotionCodeResponse>} Promise resolving to the response data from the eCBE API after removing the promotion
 * @throws Will reject with error details if the promotion cannot be removed
 */
function removePromotionCodeGuest(_a) {
    return __awaiter(this, arguments, void 0, function* ({ cartId, promotionCodeId }) {
        // Get the guest session metadata
        const guestSession = getGuestSession();
        return yield callECBEApi(`/v1/cart/guest/coupon/${cartId}/${promotionCodeId}?uuid=${guestSession === null || guestSession === void 0 ? void 0 : guestSession.uuid}&sessionId=${guestSession === null || guestSession === void 0 ? void 0 : guestSession.sessionId}`, {
            method: 'DELETE',
            headers: { 'Content-Type': 'application/json' },
        }, 'remove promotion code to guest cart', true);
    });
}
/**
 * Fetches the current user's information from the ECBE API.
 * Makes an authenticated GET request to the 'v1/user/info' endpoint.
 *
 * @returns {Promise<ECBEAccountInfo>} Promise resolving to the user information object
 * @throws Will reject with error details if the API request fails
 */
function getUserInfo() {
    return __awaiter(this, void 0, void 0, function* () {
        return yield callECBEApi('v1/user/info', {
            method: 'GET',
            headers: {
                Accept: 'application/json',
            },
        }, 'Get User Info');
    });
}
/**
 * Fetches a list of order summaries from the ECBE API.
 * Makes an authenticated GET request to the 'v1/accounts/orderSummaryList' endpoint.
 *
 * Each order summary contains details such as:
 * - orderSummaryId: Unique identifier for the order
 * - orderStatus: Current status of the order (e.g., "Pending", "Completed")
 * - orderNumber: Customer-facing order reference number
 * - extOrderId: External order identifier
 * - orderedDate: ISO timestamp of when the order was placed
 * - grandTotalAmount: Total monetary value of the order
 * - currencyIsoCode: Currency code for the order (e.g., "USD")
 *
 * @returns {Promise<OrderSummary[]>} Promise resolving to an array of order summary objects
 * @throws Will reject with error details if the API request fails
 */
function getOrderSummary() {
    return callECBEApi('v1/accounts/orderSummaryList', {
        method: 'GET',
        headers: {
            Accept: 'application/json',
        },
    }, 'get order summary');
}
/**
 * Fetches the user's subscription list from the ECBE API.
 * Makes an authenticated GET request to the 'v2/subscriptions' endpoint.
 *
 * Each subscription detail contains comprehensive information including:
 * - Subscription identifiers and metadata (ID, name, type, term)
 * - Status and important dates (start date, expiration date, cutoff date)
 * - Financial details (recurring price, currency, payment method)
 * - Configuration information (tenant info with type and configuration fields)
 * - Associated products and their details (including any add-ons)
 *
 * This data can be used to display subscription history, active subscriptions,
 * renewal information, and associated product details.
 *
 * @returns {Promise<SubscriptionDetail[]>} Promise resolving to an array of subscription details
 * @throws Will reject with error details if the API request fails
 */
function getSubscriptionList() {
    return callECBEApi('v2/subscriptions', {
        method: 'GET',
        headers: {
            Accept: 'application/json',
        },
    }, 'get subscription history');
}
/**
 * Fetches the user's invoice history from the ECBE API.
 * Makes an authenticated GET request to the 'v1/payment/invoices' endpoint.
 *
 * Each invoice contains important order and financial information including:
 * - Order identifiers (orderNumber, orderSummaryId)
 * - Financial details (totalAmount, currency)
 * - Status information (isAvailable)
 * - Invoice reference information (invoiceNumber)
 * - Timestamps (orderDate)
 *
 * This data can be used to display invoice history, track payments,
 * and provide users with access to their purchase documentation.
 *
 * @returns {Promise<InvoiceDetail[]>} Promise resolving to an array of invoice details
 * @throws Will reject with error details if the API request fails
 */
function getInvoices() {
    return callECBEApi('v1/payment/invoices', {
        method: 'GET',
        headers: {
            Accept: 'application/json',
        },
    }, 'Get Invoices');
}
/**
 * Initiates the checkout process for a cart.
 *
 * @param options - The checkout options
 * @param options.cartId - The unique identifier for the cart
 * @param options.payerId - Optional. The unique identifier for the payer
 * @param options.cartStatus - Optional. The current status of the cart
 *
 * @returns A promise that resolves to an object containing:
 *          - checkoutId: The unique identifier for the checkout session
 *          - errors: Array of any errors that occurred during checkout initialization
 *
 */
function startCheckout(_a) {
    return __awaiter(this, arguments, void 0, function* ({ cartId, payerId, cartStatus }) {
        // Only include payerId and cartStatus if they are provided
        const body = { cartId };
        if (payerId)
            body.payerId = payerId;
        if (cartStatus)
            body.cartStatus = cartStatus;
        return yield callECBEApi('v1/checkout/start', {
            method: 'POST',
            headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
            body: JSON.stringify(body),
        }, 'Start Checkout');
    });
}
/**
 * Fetches company name suggestions from the ECBE API based on a partial name input.
 * Makes an authenticated POST request to get a list of potential company matches
 * that can be used for autocomplete or company validation features.
 *
 * @param {Object} params - The parameters for fetching company suggestions
 * @param {string} params.companyName - The partial or complete company name to get suggestions for
 * @returns {Promise<SuggestionsResponse>} Promise resolving to a list of company name suggestions
 *                                         and any associated metadata
 * @throws Will log the error and rethrow if the suggestions request fails
 * @example
 * // Get company name suggestions
 * const suggestions = await getSuggestions({ companyName: "Micro" });
 * // Returns suggestions like ["Microsoft", "Microchip Technology", ...]
 */
function getSuggestions(_a) {
    return __awaiter(this, arguments, void 0, function* ({ companyName }) {
        return yield callECBEApi('v1/accounts/suggestions', {
            method: 'POST',
            headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
            body: JSON.stringify({ companyName }),
        }, 'Get Suggestions');
    });
}
/**
 * Fetches a list of all countries and their locale information from the ECBE API.
 * Makes a GET request to retrieve comprehensive country data including locale,
 * country codes, and other regional settings.
 *
 * @returns { countryInfo: EcbeCountryInfo[] } Promise resolving to an array of country information objects
 * @throws Will log the error and rethrow if the API request fails
 */
function getAllCountries() {
    return __awaiter(this, void 0, void 0, function* () {
        return yield callECBEApi('/v1/settings/countries/', {
            method: 'GET',
            headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
        }, 'Get All Countries');
    });
}
/**
 * Submits or updates user information via the ECBE API.
 * Makes an authenticated POST request to send user profile information such as
 * name, contact details, address, and preferences to the account management system.
 *
 * @param {Object} params - The parameters for submitting user information
 * @param {SubmitUserInfo} params.userInfo - The complete user information object containing
 *                                         personal details, address information, and account preferences
 * @returns {Promise<SubmitUserInfoResponse>} Promise resolving to the API response confirming
 *                                           the user information update with status and any applicable messages
 * @throws {Error} Will log the error details and rethrow if the user information submission fails
 */
function submitUserInfo(_a) {
    return __awaiter(this, arguments, void 0, function* ({ userInfo }) {
        return yield callECBEApi('/v1/accounts/info', {
            method: 'POST',
            headers: {
                Accept: 'application/json',
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(userInfo),
        }, 'Submit User Info');
    });
}
/**
 * Fetches payment gateway details from the ECBE API, including the frontend API key.
 * Makes a GET request to retrieve payment configuration information based on the
 * provided cart, country, and business parameters.
 *
 * @param {Object} params - The parameters for fetching payment details
 * @param {string} [params.cartId] - The cart ID associated with the payment
 * @param {string} [params.countryCode] - The country code for payment processing (e.g., 'US', 'GB')
 * @param {string} [params.businessUnit] - The business unit identifier
 * @param {string} [params.soldThrough] - The sales channel identifier
 * @returns {Promise<{apiKeyFrontEnd: string}>} Promise resolving to payment details with the frontend API key
 * @throws Will log the error and rethrow if the API request fails
 */
function getPaymentDetails(_a) {
    return __awaiter(this, arguments, void 0, function* ({ cartId, countryCode, businessUnit, soldThrough, }) {
        // Build query params, filtering out undefined values
        const params = new URLSearchParams();
        if (cartId)
            params.append('cartId', cartId);
        if (countryCode)
            params.append('countryCode', countryCode);
        if (businessUnit)
            params.append('businessUnit', businessUnit);
        if (soldThrough)
            params.append('soldThrough', soldThrough);
        const queryString = params.toString();
        return yield callECBEApi(`v1/payment/clientKey${queryString ? `?${queryString}` : ''}`, {
            method: 'GET',
            headers: { Accept: 'application/json' },
        }, 'Get Payment Details');
    });
}
/**
 * Updates the payment method for a specified cart via the ECBE API.
 * Makes an authenticated POST request to change the payment method associated with
 * a cart, which affects how the order will be processed during checkout.
 *
 * @param {Object} params - The parameters for updating the payment method
 * @param {string} [params.cartId] - The cart ID to update (defaults to 'current' if invalid)
 * @param {SelectedPaymentMethod} params.paymentMethod - The payment method information to set for the cart
 * @returns {Promise<{success: boolean, message: string, status: number}>} Promise resolving to the response
 */
function updatePaymentMethod(_a) {
    return __awaiter(this, arguments, void 0, function* ({ cartId, paymentMethod }) {
        // Verify and normalize the cartId
        cartId = verifyCartId(cartId);
        return yield callECBEApi(`v1/cart/${encodeURIComponent(cartId)}/update-payment-method`, {
            method: 'POST',
            headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
            body: JSON.stringify({ paymentMethod }),
        }, 'Update Payment Method');
    });
}
/**
 * Registers payment details with the ECBE API for transaction processing.
 * Makes an authenticated POST request to register payment information for a cart
 * using the provided transaction token from a payment provider.
 *
 * @param {Object} params - The parameters for registering payment details
 * @param {string} [params.cartId] - The cart ID for the payment (defaults to 'current')
 * @param {string} [params.transactionToken] - The token received from the payment gateway
 * @returns {Promise<PaymentRegisteredResponse>} Promise resolving to the payment registration confirmation
 * @throws Will log the error and rethrow if the payment registration fails
 */
function registerPaymentDetails(_a) {
    return __awaiter(this, arguments, void 0, function* ({ cartId, transactionToken }) {
        // Verify and normalize the cartId
        cartId = verifyCartId(cartId);
        return yield callECBEApi('v1/payment/register', {
            method: 'POST',
            headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
            body: JSON.stringify({ cartId, transactionToken }),
        }, 'Register Payment Details');
    });
}
/**
 * Registers a purchase order number for a cart via the ECBE API.
 * Makes an authenticated POST request to associate a purchase order number
 * with the specified cart, which can be used for order tracking and invoicing.
 *
 * @param {Object} params - The parameters for registering a purchase order number
 * @param {string} params.cartId - The cart ID to associate with the PO number (defaults to 'current' if invalid)
 * @param {string} params.poNumber - The purchase order number to register
 * @returns {Promise<RegisterPurchaseOrderResponse>} Promise resolving to the response data from the ECBE API after registering the PO number
 * @throws Will log the error and rethrow if the PO number registration fails
 */
function registerPurchaseOrderNumber(_a) {
    return __awaiter(this, arguments, void 0, function* ({ cartId, poNumber }) {
        // Verify and normalize the cartId
        cartId = verifyCartId(cartId);
        return yield callECBEApi(`/v1/cart/${encodeURIComponent(cartId)}/poNumber`, {
            method: 'POST',
            headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
            body: JSON.stringify({ poNumber }),
        }, 'Register Purchase Order Number');
    });
}
/**
 * Preauthorizes a payment transaction with the ECBE payment gateway.
 * Makes an authenticated POST request to preauthorize payment details before final processing,
 * which can be used for payment verification, fraud prevention, or holding funds.
 *
 * @param {Object} params - The parameters for preauthorizing a payment
 * @param {PreauthorizePayment} params.preauthorizedPaymentDetails - The payment details to preauthorize
 * @returns {Promise<PreauthorizePaymentResponse>} Promise resolving to the preauthorization response from the payment gateway
 * @throws Will log the error and rethrow if the payment preauthorization fails
 */
function preauthorizePayment(_a) {
    return __awaiter(this, arguments, void 0, function* ({ preauthorizedPaymentDetails }) {
        return yield callECBEApi('/v1/payment/preauthorize', {
            method: 'POST',
            headers: {
                Accept: 'application/json',
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(preauthorizedPaymentDetails),
        }, 'Preauthorize Payment');
    });
}
/**
 * Submits a payment transaction to the ECBE payment gateway for processing.
 * Makes an authenticated POST request to finalize a payment transaction after
 * preauthorization or initial validation, completing the payment flow.
 *
 * @param {Object} params - The parameters for submitting a payment
 * @param {SubmitPayment} params.submitPaymentDetails - The complete payment details for processing,
 *                                                     including cart information, payment method, and amount
 * @returns {Promise<SubmitPaymentResponse>} Promise resolving to the payment processing response from the payment gateway,
 *                         typically containing transaction IDs and approval status
 * @throws Will log the error and rethrow if the payment submission fails
 */
function submitPayment(_a) {
    return __awaiter(this, arguments, void 0, function* ({ submitPaymentDetails }) {
        return yield callECBEApi('/v1/payment/pay', {
            method: 'POST',
            headers: {
                Accept: 'application/json',
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(submitPaymentDetails),
        }, 'Submit Payment');
    });
}
/**
 * Fetches detailed order information for a specific order from the ECBE API.
 * Makes an authenticated GET request to retrieve comprehensive order data including
 * line items, pricing, shipping, and status information.
 *
 * @param {Object|string} params - Either the order ID string or an object containing the order ID
 * @param {string} [params.orderId] - The unique identifier of the order to retrieve (when params is an object)
 * @returns {Promise<OrderDetail>} Promise resolving to the complete order information
 * @throws Will log the error and rethrow if the API request fails
 */
function getOrderDetails(params) {
    return __awaiter(this, void 0, void 0, function* () {
        // Determine if params is a string or an object
        const orderId = typeof params === 'string' ? params : params.orderId;
        return yield callECBEApi(`v2/accounts/orders/${encodeURIComponent(orderId)}`, {
            method: 'GET',
            headers: {
                Accept: 'application/json',
            },
        }, 'Get Order Details');
    });
}
/**
 * Fetches tenant configuration details for specified products from the ECBE API.
 * Makes a GET request to retrieve tenant configuration based on product IDs and/or product type.
 *
 * @param {Object} params - The parameters for fetching tenant configuration
 * @param {string[]} [params.productIds] - Optional array of product IDs to get configuration for
 * @param {string[]} [params.productType] - Optional product type categorization
 * @returns {Promise<TenantResponse>} Promise resolving to tenant configuration details including
 *                                   tenant settings, available options, and configuration schema
 * @throws {Error} Will log the error details and rethrow if the tenant configuration request fails
 * @example
 * // Get configuration for specific products
 * const tenantConfig = await getTenantConfiguration({
 *   productIds: ['prod-123', 'prod-456']
 * });
 */
function getTenantConfiguration(_a) {
    return __awaiter(this, arguments, void 0, function* ({ productIds, productType }) {
        // Build query parameters
        const params = new URLSearchParams();
        if (Array.isArray(productIds) && productIds.length) {
            params.append('productIds', productIds.join(','));
        }
        if (Array.isArray(productType) && productType.length) {
            params.append('productType', productType.join(','));
        }
        const queryString = params.toString();
        const endpoint = `v1/tenants/config${queryString ? `?${queryString}` : ''}`;
        return yield callECBEApi(endpoint, {
            method: 'GET',
            headers: { Accept: 'application/json' },
        }, 'Get Tenant Configuration');
    });
}
/**
 * Fetches tenant details for a specific portfolio from the ECBE API.
 * Makes an authenticated GET request to retrieve tenant information based on
 * the provided portfolio type.
 *
 * @param {Object} params - The parameters for fetching tenant details
 * @param {string} params.portfolio - The portfolio type to retrieve tenant details for
 *                                   (e.g., "IE Base", "IE Add-On", "AX Base", "AX Add-On")
 * @returns {Promise<TenantDetailsResponse>} Promise resolving to tenant details including
 *                                          available tenants and their configuration options
 * @throws {Error} Will log the error details and rethrow if the tenant details request fails
 * @example
 * // Get tenant details for IE Base portfolio
 * const tenantDetails = await getTenantDetails({ portfolio: "IE Base" });
 */
function getTenantDetails(_a) {
    return __awaiter(this, arguments, void 0, function* ({ portfolio }) {
        return yield callECBEApi(`v2/tenants?portfolio=${encodeURIComponent(portfolio)}`, {
            method: 'GET',
            headers: { Accept: 'application/json' },
        }, 'Get Tenant Details');
    });
}
/**
 * Fetches Enterprise Customer Agreement (ECA) details for a specific user from the ECBE API.
 * Makes an authenticated GET request to retrieve ECA information based on the user's email address.
 *
 * @param {Object} params - The parameters for fetching ECA details
 * @param {string} params.usersMail - The email address of the user to retrieve ECA details for
 * @returns {Promise<ECADetailsResponse>} Promise resolving to the ECA details including
 *                                        agreement information, status, and related customer data
 * @throws Will log the error and rethrow if the API request fails
 */
function getECADetails(_a) {
    return __awaiter(this, arguments, void 0, function* ({ usersMail }) {
        return yield callECBEApi(`v1/eca/${encodeURIComponent(usersMail)}`, {
            method: 'GET',
            headers: {
                Accept: 'application/json',
            },
        }, 'Get ECA Details');
    });
}
/**
 * Validates a tenant name against the ECBE API to ensure it is available and meets requirements.
 * Makes an authenticated POST request to verify if the tenant name is valid and can be used
 * for tenant creation based on the specified portfolio.
 *
 * @param {Object} params - The parameters for tenant validation
 * @param {string} params.tenant - The tenant name to validate
 * @param {string} [params.portfolio] - Optional portfolio type to validate the tenant against
 * @returns {Promise<ValidateTenantResponse>} Promise resolving to the validation response
 *                                           containing status and any validation messages
 * @throws Will log the error details and rethrow if the validation request fails
 * @example
 * // Validate a tenant name for a specific portfolio
 * const validationResult = await validateTenant({
 *   tenant: "mycompany",
 *   portfolio: "IE Base"
 * });
 */
function validateTenant(_a) {
    return __awaiter(this, arguments, void 0, function* ({ tenant, portfolio }) {
        const queryString = portfolio ? `?portfolio=${encodeURIComponent(portfolio)}` : '';
        return yield callECBEApi(`v2/tenants/${encodeURIComponent(tenant)}/validate${queryString}`, {
            method: 'POST',
            headers: { Accept: 'application/json' },
        }, 'Validate Tenant');
    });
}
/**
 * Submits tenant information to the ECBE API for processing and tenant assignment.
 * Makes an authenticated POST request to create or assign tenant information for
 * products in a cart, including administrator details and tenant configuration.
 *
 * This function is typically called during the checkout process after users have
 * filled out tenant assignment forms. It processes all tenant information items
 * for multiple cart items and associates them with the appropriate tenant configurations.
 *
 * @param {TenantInformation} tenantInformation - The complete tenant information object containing:
 *   - tenantInformation: Array of tenant info items, each containing cart item ID and field values
 *   - cartId: The unique identifier of the cart being processed
 *   - email: The email address of the user submitting the tenant information
 * @returns {Promise<SubmitTenantResponse>} Promise resolving to the tenant submission response
 *                                         containing confirmation status and any relevant tenant IDs
 * @throws Will log the error details and rethrow if the tenant information submission fails
 */
function submitTenantInfo(tenantInformation) {
    return __awaiter(this, void 0, void 0, function* () {
        return yield callECBEApi('v1/tenant/info/', {
            method: 'POST',
            headers: {
                Accept: 'application/json',
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(tenantInformation),
        }, 'Submit Tenant Information');
    });
}
/**
 * Fetches a comprehensive review of the cart contents and checkout status from the ECBE API.
 * Makes an authenticated GET request to retrieve detailed cart review information including
 * items, pricing, shipping options, payment methods, and validation status.
 *
 * This information is typically used in the final checkout review step to display
 * a complete summary of the order before confirmation.
 *
 * @param {Object} params - The parameters for fetching cart review data
 * @param {string} params.cartId - The unique identifier of the cart to review
 * @returns {Promise<CartReviewResponse>} Promise resolving to the complete cart review information
 * @throws Will log the error and rethrow if the API request fails
 */
function getCartReview(_a) {
    return __awaiter(this, arguments, void 0, function* ({ cartId }) {
        return yield callECBEApi(`v2/checkout/cart/review/${encodeURIComponent(cartId)}`, {
            method: 'GET',
            headers: { Accept: 'application/json' },
        }, 'Get Cart Review');
    });
}
/**
 * Updates a subscription's settings via the ECBE API.
 * Makes a PUT request to modify subscription properties such as auto-renewal status and status.
 *
 * @param {Object} params - The parameters for updating the subscription
 * @param {string} params.subscriptionId - The unique identifier of the subscription to update
 * @param {UpdateSubscriptionParams} params.updateData - The subscription data to update
 * @param {boolean} params.updateData.isAutoRenew - Whether the subscription should auto-renew
 * @param {string} params.updateData.status - The status of the subscription (e.g., 'Active', 'Inactive')
 * @returns {Promise<UpdateSubscriptionResponse>} Promise resolving to the updated subscription details
 * @throws Will reject with error details if the subscription cannot be updated
 */
function updateSubscription(_a) {
    return __awaiter(this, arguments, void 0, function* ({ subscriptionId, updateData }) {
        try {
            // callECBEApi now resolves directly to the JSON response
            const data = yield callECBEApi(`/v1/subscriptions/${subscriptionId}/updatesubscription`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(updateData),
            }, 'update subscription', false);
            return data;
        }
        catch (error) {
            console.error('Error updating subscription:', error);
            throw error;
        }
    });
}
/**
 * Creates a new shipping address for the authenticated user via the ECBE API.
 * Makes a POST request to /v1/accounts/addresses with the provided address data.
 *
 * @param {CreateShippingAddressParams} addressData - The shipping address details to create
 * @returns {Promise<CreateShippingAddressResponse>} Promise resolving to the created address details
 * @throws Will reject with error details if the address cannot be created
 */
function createShippingAddress(addressData) {
    return __awaiter(this, void 0, void 0, function* () {
        try {
            const data = yield callECBEApi('/v1/accounts/addresses', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(addressData),
            }, 'create shipping address', false);
            return data;
        }
        catch (error) {
            console.error('Error creating shipping address:', error);
            throw error;
        }
    });
}
/**
 * Sets a shipping address as the default for the authenticated user via the ECBE API.
 * Makes a PATCH request to /v1/accounts/addresses/{addressId}/default.
 *
 * @param {string} addressId - The address ID to set as default
 * @returns {Promise<SetDefaultShippingAddressResponse>} Promise resolving to the updated address details
 * @throws Will reject with error details if the address cannot be set as default
 */
function setDefaultShippingAddress(addressId) {
    return __awaiter(this, void 0, void 0, function* () {
        try {
            const data = yield callECBEApi(`/v1/accounts/addresses/${addressId}/default`, {
                method: 'PATCH',
                headers: { 'Content-Type': 'application/json' },
            }, 'set default shipping address', false);
            return data;
        }
        catch (error) {
            console.error('Error setting default shipping address:', error);
            throw error;
        }
    });
}
/**
 * Deletes a shipping address for the authenticated user via the ECBE API.
 * Makes a DELETE request to /v1/accounts/addresses/{addressId}.
 *
 * @param {string} addressId - The address ID to delete
 * @returns {Promise<void>} Promise resolving when the address is deleted
 * @throws Will reject with error details if the address cannot be deleted
 */
function deleteShippingAddress(addressId) {
    return __awaiter(this, void 0, void 0, function* () {
        try {
            yield callECBEApi(`/v1/accounts/addresses/${addressId}`, {
                method: 'DELETE',
                headers: { 'Content-Type': 'application/json' },
            }, 'delete shipping address', false);
        }
        catch (error) {
            console.error('Error deleting shipping address:', error);
            throw error;
        }
    });
}
/**
 * Updates a shipping address for the authenticated user via the ECBE API.
 * Makes a PUT request to /v1/accounts/addresses/{addressId} with the provided address data.
 *
 * @param {string} addressId - The address ID to update
 * @param {CreateShippingAddressParams} addressData - The updated shipping address details
 * @returns {Promise<SetDefaultShippingAddressResponse>} Promise resolving to the updated address details
 * @throws Will reject with error details if the address cannot be updated
 */
function updateShippingAddress(addressId, addressData) {
    return __awaiter(this, void 0, void 0, function* () {
        try {
            const data = yield callECBEApi(`/v1/accounts/addresses/${addressId}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(addressData),
            }, 'update shipping address', false);
            return data;
        }
        catch (error) {
            console.error('Error updating shipping address:', error);
            throw error;
        }
    });
}

// Initialize the transformer outside the component as it doesn't depend on any component state
const ecbeTransformer = new ECBETransformer();
// Main hook to fetch and manage product data from eCBE
function useProductECBE({ productId }) {
    const { disw = {} } = (typeof window !== 'undefined' && window) || {};
    // Default product state with useMemo to avoid recreating it on each render
    const defaultProduct = useMemo(() => ({
        name: '',
        productSku: '',
        selectedPricingOption: undefined,
        pricingOptions: [],
        numberSeats: 1,
        setNumberSeats: () => { },
        productLoading: true,
        paymentOptions: [],
        preRequisites: {},
        soldThrough: '',
        portfolio: '',
        termsAndConditions: [],
        productTypology: { class: '', classification: '', type: '' },
        sellerInfo: null,
        licenseType: '',
        userType: '',
    }), []);
    const [commerceLocale, setCommerceLocale] = useState(undefined);
    const [currency, setCurrency] = useState(undefined);
    // State to hold product related data
    const [product, setProduct] = useState(defaultProduct);
    // State to track loading and error states
    const [errorInFetchingProduct, setError] = useState(false);
    // State to hold composite products for dynamic kits
    const [compositeProducts, setCompositeProducts] = useState(undefined);
    // Track the country which is selected in the country selector modal
    const [country, setCountry] = useState(undefined);
    // Stable setNumberSeats function
    const setNumberSeats = useCallback((n) => {
        setProduct((prev) => (Object.assign(Object.assign({}, prev), { numberSeats: n })));
    }, []);
    const formatPrice = (price) => {
        console.log(`[formatPrice] price=${price} commerceLocale=${commerceLocale} currency=${currency}`);
        if (!commerceLocale || !currency) {
            return price.toFixed(2);
        }
        return new Intl.NumberFormat(commerceLocale, {
            style: 'currency',
            currency: currency,
            currencyDisplay: 'narrowSymbol', // Prevent display of 2 digit currency code when locale doesn't match
            maximumFractionDigits: 2,
            minimumFractionDigits: 2,
        }).format(price);
    };
    // Memoized mapper function to transform API data to product format
    const mapEcbeToProductData = useMemo(() => {
        return (data, productLoading) => {
            // Transform the ECBE product response to a Product shape
            // need to distinguish dynamic kits from regular products here to set product properties correctly
            const product = ecbeTransformer.transformProduct(data);
            // Sets the initial number of seats based on minLicenses, defaulting to 1 if not specified
            const numberSeats = (product === null || product === void 0 ? void 0 : product.minLicenses) ? parseInt(product.minLicenses, 10) : 1;
            return {
                // Name of the product
                name: (product === null || product === void 0 ? void 0 : product.name) || '',
                // The sku associated with the product
                productSku: (product === null || product === void 0 ? void 0 : product.productSku) || '',
                // The selected pricing option, initially set to yearly by default, or first option as fallback
                selectedPricingOption: (() => {
                    var _a, _b;
                    const selectedOption = ((_a = product === null || product === void 0 ? void 0 : product.pricingOptions) === null || _a === void 0 ? void 0 : _a.find((opt) => { var _a, _b; return ((_a = opt.displayTerm) === null || _a === void 0 ? void 0 : _a.toLowerCase().includes('year')) || ((_b = opt.displayTerm) === null || _b === void 0 ? void 0 : _b.toLowerCase().includes('annual')); })) || ((_b = product === null || product === void 0 ? void 0 : product.pricingOptions) === null || _b === void 0 ? void 0 : _b[0]);
                    return selectedOption;
                })(),
                // List of available pricing options for the product
                pricingOptions: ((product === null || product === void 0 ? void 0 : product.pricingOptions) || []).map((option) => (Object.assign(Object.assign({}, option), { currency: option.currency }))),
                // The minimum number of licenses that can be purchased
                minLicenses: (product === null || product === void 0 ? void 0 : product.minLicenses) || '1',
                // The maximum number of licenses that can be purchased
                maxLicenses: (product === null || product === void 0 ? void 0 : product.maxLicenses) || '100',
                // The number of seats selected by the user, initially set to minLicenses or 1
                numberSeats: numberSeats,
                // Function to set the number of seats, which updates the product state
                setNumberSeats,
                // Indicates if the product is currently loading
                productLoading,
                // Available payment methods for the product
                paymentOptions: (product === null || product === void 0 ? void 0 : product.paymentOptions) || [],
                // Pre-requisite products
                preRequisites: (product === null || product === void 0 ? void 0 : product.preRequisites) || {},
                // Sales channel
                soldThrough: (product === null || product === void 0 ? void 0 : product.soldThrough) || '',
                // Product portfolio
                portfolio: (product === null || product === void 0 ? void 0 : product.portfolio) || '',
                // Terms and conditions
                termsAndConditions: (product === null || product === void 0 ? void 0 : product.termsAndConditions) || [],
                // Product classification information
                productTypology: (product === null || product === void 0 ? void 0 : product.productTypology) || { class: '', classification: '', type: '' },
                // Seller information
                sellerInfo: (product === null || product === void 0 ? void 0 : product.sellerInfo) || null,
                // License type
                licenseType: (product === null || product === void 0 ? void 0 : product.licenseType) || '',
                // User type
                userType: (product === null || product === void 0 ? void 0 : product.userType) || '',
            };
        };
    }, [setNumberSeats]);
    // Pricing option selection based on display term
    const selectPricingOption = useCallback((displayTerm) => {
        setProduct((prev) => {
            var _a;
            const found = (_a = prev.pricingOptions) === null || _a === void 0 ? void 0 : _a.find((o) => o.displayTerm === displayTerm);
            if (found) {
                // Ensure currency is set to prevent NumberFormat errors
                const selectedOption = Object.assign(Object.assign({}, found), { currency: found.currency });
                return Object.assign(Object.assign({}, prev), { selectedPricingOption: selectedOption });
            }
            return prev;
        });
    }, []);
    // Check if pricing term exists
    const doesProductHavePricingTerm = useCallback((displayTerm) => {
        var _a;
        const hasProductPricingTerm = !!((_a = product.pricingOptions) === null || _a === void 0 ? void 0 : _a.some((o) => {
            return o.displayTerm === displayTerm && o.autoRenew;
        }));
        return hasProductPricingTerm;
    }, [product.pricingOptions]);
    // Function to assist with determining the add to cart parameters (useful when there are more types of products)
    const determineAddToCartParameters = useCallback(({ cartItem, product }) => {
        var _a;
        return {
            productPricingId: ((_a = product.selectedPricingOption) === null || _a === void 0 ? void 0 : _a.id) || '',
            quantity: product.numberSeats || 1,
            cartItem,
        };
    }, []);
    // Function to update the number of seats for a specific composite product
    const updateCompositeNumberSeats = useCallback((productId, seats) => {
        // Also update the composite products array
        setCompositeProducts((prev) => {
            if (!prev || !prev.items)
                return prev;
            const updatedItems = prev.items.map((item) => {
                var _a;
                if (item.productId === productId) {
                    // Ensure pricing options maintain currency
                    const ensuredPricingOption = Object.assign(Object.assign({}, item.pricingOption), { currency: (_a = item.pricingOption) === null || _a === void 0 ? void 0 : _a.currency });
                    const ret = Object.assign(Object.assign({}, item), { numberSeats: seats, pricingOption: ensuredPricingOption, selectedPricingOption: ensuredPricingOption, setNumberSeats: (newSeats) => updateCompositeNumberSeats(productId, newSeats) });
                    return ret;
                }
                return item;
            });
            return Object.assign(Object.assign({}, prev), { items: updatedItems });
        });
    }, []);
    // Function to sum the prices of all composite products
    const sumCompositeProductsPrices = useCallback((composites) => {
        if (!composites || composites.length === 0)
            return 0;
        const price = composites.reduce((total, composite) => {
            // Only include items with a valid pricingOption and quantity > 0
            if (!composite.pricingOption || typeof composite.numberSeats !== 'number' || composite.numberSeats <= 0)
                return total;
            // Ensure price is a valid number
            const price = parseFloat(composite.pricingOption.price || '0') || 0;
            const seats = composite.numberSeats;
            return total + price * seats;
        }, 0);
        return price;
    }, []);
    // Function to get the current quantity of composite products in the cart
    const getCompositeProductCartQuantities = useCallback((compositeProducts) => __awaiter(this, void 0, void 0, function* () {
        var _a;
        if (!compositeProducts || compositeProducts.length === 0)
            return {};
        try {
            const cartData = yield fetchCartData();
            const quantities = {};
            // Initialize all composite products with 0 quantity
            compositeProducts.forEach((composite) => {
                if (composite.productId) {
                    quantities[composite.productId] = 0;
                }
            });
            // Search through cart items to find matching composite products
            (_a = cartData.cartItems) === null || _a === void 0 ? void 0 : _a.forEach((cartItem) => {
                if (cartItem.childProducts && cartItem.childProducts.length > 0) {
                    // This is a dynamic kit with child products
                    cartItem.childProducts.forEach((childProduct) => {
                        var _a, _b;
                        const productId = (_a = childProduct.productDetails) === null || _a === void 0 ? void 0 : _a.productId;
                        if (productId && quantities.hasOwnProperty(productId)) {
                            quantities[productId] = ((_b = childProduct.pricing) === null || _b === void 0 ? void 0 : _b.quantity) || 0;
                        }
                    });
                }
            });
            return quantities;
        }
        catch (error) {
            console.error('Error fetching cart data for composite quantities:', error);
            return {};
        }
    }), []);
    const fetchProduct = (countryObj) => __awaiter(this, void 0, void 0, function* () {
        var _a;
        setProduct((prev) => (Object.assign(Object.assign({}, prev), { productLoading: true })));
        try {
            // Get locale (cache aware) by looking up the users country in the eCBE API response
            const locale = yield getCurrentUserCartLocale();
            setCommerceLocale(locale);
            // Get the currency
            const currency = yield getCurrentUserCurrency();
            setCurrency(currency);
            const data = yield fetchProductInfo({ productId, locale });
            setProduct(mapEcbeToProductData(data, false));
            setError(false);
            // Check if this is a dynamic kit by checking classification or displayType
            const isDynamicKit = ((_a = data.productTypology) === null || _a === void 0 ? void 0 : _a.classification) === 'Dynamic Kit' || data.displayType === 'DYNAMIC_KIT';
            if (isDynamicKit && data.compositeProducts) {
                // Transform compositeProducts directly from the product catalog
                const transformedComposites = ecbeTransformer.transformCatalogCompositeProducts({
                    compositeProducts: data.compositeProducts,
                    compositeProductsLoading: false,
                });
                // Ensure all composite products have proper currency set
                if (transformedComposites === null || transformedComposites === void 0 ? void 0 : transformedComposites.items) {
                    transformedComposites.items = transformedComposites.items.map((item) => {
                        var _a;
                        const ensuredPricingOption = Object.assign(Object.assign({}, item.pricingOption), { currency: (_a = item.pricingOption) === null || _a === void 0 ? void 0 : _a.currency });
                        return Object.assign(Object.assign({}, item), { pricingOption: ensuredPricingOption, selectedPricingOption: ensuredPricingOption });
                    });
                }
                // Initialize number seats map for composite products
                transformedComposites === null || transformedComposites === void 0 ? void 0 : transformedComposites.items.forEach((item) => {
                    if (!item.productId)
                        return;
                    const isOptional = !item.required;
                    const seats = isOptional ? 0 : item.numberSeats || parseInt(item.minimumQuantity) || 0;
                    item.numberSeats = seats;
                });
                setCompositeProducts(transformedComposites);
                // // Get current quantities from shopping cart
                // if (transformedComposites?.items) {
                //   getCompositeProductCartQuantities(transformedComposites.items)
                //     .then((cartQuantities) => {
                //       // Update the composite products with cart quantities
                //       const updatedItems = transformedComposites.items.map((item) => {
                //         // Ensure all pricing objects have a currency to prevent NumberFormat errors
                //         const ensuredPricingOption = {
                //           ...item.pricingOption,
                //           currency: item.pricingOption?.currency || 'USD',
                //         };
                //         if (item.productId && cartQuantities[item.productId] !== undefined) {
                //           return {
                //             ...item,
                //             pricingOption: ensuredPricingOption,
                //             selectedPricingOption: ensuredPricingOption,
                //             currentCartQuantity: cartQuantities[item.productId],
                //           };
                //         }
                //         return {
                //           ...item,
                //           pricingOption: ensuredPricingOption,
                //           selectedPricingOption: ensuredPricingOption,
                //         };
                //       });
                //       setCompositeProducts({
                //         ...transformedComposites,
                //         items: updatedItems,
                //       });
                //     })
                //     .catch((error) => {
                //       console.error('Error fetching cart quantities for composite products:', error);
                //     });
                // }
            }
            else {
                setCompositeProducts(undefined);
            }
        }
        catch (error) {
            console.error('Error fetching product:', error);
            setError(true);
            setProduct((prev) => (Object.assign(Object.assign({}, defaultProduct), { productLoading: false })));
            setCompositeProducts(undefined);
        }
    });
    useEffect(() => {
        // Subscribe to country updates to update the country/cart if a country was selected
        const unsubscribeCountry = disw.country.subscribeToCountry((countryObject, closeSelected) => {
            // If the user selected a country, and didn't just close the modal, set the country
            if (!closeSelected) {
                // Fetch product info only if the product sku is present and product buy now url is not.
                if (productId && countryObject) {
                    // fetch product
                    fetchProduct();
                }
            }
        });
        return () => {
            unsubscribeCountry();
        };
    }, []);
    // Display configuration helper functions
    const displayConfiguration = useMemo(() => ({
        // Helper functions for scenario detection
        hasValidSku: () => !!productId,
        hasValidProduct: () => !!product && !product.productLoading,
        hasValidPricingOptions: () => { var _a; return !!(product === null || product === void 0 ? void 0 : product.selectedPricingOption) || ((_a = product === null || product === void 0 ? void 0 : product.pricingOptions) === null || _a === void 0 ? void 0 : _a.length) > 0; },
        hasValidCompositeProducts: () => { var _a; return !!((_a = compositeProducts === null || compositeProducts === void 0 ? void 0 : compositeProducts.items) === null || _a === void 0 ? void 0 : _a.length); },
        isDynamicKit: () => { var _a; return ((_a = product === null || product === void 0 ? void 0 : product.productTypology) === null || _a === void 0 ? void 0 : _a.type) === 'Dynamic Kit'; },
        isSubscriptionProduct: () => { var _a, _b; return ((_a = product === null || product === void 0 ? void 0 : product.productTypology) === null || _a === void 0 ? void 0 : _a.type) === 'Subscription product' || ((_b = product === null || product === void 0 ? void 0 : product.productTypology) === null || _b === void 0 ? void 0 : _b.type) === 'Standard product'; },
        shouldShowContactSales: () => {
            var _a, _b, _c, _d, _e, _f;
            if (!productId)
                return true;
            if (!product || product.productLoading)
                return true;
            // Check for missing or empty productTypology - show contact sales if type is not properly defined
            if (!((_a = product === null || product === void 0 ? void 0 : product.productTypology) === null || _a === void 0 ? void 0 : _a.type) || product.productTypology.type.trim() === '')
                return true;
            if (((_b = product === null || product === void 0 ? void 0 : product.productTypology) === null || _b === void 0 ? void 0 : _b.type) === 'Dynamic Kit' && !((_c = compositeProducts === null || compositeProducts === void 0 ? void 0 : compositeProducts.items) === null || _c === void 0 ? void 0 : _c.length))
                return true;
            if ((((_d = product === null || product === void 0 ? void 0 : product.productTypology) === null || _d === void 0 ? void 0 : _d.type) === 'Subscription product' || ((_e = product === null || product === void 0 ? void 0 : product.productTypology) === null || _e === void 0 ? void 0 : _e.type) === 'Standard product') &&
                !(product === null || product === void 0 ? void 0 : product.selectedPricingOption) &&
                ((_f = product === null || product === void 0 ? void 0 : product.pricingOptions) === null || _f === void 0 ? void 0 : _f.length) === 0)
                return true;
            return false;
        },
    }), [product, compositeProducts]);
    // Memoize the hook return value to prevent unnecessary re-renders in consuming components
    const hookResult = useMemo(() => ({
        // The product data fetched from eCBE, transformed to match the Product type
        product,
        // Note that pricing options are selected once for both the top-level product and child composites (bundles and kits).
        selectPricingOption,
        // Function to allow consumer to determine if the product has a pricing term available
        doesProductHavePricingTerm,
        // Function to determine the parameters for adding to cart based on the product type
        determineAddToCartParameters,
        // The country object for the product
        country,
        // Function to set the country, which can be used to resolve locale or other country-specific settings
        setCountry,
        // Return booleans to indicate if there are errors fetching the individual product or list of composites for UI handling
        errorInFetchingProduct,
        // Composite products for dynamic kits
        compositeProducts,
        // Function to update the number of seats for a specific composite product
        updateCompositeNumberSeats,
        // Function to sum the prices of all composite products
        sumCompositeProductsPrices,
        // Function to get the current quantity of composite products in the cart
        getCompositeProductCartQuantities,
        // Display configuration helper functions for determining UI display logic
        displayConfiguration,
        // Function to format prices according to the current locale and currency
        formatPrice,
    }), [
        product,
        selectPricingOption,
        doesProductHavePricingTerm,
        determineAddToCartParameters,
        country,
        setCountry,
        errorInFetchingProduct,
        compositeProducts,
        updateCompositeNumberSeats,
        sumCompositeProductsPrices,
        getCompositeProductCartQuantities,
        displayConfiguration,
        formatPrice,
    ]);
    return hookResult;
}

/**
 * Base hook to manage shared shopping cart logic.
 *
 * Encapsulates the common logic for managing shopping cart state, subscriptions,
 * and utility functions.
 *
 * @returns {Object} The shared shopping cart state and actions.
 */
function useBaseShoppingCartLogic$1({ fetchShoppingCart = false }) {
    const { disw = {} } = (typeof window !== 'undefined' && window) || {};
    const [cartData, setCartData] = useState();
    const [cartPreRequisites, setCartPreRequisites] = useState([]);
    const [cartErrors, setCartErrors] = useState([]);
    const [countItems, setCountItems] = useState(0);
    const [isMiniCartVisible, setMiniCartVisible] = useState(false);
    const [isFullCartVisible, setFullCartVisible] = useState(false);
    const [cartLoading, setCartLoading] = useState(true);
    const [commerceLocale, setCommerceLocale] = useState(undefined);
    const [currency, setCurrency] = useState(undefined);
    /**
     * Toggles the visibility of the full cart.
     *
     * @param {boolean} state - The desired visibility state of the  full cart.
     * @returns {void}
     */
    const toggleFullCart$1 = useCallback((state) => {
        toggleFullCart({ state });
    }, []);
    /**
     * Toggles the visibility of the mini cart.
     *
     * @param {boolean} state - The desired visibility state of the mini cart.
     * @returns {void}
     */
    const toggleMiniCart$1 = useCallback((state) => {
        toggleMiniCart({ state });
    }, []);
    /**
     * Adds a product to the shopping cart.
     *
     * This function attempts to add a product to the cart using the provided parameters.
     * It emits a cart event on success and returns the cart data with a success status.
     * On error, it logs the error and returns an object containing the error and failure status.
     *
     * @param {Object} params - The parameters for adding a product to the cart.
     * @param {string} params.parentProductId - The ID of the parent product to add to the cart.
     * @param {number} params.quantity - The quantity of the product to add.
     * @param {string} params.productClassification - The classification of the product.
     * @param {string} params.productId - The ID of the specific product to add.
     * @param {Array} [params.childProducts] - Optional array of child products for Dynamic Kit products.
     * @returns {Promise<Object>} A promise that resolves to an object containing:
     *   - On success: The cart data with `addToCartSuccess: true`
     *   - On error: The error object with `addToCartSuccess: false`
     */
    const addItemToCart = useCallback((_a) => __awaiter(this, [_a], void 0, function* ({ parentProductId, quantity, productClassification, productId, childProducts }) {
        const data = yield addItemToShoppingCart({ parentProductId, quantity, productClassification, productId, childProducts });
        if (data) {
            disw.cart.emitCartEvent(data, false);
        }
        else {
            console.error('🚀 ~ addItemToCart: No data returned from handleAddToCart, not emitting any Cart Data event');
        }
        return Object.assign({}, (data || {}));
    }), []);
    /**
     * @deprecated Use addItemToCart instead. This function will be removed in a future version.
     * Handles adding a product to the shopping cart.
     *
     * This function attempts to add a product to the cart using the provided parameters.
     * It emits a cart event on success and returns the cart data with a success status.
     * On error, it logs the error and returns an object containing the error and failure status.
     *
     * @param {Object} params - The parameters for adding a product to the cart.
     * @param {string} params.parentProductId - The ID of the parent product to add to the cart.
     * @param {number} params.quantity - The quantity of the product to add.
     * @param {string} params.productClassification - The classification of the product.
     * @param {string} params.productId - The ID of the specific product to add.
     * @param {Array} [params.childProducts] - Optional array of child products for Dynamic Kit products.
     * @returns {Promise<Object>} A promise that resolves to an object containing:
     *   - On success: The cart data with `addToCartSuccess: true`
     *   - On error: The error object with `addToCartSuccess: false`
     */
    const handleAddToCartEvent = addItemToCart;
    /**
     * Applies a promotion code to the cart.
     *
     * @param {Object} params - The parameters for applying the promotion code.
     * @param {string} params.discountCode - The discount code to apply.
     * @param {Function} params.onSuccess - Callback function to execute on successful application.
     * @param {Function} params.onError - Callback function to execute on error during the application.
     * @returns {Promise<void>} A promise that resolves when the promotion is applied or rejects on error.
     */
    const applyPromotion$1 = useCallback((_a) => __awaiter(this, [_a], void 0, function* ({ cartId, discountCode, onSuccess, onError }) {
        try {
            const newCartData = yield applyPromotion({ promotionCode: discountCode });
            if (newCartData) {
                onSuccess(newCartData);
            }
            else {
                console.warn('No cart data returned from applyPromotion');
                onError(new Error('No cart data returned'));
            }
        }
        catch (error) {
            console.error('Error applying promotion code:', error);
            onError(error);
        }
    }), []);
    /**
     * Removes a promotion code from the cart.
     *
     * @param {Object} params - The parameters for removing the promotion code.
     * @param {string} params.discountCode - The discount code to remove.
     * @param {Function} params.onSuccess - Callback function to execute on successful removal.
     * @param {Function} params.onError - Callback function to execute on error during the removal.
     * @returns {Promise<void>} A promise that resolves when the promotion is removed or rejects on error.
     */
    const removePromotion$1 = useCallback((_a) => __awaiter(this, [_a], void 0, function* ({ cartId, promotionCode, onSuccess, onError }) {
        try {
            const newCartData = yield removePromotion({ cartId, promotionCodeId: promotionCode === null || promotionCode === void 0 ? void 0 : promotionCode.couponCodeId });
            if (newCartData) {
                onSuccess(newCartData);
            }
            else {
                console.warn('No cart data returned from removePromotion');
                onError(new Error('No cart data returned'));
            }
        }
        catch (error) {
            console.error('Error removing promotion code:', error);
            onError(error);
        }
    }), []);
    /**
     * Indicates that the checkout process has started.
     *
     * @returns {void}
     */
    const checkoutStarted$1 = useCallback(() => {
        return checkoutStarted({ cartData: cartData });
    }, [cartData]);
    /**
     * Checks if the checkout process is enabled.
     *
     * @returns {boolean} - Returns `true` if checkout is enabled, otherwise `false`.
     */
    const isCheckoutEnabled = useCallback(() => {
        return disw.cart.isCheckoutEnabled();
    }, []);
    /**
     * Updates items in the shopping cart by adding or subtracting products.
     *
     * @param {Object} params - An object containing the arguments required for updating cart items.
     * @param {AddOrSubtractProductsArgs} params.args - The arguments required for adding or subtracting products.
     * @param {string | undefined} params.bundleSku - The SKU of the product bundle, if this item is a bundle.
     * @param {Function} params.onSubscriptionTermsConflictError - Callback function to handle subscription terms conflict errors.
     * @param {Function} params.onPress - Callback function to handle the updated cart data or errors.
     * @returns {Promise<void>}
     */
    const updateItemsInCart = useCallback((_a) => __awaiter(this, [_a], void 0, function* ({ numberSeats, cartId, cartItemId, onPress }) {
        try {
            const cartData = yield addOrSubtractProducts({ cartId, numberSeats, cartItemId });
            // addOrSubtractProducts fetches fresh cart data, so we transform and emit it
            if (cartData) {
                disw.cart.emitCartEvent(cartData, false);
            }
            onPress && onPress();
        }
        catch (error) {
            console.error('Error in updateItemsInCart:', error);
        }
    }), []);
    /**
     * Removes an item from the shopping cart.
     *
     * This function removes a specific item from the cart based on its ID, promotion code, and index.
     * It handles success and error callbacks to manage the UI state.
     *
     * @param {Object} params - The parameters for removing an item from the cart.
     * @param {string} params.conflictingCartItem - The cart item to be removed.
     * @param {string} [params.promoCode] - The promotion code applied to the cart item (optional).
     * @param {number} [params.itemIndex] - The index of the cart item in the cart (optional).
     * @param {Function} params.onPress - Callback function to execute on successful removal.
     * @param {Function} params.onError - Callback function to execute on error during removal.
     * @returns {Promise<void>} A promise that resolves when the item is successfully removed or rejects on error.
     */
    const removeFromCart = useCallback((_a) => __awaiter(this, [_a], void 0, function* ({ cartId, cartItemId, onPress, onError }) {
        try {
            const cartData = yield removeItem({ cartId, cartItemId });
            onPress && onPress(); // Call the success callback
            disw.cart.emitCartEvent(cartData, false);
        }
        catch (error) {
            console.error('Error removing item from cart:', error);
            onError && onError(); // Call the error callback
        }
    }), []);
    const DEFAULT_MIN_LICENSES = '1';
    const DEFAULT_MAX_LICENSES = '10';
    /**
     * Retrieves the minimum and maximum licenses for a product.
     *
     * This function calculates the minimum and maximum number of licenses allowed for a product
     * based on its properties. Defaults are used if the product does not specify these values.
     *
     * @param {Object} product - The product object containing license information.
     * @returns {Object} An object containing `minLicenses` and `maxLicenses`.
     * @returns {string | number} return.minLicenses - The minimum number of licenses allowed.
     * @returns {string | number} return.maxLicenses - The maximum number of licenses allowed.
     */
    const getMinAndMaxLicenses = useCallback((product) => {
        var _a, _b, _c, _d;
        // Prefer minimumQuantity/maximumQuantity, fallback to minLicenses/maxLicenses, then defaults
        const minRaw = (_b = (_a = product === null || product === void 0 ? void 0 : product.quantityRules) === null || _a === void 0 ? void 0 : _a.minimum) !== null && _b !== void 0 ? _b : DEFAULT_MIN_LICENSES;
        const maxRaw = (_d = (_c = product === null || product === void 0 ? void 0 : product.quantityRules) === null || _c === void 0 ? void 0 : _c.maximum) !== null && _d !== void 0 ? _d : DEFAULT_MAX_LICENSES;
        // Always coerce to string for parseInt, fallback to '0' if still undefined/null
        const min = Number.parseInt(String(minRaw !== null && minRaw !== void 0 ? minRaw : DEFAULT_MIN_LICENSES), 10);
        const max = Number.parseInt(String(maxRaw !== null && maxRaw !== void 0 ? maxRaw : DEFAULT_MAX_LICENSES), 10);
        const minLicenses = Number.isFinite(min) && min > 0 ? String(min) : DEFAULT_MIN_LICENSES;
        const maxLicenses = Number.isFinite(max) && max > 0 ? String(max) : DEFAULT_MAX_LICENSES;
        return { minLicenses, maxLicenses };
    }, []);
    /**
     * Handles guest session management when country changes.
     * Creates or updates guest session based on country compatibility.
     */
    const handleGuestSessionManagement = useCallback((countryObject) => {
        var _a;
        const guestSession = getGuestSession();
        const currentCountryCode = countryObject.countrycode;
        if (guestSession) {
            // If guest session country doesn't match current country, create new session
            if (((_a = guestSession.country) === null || _a === void 0 ? void 0 : _a.countrycode) !== currentCountryCode) {
                clearGuestSession();
                createGuestSession();
            }
        }
        else {
            // Create new guest session if none exists
            createGuestSession();
        }
    }, []);
    /**
     * Handles unauthenticated user scenario when country changes.
     */
    const handleUnauthenticatedCountryChange = useCallback((countryObject) => {
        handleGuestSessionManagement(countryObject);
    }, [handleGuestSessionManagement]);
    /**
     * Handles authenticated user scenario when country changes.
     * Preserves guest cart data if available.
     */
    const handleAuthenticatedCountryChange = useCallback(() => __awaiter(this, void 0, void 0, function* () {
        setCartLoading(true);
        const guestSession = getGuestSession();
        if (!guestSession) {
            console.log('[useShoppingCart] No guest session found, skipping guest cart preservation');
            return;
        }
        console.log('[useShoppingCart] Guest session found, preserving guest cart data');
        // if there's a guest session, assign the guest cart to the customer
        try {
            yield handlePreserveGuestCartData();
        }
        catch (error) {
            console.error(`[useShoppingCart] Error preserving guest cart data: `, error);
            clearGuestSession();
            setCartErrors((currentErrors) => {
                console.log(`[useShoppingCart] Adding error to cartErrors. Current errors:`, currentErrors);
                const newErrors = [...currentErrors, constants.CART_ERROR_PRESERVING_GUEST_CART];
                console.log(`[useShoppingCart] New cartErrors array:`, newErrors);
                return newErrors;
            });
        }
    }), []);
    /**
     * Loads cart data after country changes and updates shopping cart component state.
     *
     */
    const loadCartAfterCountryChange = useCallback(() => __awaiter(this, void 0, void 0, function* () {
        var _a, _b;
        try {
            const shoppingCart = yield loadCartData({ cartId: 'current' });
            if (shoppingCart) {
                setCartData(shoppingCart);
                setCountItems((shoppingCart === null || shoppingCart === void 0 ? void 0 : shoppingCart.cartItems) ? shoppingCart.cartItems.length : 0);
                setCartLoading(false);
                // Load prerequisites for authenticated users
                const isAuthenticated = (_b = (_a = disw === null || disw === void 0 ? void 0 : disw.account) === null || _a === void 0 ? void 0 : _a.getAccount()) === null || _b === void 0 ? void 0 : _b.authenticated;
                if (isAuthenticated) {
                    const preRequisites = yield getCartPreRequisites({ cartId: 'current' });
                    setCartPreRequisites(preRequisites);
                }
            }
            else {
                setCartData(MIMICKED_ECBE_EMPTY_CART);
                setCountItems(0);
                setCartLoading(false);
            }
        }
        catch (error) {
            console.error('[useShoppingCart] Error loading cart data:', error);
            setCartData(undefined);
            setCountItems(0);
            setCartLoading(false);
            // Add error to cartErrors array for cart loading failures
            setCartErrors((currentErrors) => {
                console.log(`[useShoppingCart] Adding cart loading error to cartErrors. Current errors:`, currentErrors);
                const newErrors = [...currentErrors, constants.CART_ERROR_FETCHING_CART_DATA];
                console.log(`[useShoppingCart] New cartErrors array after loading error:`, newErrors);
                return newErrors;
            });
        }
    }), []);
    /**
     * Main callback for handling country changes.
     * Orchestrates the flow between authenticated and unauthenticated scenarios.
     */
    const handleCountryChange = useCallback((countryObject, closeSelect) => __awaiter(this, void 0, void 0, function* () {
        var _a, _b;
        // Get the current user cart locale by taking their country and looking it up via ecBE country API
        const commerceLocale = yield getCurrentUserCartLocale();
        setCommerceLocale(commerceLocale);
        const currency = yield getCurrentUserCurrency();
        setCurrency(currency);
        if (!fetchShoppingCart) {
            return;
        }
        const isAuthenticated = (_b = (_a = disw === null || disw === void 0 ? void 0 : disw.account) === null || _a === void 0 ? void 0 : _a.getAccount()) === null || _b === void 0 ? void 0 : _b.authenticated;
        if (isAuthenticated) {
            yield handleAuthenticatedCountryChange();
        }
        else {
            handleUnauthenticatedCountryChange(countryObject);
        }
        yield loadCartAfterCountryChange();
    }), [fetchShoppingCart, handleAuthenticatedCountryChange, handleUnauthenticatedCountryChange, loadCartAfterCountryChange]);
    const formatPrice = (price) => {
        console.log(`[formatPrice] price=${price} commerceLocale=${commerceLocale} currency=${currency}`);
        if (!commerceLocale || !currency) {
            return price.toFixed(2);
        }
        return new Intl.NumberFormat(commerceLocale, {
            style: 'currency',
            currency: currency,
            currencyDisplay: 'narrowSymbol', // Prevent display of 2 digit currency code when locale doesn't match
            maximumFractionDigits: 2,
            minimumFractionDigits: 2,
        }).format(price);
    };
    /**
     * Subscribes to cart changes, mini cart visibility, and full cart visibility.
     * Updates the cart data, mini cart visibility, and full cart visibility accordingly.
     * Also initializes user authentication check on component mount.
     *
     * @returns {void}
     */
    useEffect(() => {
        setCartLoading(true);
        const unsubscribeMiniCart = disw.cart.subscribeToMiniCart(setMiniCartVisible);
        const unsubscribeFullCart = disw.cart.subscribeToFullCart(setFullCartVisible);
        /**
         * The subscription to the cart module here is responsible for handling cart update events. There are two scenarios where we need to update the shopping cart data.
         * 1. The country changes and we need to re-fetch the shopping cart (mainly to cover the anonymous scenario because the country should never change once logged in.
         * 2. The cart is updated via an add / update cart call, in which case the cart is re-fetched and the event is emitted, captured here and the hook updates the state
         * passed to all of the consumers (ecommerce heros, and anything else needing cart info so each independent item is not fetching the cart independently).
         *
         */
        const unsubscribeCart = disw.cart.subscribeToCart((cartData) => __awaiter(this, void 0, void 0, function* () {
            setCartData(cartData);
            setCountItems((cartData === null || cartData === void 0 ? void 0 : cartData.cartItems) ? cartData.cartItems.length : 0);
            setCartLoading(false);
        }));
        // Subscribe to country updates to update the country/cart if a country was selected
        const unsubscribeCountry = disw.country.subscribeToCountry(handleCountryChange);
        return () => {
            unsubscribeMiniCart && unsubscribeMiniCart();
            unsubscribeFullCart && unsubscribeFullCart();
            unsubscribeCountry && unsubscribeCountry();
            unsubscribeCart && unsubscribeCart();
        };
    }, [handleCountryChange]);
    return {
        cartData,
        cartPreRequisites,
        cartErrors,
        countItems,
        isMiniCartVisible,
        isFullCartVisible,
        setCartData,
        setCartErrors,
        setCountItems,
        toggleFullCart: toggleFullCart$1,
        toggleMiniCart: toggleMiniCart$1,
        checkoutStarted: checkoutStarted$1,
        removePromotion: removePromotion$1,
        applyPromotion: applyPromotion$1,
        isCheckoutEnabled,
        getMinAndMaxLicenses,
        updateItemsInCart,
        addOrSubtractProductsFromCart,
        removeFromCart,
        addItemToCart,
        handleAddToCartEvent,
        cartLoading,
        formatPrice,
    };
}
/**
 * Custom hook to manage shopping cart logic. Can share state with any components used in the ShoppingCartProvider component tree.
 *
 * @returns {CartContextType} The shopping cart state and actions, via context.
 */
const useShoppingCartLogic = ({ fetchShoppingCart = false }) => {
    const baseLogic = useBaseShoppingCartLogic$1({ fetchShoppingCart });
    return Object.assign(Object.assign({}, baseLogic), { cartData: baseLogic.cartData });
};
/**
 * Custom hook to subscribe to shopping cart logic. Can NOT share state without propagating functions/state through components.
 *
 * @returns {ShoppingCartHook} A hook for the shopping cart state and actions.
 */
const subscribeToShoppingCartLogic$1 = ({ fetchShoppingCart = false }) => {
    const baseLogic = useBaseShoppingCartLogic$1({ fetchShoppingCart });
    return Object.assign(Object.assign({}, baseLogic), { cartData: baseLogic.cartData });
};
// Define the ShoppingCartContext with a default value of null
const ShoppingCartContext$1 = createContext(null);
/**
 * The provider component for the shopping cart context.
 *
 * Wraps its children with the `ShoppingCartContext.Provider` and provides shopping cart state and actions.
 *
 * @param {Object} props - The props for the provider.
 * @param {React.ReactNode} props.children - The child components to wrap with the provider.
 * @returns {React.ReactElement} The provider component.
 */
const ShoppingCartProvider = ({ children, fetchShoppingCart }) => {
    const shoppingCartValue = useShoppingCartLogic({ fetchShoppingCart });
    return React.createElement(ShoppingCartContext$1.Provider, { value: shoppingCartValue }, children);
};
/**
 * Custom hook to access the shopping cart context.
 *
 * Provides access to cart data, errors, item count, and functions to toggle the visibility
 * of the mini cart and full cart. Also includes utility functions for checkout and country updates.
 *
 * @returns {CartContextType | ShoppingCartHook} The shopping cart context or the subscribeToShoppingCartLogic hook.
 */
const useShoppingCartECBE = ({ fetchShoppingCart = false } = {}) => {
    // In the case we use context, the fetchShoppingCart will have been passed into the providers initialization.
    const context = useContext(ShoppingCartContext$1);
    // If the context is not available, call and return the subscribeToShoppingCartLogic hook directly
    if (!context) {
        return subscribeToShoppingCartLogic$1({ fetchShoppingCart });
    }
    return context;
};

const useCommerceButton = (props) => {
    const type = props.type;
    let buttonFunction = props.onPress;
    const { disw = {} } = (typeof window !== 'undefined' && window) || {};
    const [loading, setLoading] = useState(false);
    const setLoadingStart = () => {
        setLoading(true);
    };
    const setLoadingEnd = () => {
        setLoading(false);
    };
    /**
     * In the scenario of the 'Cart' type, we want to prescribe what the button press should do (addOrSubtractFromCart).
     * With that said, we also need to leave it open ended in the sense that we can still execute additional
     * logic via the buttonPress prop. For that reason, the code below ensures addOrSubtractFromCart is called before
     * executing the additional function.
     */
    if (type) {
        /**
         * In the scenario of the 'Cart' type, we want to prescribe what the button press should do (addOrSubtractFromCart).
         * With that said, we also need to leave it open ended in the sense that we can still execute additional
         * logic via the buttonPress prop. For that reason, the code below ensures addOrSubtractFromCart is called before
         * executing the additional function.
         */
        if (type.toLowerCase() === 'cart') {
            buttonFunction = (e) => __awaiter(void 0, void 0, void 0, function* () {
                e.preventDefault();
                // Set the loading state to disable the buttons while loading
                setLoadingStart();
                if (props.onPress) {
                    yield props.onPress();
                }
                // Conclude the loading state after the onPress concludes
                setLoadingEnd();
            });
        }
        // Live Chat Commerce button will initiate the live chat modal popping up for customer to interact with sales chat.
        if (type.toLowerCase() === 'chat') {
            buttonFunction = (e) => __awaiter(void 0, void 0, void 0, function* () {
                e.preventDefault();
                // Initiate the live chat preliminary modal pop-up.
                disw.chat.initiateSalesPreliminaryModal();
                // We should still execute the callback after opening chat for any actions fired after chat pops
                if (props.onPress) {
                    yield props.onPress();
                }
                e.stopPropagation();
            });
        }
    }
    return { loading, onPress: buttonFunction };
};

/**
 * This key is used only for signing requests for the eCommerce events endpoint.
 */
const CUSTOM_SECRET_KEY = '70ebe8c9da6e4647948e47ca366ea2cc1d015b4cfee83cce6ffe48f9ea0a62a5';
/**
 * Post event data for an Ecommerce Event to the eCommerce events SNS topic.
 *
 * @param eventData The data for the event
 * @param eventType The type of event
 * @returns The response from the eCommerce events posting API
 */
function postFrontendEcommerceEvent(eventData, eventType) {
    return new Promise((resolve, reject) => {
        // Create the body of the request
        const requestBody = JSON.stringify({
            eventType,
            eventData
        });
        // Get the signed token for this request
        const token = addSecretKeyAndHash(requestBody, CUSTOM_SECRET_KEY);
        fetch(`${getMiddlewareAPIURL({ env: getCartEnv() })}/postEcommerceEvents`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${token}`
            },
            body: requestBody
        }).then(response => {
            response.json().then(data => {
                resolve(data);
            });
        }).catch(error => {
            console.log('Error posting eCommerce event:', error);
            reject(error);
        });
    });
}

// Utility functions used across Product related components
// Constants
const DEFAULT_MIN_LICENSES = '1';
const DEFAULT_MAX_LICENSES = '10';
/**
 * Format the currency according to the current locale
 *
 * @param {string} locale A string with a BCP 47 language tag
 * @param {string} currency ISO 4217 currency code
 * @param {number} price numerical value to display as a price
 * @returns
 */
const formatPrice = (locale, currency, price) => {
    return new Intl.NumberFormat(locale, {
        style: 'currency',
        currency: currency,
        currencyDisplay: 'narrowSymbol', // Prevent display of 2 digit currency code when locale doesn't match
        maximumFractionDigits: 2,
        minimumFractionDigits: 2,
    }).format(price);
};
/**
 * A helper function that adds a parameter and its value to the url.
 *
 * @param {string} url - the url to add the context to.
 * @param {string} parameter -  the parameter that needs to be attached to the url.
 * @param {string} parameterValue - this is the value of the parameter passed.
 * @returns {string} string containing the url attached with the parameter and value.
 */
function addQueryParameter(url, parameter, parameterValue) {
    if (!parameter || !parameterValue || !url) {
        return url;
    }
    try {
        const parsedUrl = new URL(url);
        if (!parsedUrl.searchParams.get(parameter)) {
            parsedUrl.searchParams.set(parameter, parameterValue);
        }
        return parsedUrl.toString();
    }
    catch (e) {
        // url is not valid
        return url;
    }
}

/**
 * Validate contact info
 *
 * @param contactInfo The contact info to validate
 * @returns A promise that resolves to a validated {@link DEXContactInfo} object
 */
function validateContactInfo(customerContactInfo) {
    return new Promise((resolve, reject) => {
        // Get the request body
        const requestBody = JSON.stringify({
            firstName: customerContactInfo.firstName,
            lastName: customerContactInfo.lastName,
            email: customerContactInfo.email,
            phone: customerContactInfo.phone,
            company: customerContactInfo.company,
            address1: customerContactInfo.address1,
            address2: customerContactInfo.address2,
            address3: customerContactInfo.address3,
            city: customerContactInfo.city,
            region: customerContactInfo.region,
            postalCode: customerContactInfo.postalCode,
            country: customerContactInfo.country
        });
        // Make the request to DEX to validate the address
        callDEXApi('/contact/validate', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            },
            body: requestBody
        })
            .then(response => {
            response.json().then((responseData) => {
                if (response.ok) {
                    resolve(responseData.validated);
                }
                else {
                    console.log(`Got a ${response.status} response from DEX validating contact info: ${JSON.stringify(responseData)}`);
                    reject();
                }
            });
        })
            .catch(error => {
            console.error(`Got an error trying to validate contact info: ${error}`);
            reject();
        });
    });
}
/**
 * Call the DEX API to assign contact info to the specified cart.
 *
 * @param {AssignContactInfoProps} props
 * @returns The fetch response from the API call
 */
function assignContactInfoToCart(props) {
    const requestBody = JSON.stringify(props);
    return callDEXApi('/checkout/account/info', {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json'
        },
        body: requestBody,
    });
}
/**
 * Check if an account exists for a given user by email address.
 *
 * @param emailAddress The email address to check for an account
 * @returns true if an account exists, false otherwise
 */
function checkForAccount(emailAddress) {
    return new Promise((resolve, reject) => {
        callDEXApi(`/user/${emailAddress}/country`, {
            method: 'GET',
            headers: {
                'Accept': 'application/json'
            }
        }).then(response => {
            if (response.ok) {
                resolve(true);
            }
            else {
                resolve(false);
            }
        }).catch(error => {
            reject(error);
        });
    });
}
/**
 * Get contact info for a user from DEX, by email.
 *
 * @param email The email address of the user to fetch contact info for
 * @returns A promise that resolves to an array of {@link DEXContactInfo} objects.
 */
function getContactInfoForAccount(email) {
    return new Promise(resolve => {
        callDEXApi(`/contact/${email}/`, {
            method: 'GET'
        }).then(response => {
            response.json().then((data) => {
                if (response.ok) {
                    resolve(data.contacts);
                }
                else {
                    resolve([]);
                }
            });
        }).catch(error => {
            console.error(`There was an error fetching user contact info: ${error}`);
            resolve([]);
        });
    });
}
/**
 * Calculate sales tax for the given cart.
 *
 * @param cartId The ID of the cart to calculate sales tax for
 * @param emailAddress The email address assigned to the cart
 */
function calculateSalesTaxForCart(cartId, email) {
    return new Promise((resolve, reject) => {
        const body = JSON.stringify({
            cartId,
            email
        });
        callDEXApi('/checkout/tax/calculate', {
            method: 'POST',
            body,
            headers: {
                Accept: 'application/json',
                'Content-Type': 'application/json',
            }
        }).then(response => {
            if (response.ok) {
                response.json().then(data => {
                    resolve(data);
                });
            }
            else {
                // This response is not okay. Sad times
                reject(`The request to the DEX API failed with a ${response.status} status code`);
            }
        }).catch(error => {
            reject(`An error occurred trying to fetch order review details: ${error}`);
        });
    });
}
/**
 * Get the order review data for this cart
 *
 * @param cartId The cart ID
 * @param email The email address assigned to the cart
 */
function getOrderReviewData(cartId, email) {
    return new Promise((resolve, reject) => {
        callDEXApi(`/checkout/cart/review/${cartId}?email=${email}`, {
            method: 'GET',
            headers: {
                Accept: 'application/json',
            }
        }).then(response => {
            if (response.ok) {
                response.json().then(data => {
                    // data.cart = postProcessCartData(data?.cart);
                    resolve(data);
                });
            }
            else {
                // This response is not okay. Sad times
                reject(`The request to the DEX API failed with a ${response.status} status code`);
            }
        }).catch(error => {
            reject(`An error occurred trying to fetch order review details: ${error}`);
        });
    });
}
/**
 * Simple helper function to check a Product's attributes to see if it requires ECA
 *
 * @param product as Product
 * @returns boolean
 */
function requiresECA(product) {
    return product ? product.productType === 'ECA' : false;
}
/**
 * Get ECA information for an account by webkey email
 *
 * @param webkeyEmail The email address of the account to fetch ECA info for
 * @returns The ECA accounts response
 */
function getEcaInformation(webkeyEmail) {
    return new Promise((resolve, reject) => {
        // Build the request
        callDEXApi(`/eca/accounts/${webkeyEmail}`, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
            }
        }).then(response => resolve(response.json())).catch((e) => {
            console.error(e);
            reject([]);
        });
    });
}
/**
 * Assign ECA admins.
 *
 * @param props The required parameters for assigning ECA admins
 */
function assignECAAdmins(props) {
    return new Promise((resolve, reject) => {
        const body = JSON.stringify(props);
        callDEXApi(`/tenant/info`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            },
            body
        }).then((response) => {
            resolve('success');
        }).catch(error => {
            console.error(`Got an error trying to assign admin to cart items: ${error}`);
            reject([]);
        });
    });
}

// Define a client ID for determining the Adyen config to use
const DEX_ADYEN_CLIENT_ID = 'SMB';
/**
 * Get the available payment methods for a given cart.
 *
 * @param cartId The cart ID
 * @param countryCode The 2 digit country code that the customer is in
 */
function getPaymentMethodsForCart(cartId, countryCode) {
    return new Promise((resolve, reject) => {
        const body = JSON.stringify({
            clientId: DEX_ADYEN_CLIENT_ID,
            cartId,
            countryCode,
            email: getWebkeyEmail() // Include the user's email
        });
        callDEXApi('/payment/methods', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body,
        }).then(response => {
            if (response.ok) {
                return response.json().then((paymentOptionsResponse) => {
                    resolve(paymentOptionsResponse);
                });
            }
            else {
                reject(`Failed to get payment options: ${response.statusText}`);
            }
        }).catch(error => {
            // Catch any errors and re-throw them to be handled elsewhere.
            // This is done to ensure that the error can be caught and handled appropriately.
            reject(error);
        });
    });
}
/**
 * Fix the stupid type in DEX's payment response.
 */
function fixDexTypo(data) {
    // Fix the silliness
    const paymentResponse = Object.assign(Object.assign({}, data), { paymentResponse: data.paymentRespnose });
    delete paymentResponse.paymentRespnose;
    return paymentResponse;
}
/**
 * Post a request for quote to DEX.
 *
 * @param requestForQuoteData Request for quote payment data.
 * @returns A promise which resolves to {@link CheckoutPaymentResponse}
 */
function postRequestForQuote(requestForQuoteData) {
    return new Promise((resolve, reject) => {
        const body = JSON.stringify(requestForQuoteData);
        // Build the request
        callDEXApi('/order/rfq', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body,
        }).then(response => {
            if (response.ok) {
                return response.json().then((responseData) => {
                    resolve(fixDexTypo(responseData));
                });
            }
            else {
                reject(`Error posting request for quote: ${response.statusText}`);
            }
        }).catch((e) => {
            console.error(e);
            reject('An unexpected error occurred while trying to submit the request for quote.');
        });
    });
}
/**
 * Retrieves a list of POA payers associated with the specified Webkey email.
 *
 * @param webkeyEmail - The email address used to identify the Webkey account.
 * @returns A Promise that resolves with the list of POA payers as returned by the API,
 *          or rejects with an error message if the request fails.
 */
function getPOAPayersByEmail(webkeyEmail) {
    return new Promise((resolve, reject) => {
        callDEXApi(`/payment/poa/payers/${webkeyEmail}`, {
            method: 'GET',
        }).then(response => {
            if (response.ok) {
                return response.json().then(resolve);
            }
            else {
                reject(`Failed to get payers by webkey: ${response.statusText}`);
            }
        });
    });
}
/**
 * Performs a credit check for a Pay On Account (POA) transaction using the provided cart and payer information.
 *
 * Sends a POST request to the `/payment/poa/credit/check` endpoint with the relevant cart and payer details.
 * Returns a promise that resolves with the POA credit check response.
 *
 * @param cartId - The unique identifier for the cart.
 * @param payerId - (Optional) The unique identifier for the payer.
 * @param cartAmount - (Optional) The total amount of the cart.
 * @param currencyCode - (Optional) The currency code for the transaction (e.g., 'USD').
 * @param taxAmount - (Optional) The tax amount for the cart.
 * @returns A promise that resolves to a {@link POACreditCheckResponse} object containing the credit check result.
 */
function poaCreditCheck(cartId, payerId, cartAmount, currencyCode, taxAmount) {
    return new Promise((resolve, reject) => {
        const poaCreditCheckData = {
            cartId,
            payerId,
            cartAmount,
            taxAmount,
            currencyCode,
            email: getWebkeyEmail(),
        };
        callDEXApi(`/payment/poa/credit/check`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(poaCreditCheckData),
        }).then(response => {
            response.json().then(resolve);
        }).catch(error => {
            // Catch any errors and re-throw them to be handled elsewhere.
            // This is done to ensure that the error can be caught and handled appropriately.
            reject(error);
        });
    });
}
/**
 * Create a 3DS return URL for the checkout, containing all the information needed to restore a checkout to the previous state
 * before the 3DS redirect.
 *
 * @param cartId The cart ID
 * @param email The email assigned to the cart
 */
function create3DSReturnUrl(cartId, email) {
    return addQueryParameter(addQueryParameter(addQueryParameter(window.location.href, '3DSreturn', 'true'), 'cartId', cartId), 'email', email);
}
/**
 * Sends encrypted payment data to the DEX API to process a payment for the specified cart.
 *
 * This function constructs a payment request including cart details, user email, and a 3DS return URL,
 * then sends a POST request to the `/payment/pay` endpoint. If the request is successful, it resolves
 * with a normalized {@link CheckoutPaymentResponse} object. If the request fails, it rejects with an error message.
 *
 * @param cartId - The unique identifier for the cart being paid for.
 * @param paymentData - The encrypted payment details to be sent to the API.
 * @param createOrder - Whether to create an order as part of the payment process (default: false).
 * @returns A promise that resolves to a {@link CheckoutPaymentResponse} containing the payment result.
 */
function sendPaymentData(cartId, paymentData, createOrder) {
    return new Promise((resolve, reject) => {
        // Get the user's email
        const email = getWebkeyEmail();
        // Create a return URL for 3DS cards
        const returnUrl = create3DSReturnUrl(cartId, email);
        const paymentDataRequest = {
            originUrl: window.location.origin,
            cartId,
            returnUrl,
            skipOrderCreation: !createOrder,
            paymentRequestDetails: paymentData,
            clientId: DEX_ADYEN_CLIENT_ID,
            email // Include the user's email obtained from the webkeyEmail function in the request body.
        };
        // Send a POST request to the API.
        callDEXApi('/payment/pay', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(paymentDataRequest),
        }).then(response => {
            if (response.ok) {
                // If the response is successful, parse it as JSON and return it.
                return response.json().then((responseData) => {
                    resolve(fixDexTypo(responseData));
                });
            }
            else {
                reject(`An error occurred posting payment data: ${response.statusText}`);
            }
        }).catch(error => {
            reject(error);
        });
    });
}
/**
 * Sends a Pay On Account (POA) payment request to the DEX API for the specified cart and payer details.
 *
 * This function constructs a payment request using the provided POA payment details and the user's webkey email,
 * then sends a POST request to the `/payment/poa/pay` endpoint. If the request is successful, it resolves
 * with the parsed response from the API. If the request fails, it rejects with an error message.
 *
 * @param props - The POA payment details, including cart, payer, and payment information.
 * @returns A promise that resolves to the API response for the POA payment.
 */
function payWithPOA(props) {
    return new Promise((resolve, reject) => {
        const request = Object.assign(Object.assign({}, props), { email: getWebkeyEmail() });
        callDEXApi(`/payment/poa/pay`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(request),
        }).then(response => {
            if (response.ok) {
                return response.json().then(resolve); // If the response is successful, parse it as JSON and return it.
            }
            else {
                reject(`Error trying to pay with POA: ${response.statusText}`);
            }
        }).catch(error => {
            // Catch any errors and re-throw them to be handled elsewhere.
            // This is done to ensure that the error can be caught and handled appropriately.
            reject(error);
        });
    });
}
/**
 * Sends payment details to the DEX API for processing, optionally creating an order.
 *
 * @param cartId - The cart ID for which payment is being processed.
 * @param paymentData - The payment state data containing necessary payment information.
 * @param createOrder - Optional flag indicating whether to create an order. Defaults to `false`.
 * @returns A promise that resolves to a {@link PaymentDetailsResponse} object containing the result of the payment details request.
 *
 * @remarks
 * - If the request is successful, the promise resolves with the parsed payment details response.
 * - If the request fails, the promise is rejected with the error.
 * - Unsuccessful payment attempts (e.g., wrong 3DS password) still resolve with a `PaymentDetailsResponse`.
 */
function sendPaymentDetails(cartId, paymentData, createOrder = false) {
    return new Promise((resolve, reject) => {
        // Create request body
        const paymentDetailsRequest = {
            cartId,
            stateData: paymentData,
            email: getWebkeyEmail(),
            clientId: DEX_ADYEN_CLIENT_ID,
            skipOrderCreation: !createOrder,
        };
        callDEXApi('/payment/details', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(paymentDetailsRequest),
        }).then((response) => __awaiter(this, void 0, void 0, function* () {
            // If the response didn't fail, parse it as JSON and return it.
            // The request might have been unsuccessful in some aspect, such as the
            // user using the wrong password for 3DS, but that will still result in
            // the same PaymentDetailsResponse object that a successful request would.
            const paymentDetailsData = yield response.json();
            resolve(paymentDetailsData);
        })).catch(error => {
            // Catch any errors and re-throw them to be handled elsewhere.
            // This is done to ensure that the error can be caught and handled appropriately.
            reject(error);
        });
    });
}

const SUPPORTED_COUNTRIES = [
    {
        locale: 'da_DK',
        browserLocale: 'da_DK',
        currency: 'DKK',
        countryCode: 'DK',
        vatRequired: true,
        vatPrefixDisplay: 'DK'
    },
    {
        locale: 'en_GB',
        browserLocale: 'en_GB',
        currency: 'GBP',
        countryCode: 'GB',
        vatRequired: true,
        vatPrefixDisplay: 'GB;XI'
    },
    {
        locale: 'en_NZ',
        browserLocale: 'en_NZ',
        currency: 'AUD',
        countryCode: 'NZ',
        vatRequired: false,
        vatPrefixDisplay: null
    },
    {
        locale: 'pt_PT',
        browserLocale: 'pt_PT',
        currency: 'EUR',
        countryCode: 'PT',
        vatRequired: true,
        vatPrefixDisplay: 'PT'
    },
    {
        locale: 'bg_BG',
        browserLocale: 'bg_BG',
        currency: 'EUR',
        countryCode: 'BG',
        vatRequired: true,
        vatPrefixDisplay: 'BG'
    },
    {
        locale: 'en_CA',
        browserLocale: 'en_CA',
        currency: 'CAD',
        countryCode: 'CA',
        vatRequired: false,
        vatPrefixDisplay: null
    },
    {
        locale: 'iw_IL',
        browserLocale: 'iw_IL',
        currency: 'ILS',
        countryCode: 'IL',
        vatRequired: false,
        vatPrefixDisplay: null
    },
    {
        locale: 'en_AU',
        browserLocale: 'en_AU',
        currency: 'AUD',
        countryCode: 'AU',
        vatRequired: false,
        vatPrefixDisplay: null
    },
    {
        locale: 'nl_NL',
        browserLocale: 'nl_NL',
        currency: 'EUR',
        countryCode: 'NL',
        vatRequired: true,
        vatPrefixDisplay: 'NL'
    },
    {
        locale: 'pt_BR',
        browserLocale: 'pt_BR',
        currency: 'BRL',
        countryCode: 'BR',
        vatRequired: false,
        vatPrefixDisplay: null
    },
    {
        locale: 'sv_SE',
        browserLocale: 'sv_SE',
        currency: 'SEK',
        countryCode: 'SE',
        vatRequired: true,
        vatPrefixDisplay: 'SE'
    },
    {
        locale: 'ko_KR',
        browserLocale: 'ko_KR',
        currency: 'KRW',
        countryCode: 'KR',
        vatRequired: true,
        vatPrefixDisplay: 'None'
    },
    {
        locale: 'hr_HR',
        browserLocale: 'hr_HR',
        currency: 'EUR',
        countryCode: 'HR',
        vatRequired: true,
        vatPrefixDisplay: 'HR'
    },
    {
        locale: 'de_DE',
        browserLocale: 'de_DE',
        currency: 'EUR',
        countryCode: 'DE',
        vatRequired: true,
        vatPrefixDisplay: 'DE'
    },
    {
        locale: 'pl_PL',
        browserLocale: 'pl_PL',
        currency: 'PLN',
        countryCode: 'PL',
        vatRequired: true,
        vatPrefixDisplay: 'None'
    },
    {
        locale: 'ja_JP',
        browserLocale: 'ja_JP',
        currency: 'JPY',
        countryCode: 'JP',
        vatRequired: false,
        vatPrefixDisplay: null
    },
    {
        locale: 'fr_CH',
        browserLocale: 'fr_CH',
        currency: 'CHF',
        countryCode: 'CH',
        vatRequired: true,
        vatPrefixDisplay: 'CHE-'
    },
    {
        locale: 'en_IE',
        browserLocale: 'en_IE',
        currency: 'EUR',
        countryCode: 'IE',
        vatRequired: true,
        vatPrefixDisplay: 'IE'
    },
    {
        locale: 'cs_CZ',
        browserLocale: 'cs_CZ',
        currency: 'CZK',
        countryCode: 'CZ',
        vatRequired: true,
        vatPrefixDisplay: 'CZ'
    },
    {
        locale: 'zh_TW',
        browserLocale: 'zh_TW',
        currency: 'TWD',
        countryCode: 'TW',
        vatRequired: false,
        vatPrefixDisplay: null
    },
    {
        locale: 'nl_BE',
        browserLocale: 'nl_BE',
        currency: 'EUR',
        countryCode: 'BE',
        vatRequired: true,
        vatPrefixDisplay: 'BE'
    },
    {
        locale: 'ro_RO',
        browserLocale: 'ro_RO',
        currency: 'EUR',
        countryCode: 'RO',
        vatRequired: true,
        vatPrefixDisplay: 'RO'
    },
    {
        locale: 'en_IN',
        browserLocale: 'en_IN',
        currency: 'INR',
        countryCode: 'IN',
        vatRequired: false,
        vatPrefixDisplay: null
    },
    {
        locale: 'fr_FR',
        browserLocale: 'fr_FR',
        currency: 'EUR',
        countryCode: 'FR',
        vatRequired: true,
        vatPrefixDisplay: 'FR'
    },
    {
        locale: 'es_MX',
        browserLocale: 'es_MX',
        currency: 'MXN',
        countryCode: 'MX',
        vatRequired: false,
        vatPrefixDisplay: null
    },
    {
        locale: 'de_AT',
        browserLocale: 'de_AT',
        currency: 'EUR',
        countryCode: 'AT',
        vatRequired: true,
        vatPrefixDisplay: 'AT'
    },
    {
        locale: 'en_ZA',
        browserLocale: 'en_ZA',
        currency: 'EUR',
        countryCode: 'ZA',
        vatRequired: false,
        vatPrefixDisplay: null
    },
    {
        locale: 'fi_FI',
        browserLocale: 'fi_FI',
        currency: 'EUR',
        countryCode: 'FI',
        vatRequired: true,
        vatPrefixDisplay: 'FI'
    },
    {
        locale: 'zh_CN',
        browserLocale: 'zh_CN',
        currency: 'CNY',
        countryCode: 'CN',
        vatRequired: false,
        vatPrefixDisplay: null
    },
    {
        locale: 'hu_HU',
        browserLocale: 'hu_HU',
        currency: 'EUR',
        countryCode: 'HU',
        vatRequired: true,
        vatPrefixDisplay: 'HU'
    },
    {
        locale: 'zh_HK',
        browserLocale: 'zh_HK',
        currency: 'HKD',
        countryCode: 'HK',
        vatRequired: false,
        vatPrefixDisplay: null
    },
    {
        locale: 'zh_SG',
        browserLocale: 'zh_SG',
        currency: 'SGD',
        countryCode: 'SG',
        vatRequired: false,
        vatPrefixDisplay: null
    },
    {
        locale: 'es_AR',
        browserLocale: 'es_AR',
        currency: 'USD',
        countryCode: 'AR',
        vatRequired: false,
        vatPrefixDisplay: null
    },
    {
        locale: 'en_US',
        browserLocale: 'en_US',
        currency: 'USD',
        countryCode: 'US',
        vatRequired: false,
        vatPrefixDisplay: null
    },
    {
        locale: 'es_ES',
        browserLocale: 'es_ES',
        currency: 'EUR',
        countryCode: 'ES',
        vatRequired: true,
        vatPrefixDisplay: 'ES'
    },
    {
        locale: 'it_IT',
        browserLocale: 'it_IT',
        currency: 'EUR',
        countryCode: 'IT',
        vatRequired: true,
        vatPrefixDisplay: 'IT'
    },
    {
        locale: 'no_NO',
        browserLocale: 'no_NO',
        currency: 'EUR',
        countryCode: 'NO',
        vatRequired: true,
        vatPrefixDisplay: 'NO'
    }
];
/**
 * Get the list of countries where eCommerce is supported.
 * This function will attempt to pull the list of supported countries from the API, and will fall back to the hard coded {@link SUPPORTED_COUNTRIES} if that fails.
 */
function getECommerceSupportedCountries() {
    return new Promise((resolve) => {
        callDEXApi('settings/countries', { method: 'GET' }).then(response => {
            if (response.ok) {
                response.json().then(data => {
                    const countryArray = data === null || data === void 0 ? void 0 : data.countryInfo;
                    resolve((countryArray === null || countryArray === void 0 ? void 0 : countryArray.length) === 0 ? SUPPORTED_COUNTRIES.map((item) => item) : countryArray);
                });
            }
            else {
                // The response wasn't okay, so we didn't get what we wanted.
                // Default to the hard coded list of supported countries
                resolve(SUPPORTED_COUNTRIES.map((item) => item));
            }
        }).catch(error => {
            console.error('Request to fetch supported eCommerce countries failed.', error);
            resolve(SUPPORTED_COUNTRIES.map((item) => item));
        });
    });
}

/**
 * Transformer class for transforming Digital Exchange (DEX) objects into generic objects.
 * This class extends the eCommerceTransformer and implements the transformShoppingCart
 * and transformProduct methods to convert DEX-specific objects into generic objects.
 *
 */
class DigitalExchangeTransformer extends eCommerceTransformer {
    transformShoppingCart(dexCart) {
        var _a, _b, _c, _d;
        if (!dexCart) {
            return undefined;
        }
        console.log(`[CART-DEBUG] DigitalExchangeTransformer`);
        // print stack trace without throwing error
        console.log(new Error('Stack trace'));
        const cart = {
            id: dexCart.id,
            items: (_a = dexCart.items) === null || _a === void 0 ? void 0 : _a.map((item) => ({
                product: this.transformProduct(item.product),
                quantity: item.quantity,
                price: item.price,
                total: item.total,
                discount: item.discount,
                id: item.id,
                isBundleProduct: item.isBundleProduct,
                isDynamicKitProduct: item.isDynamicKitProduct,
                isCompositeProduct: item.isCompositeProduct,
                discountPercent: item.discountPercent,
            })),
            total: ((_b = dexCart.total) !== null && _b !== void 0 ? _b : 0).toString(),
            subTotal: ((_c = dexCart.subTotal) !== null && _c !== void 0 ? _c : 0).toString(),
            currency: dexCart.currency,
            promotionCodes: dexCart.promotionCodes || [],
            billTo: (_d = dexCart.billTo) !== null && _d !== void 0 ? _d : null,
            // Add more properties as needed, but only include the ones that are necessary.
        };
        return cart;
    }
    /**
     * Transform composite products from cart data to generic composite products.
     * This method is used when working with products that are already in the cart
     * and have their pricing and quantities already defined.
     *
     * @param {Object} params - The parameters for transforming cart-based composite products.
     * @param {CompositeProduct[] | undefined} params.items - Array of composite products from the cart to transform.
     * @param {boolean} params.compositeProductsLoading - Loading state for composite products.
     * @returns {CompositeProducts | undefined} A transformed composite products object or undefined if no items exist.
     */
    transformCartCompositeProducts({ items, compositeProductsLoading }) {
        // Check if items is undefined or empty
        if (!items || items.length === 0) {
            return undefined;
        }
        const compositeProductsData = {
            // Generic array of composite products including their pricing options
            items: items,
            // Booleans to indicate state for UI handling
            compositeProductsLoading,
            // Add more properties as needed, but only include the ones that are necessary.
        };
        return compositeProductsData;
    }
    /**
     * Transform a DEX product to a generic product.
     *
     * @param object - The DEX product to transform.
     * @returns The transformed generic product with minimal properties needed for the front-end.
     *
     */
    transformProduct(object) {
        var _a;
        if (!object) {
            return undefined;
        }
        const minLicenses = object.minimumQuantity || constants.DEFAULT_MIN_LICENSES;
        const maxLicenses = object.maximumQuantity && !isNaN(Number(object.maximumQuantity)) && Number(object.maximumQuantity) >= 0 ? object.maximumQuantity : constants.DEFAULT_MAX_LICENSES;
        const product = {
            name: object.name,
            minLicenses,
            maxLicenses,
            pricingOptions: (_a = object.pricingOptions) === null || _a === void 0 ? void 0 : _a.map((option) => this.transformPricingOption(option)).filter((option) => option !== undefined),
            compositeProducts: object.compositeProducts,
            bundleProducts: object.bundleProducts,
            productSku: object.productSku,
            // Required properties added for compatibility
            paymentOptions: [],
            preRequisites: {},
            soldThrough: '',
            portfolio: '',
            termsAndConditions: [],
            productTypology: { class: '', classification: '', type: '' },
            sellerInfo: null,
            licenseType: object.licenseType || '', // e.g., "One Time"
            userType: object.userType || '', // e.g., "Floating" etc.
            // Add more properties as needed, but only include the ones that are necessary.
        };
        return product;
    }
    /**
     * Transform a DEX pricing option to a generic pricing option.
     *
     * @param pricingOption - The DEX pricing option to transform.
     * @returns The transformed generic pricing option with minimal properties needed for the front-end.
     */
    transformPricingOption(pricingOption) {
        if (!pricingOption) {
            return undefined;
        }
        // Map the digital exchange product to the generic pricing option
        const returnOption = {
            // example: "1742631"
            id: pricingOption.id,
            // example: "43519.76"
            price: pricingOption.price,
            // example: "CAD"
            currency: pricingOption.currency,
            // example: "3 Year Subscription"
            displayTerm: pricingOption.displayTerm,
            // Add more properties as needed, but only include the ones that are necessary.
            autoRenew: pricingOption.autoRenew,
            term: pricingOption.term,
        };
        return returnOption;
    }
    /**
     * Transform a DEX paymentOption to a generic payment option.
     *
     * @param paymentOption - The DEX payment option to transform.
     * @returns The transformed generic payment option with minimal properties needed for the front-end.
     */
    transformPaymentOption(paymentOption) {
        if (!paymentOption) {
            return undefined;
        }
        return paymentOption;
    }
}

/**
 * Base hook to manage shared shopping cart logic.
 *
 * Encapsulates the common logic for managing shopping cart state, subscriptions,
 * and utility functions.
 *
 * @returns {Object} The shared shopping cart state and actions.
 */
function useBaseShoppingCartLogic({ fetchShoppingCart = false }) {
    const { disw = {} } = (typeof window !== 'undefined' && window) || {};
    const [cartData, setCartData] = useState();
    const [cartPreRequisites, setCartPreRequisites] = useState({});
    const [cartErrors, setCartErrors] = useState([]);
    const [countItems, setCountItems] = useState(0);
    const [isMiniCartVisible, setMiniCartVisible] = useState(false);
    const [isFullCartVisible, setFullCartVisible] = useState(false);
    const [transformer] = useState(() => new DigitalExchangeTransformer());
    /**
     * Toggles the visibility of the full cart.
     *
     * @param {boolean} state - The desired visibility state of the full cart.
     * @returns {void}
     */
    const toggleFullCart$1 = useCallback((state) => {
        toggleFullCart({ state });
    }, []);
    /**
     * Toggles the visibility of the mini cart.
     *
     * @param {boolean} state - The desired visibility state of the mini cart.
     * @returns {void}
     */
    const toggleMiniCart$1 = useCallback((state) => {
        toggleMiniCart({ state });
    }, []);
    /**
     * Applies a promotion code to the cart.
     *
     * @param {Object} params - The parameters for applying the promotion code.
     * @param {string} params.discountCode - The discount code to apply.
     * @param {Function} params.onSuccess - Callback function to execute on successful application.
     * @param {Function} params.onError - Callback function to execute on error during the application.
     * @returns {Promise<void>} A promise that resolves when the promotion is applied or rejects on error.
     */
    const applyPromotion = useCallback((_a) => __awaiter(this, [_a], void 0, function* ({ discountCode, onSuccess, onError }) {
    }), []);
    /**
     * Removes a promotion code from the cart.
     *
     * @param {Object} params - The parameters for removing the promotion code.
     * @param {string} params.discountCode - The discount code to remove.
     * @param {Function} params.onSuccess - Callback function to execute on successful removal.
     * @param {Function} params.onError - Callback function to execute on error during the removal.
     * @returns {Promise<void>} A promise that resolves when the promotion is removed or rejects on error.
     */
    const removePromotion = useCallback((_a) => __awaiter(this, [_a], void 0, function* ({ discountCode, onSuccess, onError }) {
    }), []);
    /**
     * Indicates that the checkout process has started.
     *
     * @returns {void}
     */
    const checkoutStarted$1 = useCallback(() => {
        return checkoutStarted({ cartData: cartData });
    }, [cartData]);
    /**
     * Checks if the checkout process is enabled.
     *
     * @returns {boolean} - Returns `true` if checkout is enabled, otherwise `false`.
     */
    const isCheckoutEnabled = useCallback(() => {
        return disw.cart.isCheckoutEnabled();
    }, []);
    /**
     * Updates items in the shopping cart by adding or subtracting products.
     *
     * @param {Object} params - An object containing the arguments required for updating cart items.
     * @param {AddOrSubtractProductsArgs} params.args - The arguments required for adding or subtracting products.
     * @param {string | undefined} params.bundleSku - The SKU of the product bundle, if this item is a bundle.
     * @param {Function} params.onSubscriptionTermsConflictError - Callback function to handle subscription terms conflict errors.
     * @param {Function} params.onPress - Callback function to handle the updated cart data or errors.
     * @returns {Promise<void>}
     */
    const updateItemsInCart = useCallback((_a) => __awaiter(this, [_a], void 0, function* ({ args, bundleSku, onSubscriptionTermsConflictError, onPress, }) {
        addOrSubtractProductsFromCart({ parameter: args, bundleSku })
            .then((cartData) => {
            if (cartData) {
                if (cartData === constants.SAME_PRODUCT_DIFFERENT_TERM) {
                    onSubscriptionTermsConflictError();
                }
                else {
                    onPress(cartData);
                }
            }
            else {
                onPress(null); // if there was a cart error, this will propagate the error
            }
        })
            .catch(() => {
            onPress(null); // if there was a cart error, this will propagate the error
        });
    }), []);
    /**
     * @deprecated Use updateItemsInCart instead. This function will be removed in a future version.
     * Adds or subtracts products from the shopping cart.
     *
     * @param {Object} params - An object containing the arguments required for adding or subtracting products.
     * @param {AddOrSubtractProductsArgs} params.args - The arguments required for adding or subtracting products.
     * @param {string | undefined} params.bundleSku - The SKU of the product bundle, if this item is a bundle.
     * @param {Function} params.onSubscriptionTermsConflictError - Callback function to handle subscription terms conflict errors.
     * @param {Function} params.onPress - Callback function to handle the updated cart data or errors.
     * @returns {Promise<void>}
     */
    const addOrSubtractProductsFromCart$1 = updateItemsInCart;
    /**
     * Removes an item from the shopping cart.
     *
     * This function removes a specific item from the cart based on its ID, promotion code, and index.
     * It handles success and error callbacks to manage the UI state.
     *
     * @param {Object} params - The parameters for removing an item from the cart.
     * @param {string} params.conflictingCartItem - The cart item to be removed.
     * @param {string} [params.promoCode] - The promotion code applied to the cart item (optional).
     * @param {number} [params.itemIndex] - The index of the cart item in the cart (optional).
     * @param {Function} params.onPress - Callback function to execute on successful removal.
     * @param {Function} params.onError - Callback function to execute on error during removal.
     * @returns {Promise<void>} A promise that resolves when the item is successfully removed or rejects on error.
     */
    const removeFromCart$1 = useCallback((_a) => __awaiter(this, [_a], void 0, function* ({ conflictingCartItem, promoCode, itemIndex, onPress, onError }) {
        try {
            yield removeFromCart({ cartItemId: conflictingCartItem.id, removedProduct: conflictingCartItem, promotionCode: promoCode !== null && promoCode !== void 0 ? promoCode : null, itemIndex });
            onPress && onPress(); // Call the success callback
        }
        catch (error) {
            console.error('Error removing item from cart:', error);
            onError && onError(); // Call the error callback
        }
    }), []);
    /**
     * Removes a bundle of products from the shopping cart.
     *
     * This function removes all products in a bundle from the cart. It handles success and error callbacks
     * to manage the UI state and propagate the result.
     *
     * @param {Object} params - The parameters for removing the bundle from the cart.
     * @param {Array<any>} bundleItems - The list of items in the bundle to be removed.
     * @param {string} bundleSku - The SKU of the bundle being removed.
     * @param {Function} onSuccess - Callback function to execute on successful removal.
     * @param {Function} onError - Callback function to execute on error during removal.
     * @returns {Promise<void>} A promise that resolves when the bundle is successfully removed or rejects on error.
     */
    const removeBundleFromCart = useCallback((_a) => __awaiter(this, [_a], void 0, function* ({ bundleItems, bundleSku, onSuccess, onError }) {
        // try {
        //   await cart.removeProductFromCart({ parameter: bundleItems, bundleSku });
        //   onSuccess(); // Call the success callback
        // } catch (error) {
        //   console.error('Error removing bundle from cart:', error);
        //   onError(); // Call the error callback
        // }
    }), []);
    /**
     * Retrieves and processes pricing options for a product.
     *
     * This function validates the provided pricing options, calls `disw.cart.getPricingOptions` to process them,
     * and selects a default option. It uses success and error callbacks to handle the result.
     *
     * @param {Array<any>} pricingOptions - The list of pricing options for the product.
     * @param {Function} onSuccess - Callback function to execute with the selected pricing option.
     * @param {Function} [onError] - Callback function to execute if no pricing options are available (optional).
     * @returns {Promise<void>} A promise that resolves when the pricing options are successfully processed or rejects on error.
     */
    const getPricingOptions = useCallback((pricingOptions, onSuccess, onError) => __awaiter(this, void 0, void 0, function* () {
        if (!pricingOptions || pricingOptions.length === 0) {
            console.error('No pricing options available:', pricingOptions);
            if (onError)
                onError();
            return;
        }
        // try {
        //   const processedOption = await cart.getPricingOptions({ pricingOptions });
        //   onSuccess(processedOption);
        // } catch (error) {
        //   console.error('Error processing pricing options:', error);
        //   if (onError) onError();
        // }
    }), []);
    /**
     * Retrieves and processes pricing options for a bundle of products.
     *
     * @param {Array<any>} bundleProducts - The list of products in the bundle.
     * @param {Function} onSuccess - Callback function to execute with the aggregated pricing options.
     * @param {Function} [onError] - Callback function to execute if an error occurs (optional).
     * @returns {Promise<void>} A promise that resolves when the pricing options are successfully processed or rejects on error.
     */
    const getPricingOptionsFromBundleProducts = useCallback((bundleProducts, onSuccess, onError) => __awaiter(this, void 0, void 0, function* () {
        // if (!bundleProducts || bundleProducts.length === 0) {
        //   console.error('No bundle products provided.');
        //   if (onError) onError();
        //   return;
        // }
        // try {
        //   const products = bundleProducts.map((product) => product?.product);
        //   const pricingOptionsDictionary = await cart.getPricingOptionsFromBundleProduct({ products });
        //   // Transform the dictionary into an array of aggregated pricing options
        //   const aggregatedPricingOptions = transformPricingOptions(pricingOptionsDictionary);
        //   onSuccess(aggregatedPricingOptions);
        // } catch (error) {
        //   console.error('Error retrieving pricing options for bundle products:', error);
        //   if (onError) onError();
        // }
    }), []);
    const DEFAULT_MIN_LICENSES = '1';
    const DEFAULT_MAX_LICENSES = '10';
    /**
     * Retrieves the minimum and maximum licenses for a product.
     *
     * This function calculates the minimum and maximum number of licenses allowed for a product
     * based on its properties. Defaults are used if the product does not specify these values.
     *
     * @param {Object} product - The product object containing license information.
     * @returns {Object} An object containing `minLicenses` and `maxLicenses`.
     * @returns {string | number} return.minLicenses - The minimum number of licenses allowed.
     * @returns {string | number} return.maxLicenses - The maximum number of licenses allowed.
     */
    const getMinAndMaxLicenses = useCallback((product) => {
        var _a, _b, _c, _d;
        // Prefer minimumQuantity/maximumQuantity, fallback to minLicenses/maxLicenses, then defaults
        const minRaw = (_b = (_a = product === null || product === void 0 ? void 0 : product.minimumQuantity) !== null && _a !== void 0 ? _a : product === null || product === void 0 ? void 0 : product.minLicenses) !== null && _b !== void 0 ? _b : DEFAULT_MIN_LICENSES;
        const maxRaw = (_d = (_c = product === null || product === void 0 ? void 0 : product.maximumQuantity) !== null && _c !== void 0 ? _c : product === null || product === void 0 ? void 0 : product.maxLicenses) !== null && _d !== void 0 ? _d : DEFAULT_MAX_LICENSES;
        // Always coerce to string for parseInt, fallback to '0' if still undefined/null
        const min = Number.parseInt(String(minRaw !== null && minRaw !== void 0 ? minRaw : DEFAULT_MIN_LICENSES), 10);
        const max = Number.parseInt(String(maxRaw !== null && maxRaw !== void 0 ? maxRaw : DEFAULT_MAX_LICENSES), 10);
        const minLicenses = Number.isFinite(min) && min > 0 ? String(min) : DEFAULT_MIN_LICENSES;
        const maxLicenses = Number.isFinite(max) && max > 0 ? String(max) : DEFAULT_MAX_LICENSES;
        return { minLicenses, maxLicenses };
    }, []);
    /**
     * Function to any matching items from the cart and add the pending item to the cart
     *
     * @param params - The updated shopping cart data.
     * @param {string | false} [errorContext=false] - The error context, if any.
     * @returns {void}
     */
    const updateTerms = useCallback((_a) => __awaiter(this, [_a], void 0, function* ({ context, productSku, newPricingId, quantityToAdd, setLoading, }) {
        // try {
        //   const currentCartData: DexShoppingCart = await cart.getCart();
        //   //const conflictingCartItem: ICartItem | undefined = currentCartData.items.find(({ product }) => product.productSku === productsku);
        //   const conflictingCartItem: DexShoppingCartItem | undefined = cart.getCartItemWithMatchingSku({ cartItems: currentCartData.items, productSku: productSku });
        //   // There should always be a conflicting cart item if this is opening, however, typescript knows that .find() can potentially return 'undefined'
        //   if (conflictingCartItem) {
        //     // Find the current index of the conflicting cart item
        //     const itemIndex: number = currentCartData.items.indexOf(conflictingCartItem);
        //     // Get any existing promo codes
        //     const { promotionCodes } = currentCartData;
        //     const promoCode: string | undefined = promotionCodes && promotionCodes.length ? currentCartData.promotionCodes[0] : undefined;
        //     // Remove conflicting cart item
        //     setLoading(true);
        //     await removeFromCart({ conflictingCartItem: conflictingCartItem, promoCode: promoCode, itemIndex }); // Why do we pass the whole item and its attribute as separate parameters?
        //     // Add new pricing terms
        //     // I am passing the conflicting cart item here because the cart item is only used for its product ID
        //     // The new pricing ID is used to actually perform the action of adding an item to the cart
        //     const addOrSubtractFromCartResponse: DexShoppingCart = await cart.addOrSubtractProductsFromCart({
        //       parameter: {
        //         productPricingId: newPricingId,
        //         quantity: quantityToAdd,
        //         cartItem: conflictingCartItem,
        //         context,
        //         promotionCode: null,
        //         itemIndex: null,
        //       },
        //     });
        //     // Return the updated item from the returned cart data
        //     const updatedCartItem: DexShoppingCartItem | undefined = cart.getCartItemWithMatchingSku({ cartItems: addOrSubtractFromCartResponse.items, productSku });
        //     setLoading(false);
        //     // Close the modal and open the added to cart modal to show the updated cart data and allow the user a chance to go to checkout
        //     closeSubscriptionTermsConflictModal({ flag: true, context, updatedCartItem });
        //   }
        // } catch (err) {
        //   console.error(err);
        // }
    }), []);
    /**
     * Subscribes to cart changes, mini cart visibility, and full cart visibility.
     * Updates the cart data, mini cart visibility, and full cart visibility accordingly.
     *
     * @returns {void}
     */
    useEffect(() => {
        const unsubscribeCartChanges = disw.cart.subscribeToCart((shoppingCart, errorContext) => {
            // If there was no error thrown, then set the cart data.
            if (errorContext) {
                setCartErrors((currentErrors) => [...currentErrors, errorContext]);
                // If an error did occur we can handle here when needed
                if (errorContext === disw.cart.CART_ERROR_INITIALIZING_CART) {
                    // In this scenario a new cart could not be created (perhaps DEX does not support the country). For this
                    // scenario, mimic an empty cart
                    setCartData(MIMICKED_EMPTY_CART);
                }
                setCountItems(0);
            }
            else {
                // Wipe the list of errors if the cart changed but it wasn't due to an error
                setCartErrors([]);
                setCountItems(shoppingCart.items ? shoppingCart.items.filter((item) => !item.isCompositeProduct).length : 0);
                setCartData(shoppingCart);
            }
        });
        const unsubscribeMiniCart = disw.cart.subscribeToMiniCart(setMiniCartVisible);
        const unsubscribeFullCart = disw.cart.subscribeToFullCart(setFullCartVisible);
        // Subscribe to country updates to update the country/cart if a country was selected
        // const unsubscribeCountry = fetchShoppingCart && disw.country.subscribeToCountry(cart.onCountryChanged);
        return () => {
            unsubscribeCartChanges && unsubscribeCartChanges();
            unsubscribeMiniCart && unsubscribeMiniCart();
            unsubscribeFullCart && unsubscribeFullCart();
            // unsubscribeCountry && unsubscribeCountry();
        };
    }, []);
    return {
        cartData,
        cartPreRequisites,
        cartErrors,
        countItems,
        isMiniCartVisible,
        isFullCartVisible,
        transformer,
        setCartData,
        setCartErrors,
        setCountItems,
        toggleFullCart: toggleFullCart$1,
        toggleMiniCart: toggleMiniCart$1,
        checkoutStarted: checkoutStarted$1,
        removePromotion,
        applyPromotion,
        isCheckoutEnabled,
        getPricingOptions,
        getPricingOptionsFromBundleProducts,
        getMinAndMaxLicenses,
        updateItemsInCart,
        addOrSubtractProductsFromCart: addOrSubtractProductsFromCart$1,
        removeFromCart: removeFromCart$1,
        removeBundleFromCart,
        updateTerms,
    };
}
/**
 * Custom hook to subscribe to shopping cart logic. Can NOT share state without propagating functions/state through components.
 *
 * @returns {ShoppingCartHook} A hook for the shopping cart state and actions.
 */
const subscribeToShoppingCartLogic = ({ fetchShoppingCart = false }) => {
    const baseLogic = useBaseShoppingCartLogic({ fetchShoppingCart });
    return Object.assign(Object.assign({}, baseLogic), { cartData: baseLogic.transformer.transformShoppingCart(baseLogic.cartData) });
};
// Define the ShoppingCartContext with a default value of null
const ShoppingCartContext = createContext(null);
/**
 * Custom hook to access the shopping cart context.
 *
 * Provides access to cart data, errors, item count, and functions to toggle the visibility
 * of the mini cart and full cart. Also includes utility functions for checkout and country updates.
 *
 * @returns {CartContextType | ShoppingCartHook} The shopping cart context or the subscribeToShoppingCartLogic hook.
 */
const useShoppingCart = ({ fetchShoppingCart = false } = {}) => {
    // In the case we use context, the fetchShoppingCart will have been passed into the providers initialization.
    const context = useContext(ShoppingCartContext);
    // If the context is not available, call and return the subscribeToShoppingCartLogic hook directly
    if (!context) {
        return subscribeToShoppingCartLogic({ fetchShoppingCart });
    }
    return context;
};
/**
 * Use the original DEX shopping cart without any transforming.
 * Currently only used by the checkout.
 *
 * @returns The original shopping cart hook with no cart data transformation
 */
function useCheckoutCart() {
    const [cartDataLoadError, setCartDataLoadError] = useState(false);
    const { cartData, cartErrors, setCartErrors } = useBaseShoppingCartLogic({ fetchShoppingCart: false });
    const [finalCartData, setFinalCartData] = useState();
    // Calculate the cartData and cartErrors
    useEffect(() => {
        if (cartErrors && cartErrors.includes(constants.CART_CLEARED_AFTER_CHECKOUT)) {
            console.log('cart cleared after checkout');
            setCartErrors([]);
            // But we don't actually set the cart data to undefined because then the checkout would crap itself.
        }
        else {
            if (cartErrors && cartErrors.includes(constants.CART_ERROR_FETCHING_CART_DATA)) {
                setCartDataLoadError(true);
            }
            if (cartErrors && cartErrors.includes(constants.CART_ERROR_INITIALIZING_CART)) {
                // In this scenario a new cart could not be created (perhaps DEX does not support the country). For this
                // scenario, mimic an empty cart
                setFinalCartData(undefined);
            }
            setFinalCartData(cartData);
        }
    }, [cartErrors, cartData]);
    return {
        cartErrors,
        cartDataLoadError,
        // cartData: finalCartData,
    };
}

/**
 * These are the payment options that DEX sends us for each product in the cart data.
 */
var DexPaymentOption;
(function (DexPaymentOption) {
    DexPaymentOption["CREDIT_CARD"] = "Credit Card";
    DexPaymentOption["PAY_ON_ACCOUNT"] = "Payment On Account";
    DexPaymentOption["REQUEST_QUOTE"] = "Request Quote";
    DexPaymentOption["PAYPAL"] = "Paypal";
})(DexPaymentOption || (DexPaymentOption = {}));

/**
 * Represents the type of product being handled in ecommerce transactions.
 * This enum is used to categorize products into different types for better handling and processing.
 */
var ProductType;
(function (ProductType) {
    ProductType["PRODUCT"] = "product";
    ProductType["BUNDLE"] = "bundle";
    ProductType["DYNAMIC_KIT"] = "kit";
})(ProductType || (ProductType = {}));
/**
 * These are the available payment methods.
 *
 * The value is how they will be displayed under the payment section to the user.
 */
var AdyenCreditCardBrand;
(function (AdyenCreditCardBrand) {
    AdyenCreditCardBrand["MASTERCARD"] = "mc";
    AdyenCreditCardBrand["VISA"] = "visa";
    AdyenCreditCardBrand["DINERS_CLUB"] = "diners";
    AdyenCreditCardBrand["AMEX"] = "amex";
    AdyenCreditCardBrand["DISCOVER"] = "discover";
})(AdyenCreditCardBrand || (AdyenCreditCardBrand = {}));

/**
 * useProduct is the main hook for interacting with products. It is capable of pulling the product information, composite products (if available) for
 * a bundle or dynamic kit and the pricing options for the product. It allows consumers to make the pricing option selections, and number of seat selections,
 * and does all of the calculations around the number of seats to be added (determining the delta between the cart and the number of seats selected). The
 * hook provides status indicators for loading of products and composites (independently to allow consumers to decide how to handle the UI).
 *
 */
const useProduct = ({ product: taxonomyProduct, productType = ProductType.PRODUCT, noPriceOrErrorCallback, }) => {
    const { disw = {} } = (typeof window !== 'undefined' && window) || {};
    // Rename productInfo to product
    const [product, setProduct] = useState();
    // Track the available pricing options for the product (which can be an array or a single object, which we should refactor soon)
    const [pricingOptions, setPricingOptions] = useState();
    // Track the selected pricing option based on the customers selection (for the top-level product, composite products defer to the parents pricing option)
    const [selectedPricingOption, setSelectedPricingOption] = useState();
    // Track if a product is actively being added to the cart for a loading indictor
    const [productLoading, setProductLoading] = useState(true);
    // Track the quantity to add (which is the number of products in the cart minus the number being added).
    const [quantityToAdd, setQuantityToAdd] = useState(0);
    // Track the number of seats indicated by the user in the drop-down (used to calculate the quantity to add)
    const [numberSeats, setNumberSeats] = useState(1);
    // Array of the composite product information pulled in parallel from DEX by API
    const [compositeProductsPricingOptions, setCompositeProductsPricingOptions] = useState();
    // Track the product information returned by the api for composites
    const [compositeProductsInfo, setCompositeProductsInfo] = useState();
    // Track if a bundle is still processing the product information for a loading indicator
    const [compositeProductsLoading, setCompositeProductsLoading] = useState(true);
    // Track the number of seats for each composite product (this is a map of the product id to the number of seats selected in the drop-down)
    const [compositeNumberSeatsMap, setCompositeNumberSeatsMap] = useState({});
    // We build a dictionary of composite pricing options keyed by the display term (e.g. monthly, yearly) with each composites options for the respective term
    // Note this is NOT to be returned to the consumer.
    const [bundlePricingDictionary, setBundlePricingDictionary] = useState(); // Update type later to be specific
    // Track the DEX shopping cart (needed to figure out what products are in the cart to calculate add/remove deltas)
    const [shoppingCart, setShoppingCart] = useState();
    // Track if there was an error in fetching the product information to pass the state for UI to handle
    const [errorInFetchingProduct, setErrorInFetchingProduct] = useState(false);
    // Track if there's an error fetching the composite products (we need to fetch all composites)
    const [errorInFetchingComposites, setErrorInFetchingComposites] = useState(false);
    // Track the payment options for the product
    const [paymentOptions, setPaymentOptions] = useState([]);
    // Check which pricing terms are available for a product
    const doesProductHavePricingTerm = (displayTerm) => {
        return Array.isArray(pricingOptions) && (pricingOptions === null || pricingOptions === void 0 ? void 0 : pricingOptions.some((pricingOption) => pricingOption.displayTerm === displayTerm && pricingOption.autoRenew === true));
    };
    // this function handles the pricing selection in the case of multiple pricing options (monthly/yearly)
    const selectPricingOption = (displayTerm, inputPricingOptions = undefined) => {
        const options = inputPricingOptions ? inputPricingOptions : pricingOptions;
        if (options && Array.isArray(options))
            // Update selected pricing option
            setSelectedPricingOption(options === null || options === void 0 ? void 0 : options.find((obj) => {
                return obj.displayTerm === displayTerm;
            }));
    };
    /**
     * Fetch the product information via SKU. Note that country is not passed explicitly, but is detected & passed to the API.
     * @param {*} productSKU - representing the product being queried.
     */
    const fetchProductInfo = (productSKU) => {
        // Reset the state variables if we're refreshing product information
        setProductLoading(true);
        setErrorInFetchingProduct(false);
        setCompositeProductsLoading(true);
        // getProductInfo({ productSku: productSKU })
        //   .then((dexProductResponse: DexProduct) => {
        //     setProduct(dexProductResponse);
        //     // If the product came back with no pricing options, immediately updated the pricing options state
        //     if (dexProductResponse?.pricingOptions.length === 0) {
        //       setSelectedPricingOption(undefined);
        //       setPricingOptions(undefined);
        //     }
        //     // If the product has payment options, set them
        //     if (dexProductResponse?.paymentOptions) {
        //       setPaymentOptions(dexProductResponse.paymentOptions);
        //     }
        //     // Get all possible pricing options where autoRenew == true, by not supplying function with a term we get all types with this call
        //     const parsedPricingOptions = getPricingOptions({ pricingOptions: dexProductResponse.pricingOptions });
        //     // If it is an array, we know that there is a monthly/yearly option. Need to set value to be based on users choice/default
        //     if (parsedPricingOptions) {
        //       // Assign as an array for code functionality reasons
        //       setPricingOptions(Array.isArray(parsedPricingOptions) ? parsedPricingOptions : [parsedPricingOptions]);
        //       // And set the selected one to the annual (or the only one if not an array)
        //       if (Array.isArray(parsedPricingOptions)) {
        //         // Set the selected pricing option to annual unless use annual pricing indicates otherwise.
        //         selectPricingOption(constants.ANNUAL_SUBSCRIPTION_DISPLAY_TERM, parsedPricingOptions);
        //       } else {
        //         // Only one option, so we set to that
        //         setSelectedPricingOption(parsedPricingOptions);
        //       }
        //     } else {
        //       // If no pricing options are found, treat as such - clear values so UI can handle it.
        //       if (noPriceOrErrorCallback) {
        //         noPriceOrErrorCallback();
        //       }
        //       setProduct(undefined);
        //       setSelectedPricingOption(undefined);
        //       setPricingOptions(undefined);
        //     }
        //     // Check if the product is a bundle
        //     if (isBundle(dexProductResponse)) {
        //       // Set the bundle products loading state to true while we fetch the composite product information
        //       setCompositeProductsLoading(true);
        //       // Fetch the product information for each composite product
        //       return fetchCompositeProductInfo({ productInfo: dexProductResponse, productType })
        //         .then(async (compositeProducts) => {
        //           // Reset the error state to be safe (incase there was an error and user changed country which could succeed)
        //           setErrorInFetchingComposites(false);
        //           // Set the array of composite product information
        //           setCompositeProductsInfo(compositeProducts as DexCompositeProduct[]);
        //           // Get the pricing option dictionary for the composite products
        //           const bundlePricingOptionDictionary = getPricingOptionsFromBundleProduct({ products: compositeProducts });
        //           // Set the bundle pricing dictionary to easily sort through the pricing options grouped by term
        //           setBundlePricingDictionary(bundlePricingOptionDictionary);
        //         })
        //         .catch((error) => {
        //           console.error(`Something went wrong fetching composites: ${error}`);
        //           setErrorInFetchingComposites(true);
        //           // If there's an error getting composites, reset state.
        //           resetProductState();
        //         })
        //         .finally(() => {
        //           // Set the loading state to false once the composite product information is fetched
        //           setCompositeProductsLoading(false);
        //           // Also set the product loading to false here to ensure all loading is complete
        //           setProductLoading(false);
        //         });
        //     }
        //     // If not a bundle, we can set product loading to false here
        //     setProductLoading(false);
        //   })
        //   .catch((error: any) => {
        //     console.error(`Something went wrong fetching full list of products: `, error);
        //     setErrorInFetchingProduct(true);
        //     // Reset the product state to handle the error gracefully in the UI.
        //     resetProductState();
        //     // If there was an error, we need to set the product info to undefined so that the UI can handle it.
        //     if (noPriceOrErrorCallback) {
        //       noPriceOrErrorCallback();
        //     }
        //     // Set loading to false in case of error
        //     setProductLoading(false);
        //   });
    };
    // Function to update the numberSeats for a specific composite product
    const updateCompositeNumberSeats = (productId, seats) => {
        setCompositeNumberSeatsMap((prev) => (Object.assign(Object.assign({}, prev), { [productId]: seats })));
    };
    /**
     * Sum the composite products prices to get the total price of the bundle.
     * In general, this function sums the prices of all composite products.
     * For dynamic kits, it sums the prices of all composite products that have a number seats value.
     *
     */
    const sumCompositeProductsPrices = (compositeProducts) => {
        if (compositeProducts && compositeProducts.length > 0) {
            if (productType === ProductType.DYNAMIC_KIT) {
                // For kits, we need to sum the prices of all composite products that have a number seats value (numberSeats)
                // Each count of numberSeats is multiplied by the price of the product
                const total = compositeProducts
                    .map((item) => {
                    var _a, _b;
                    // Skip items without proper pricing options
                    if (!item.pricingOption)
                        return 0;
                    // Ensure currency is present to avoid NumberFormat errors
                    if (!item.pricingOption.currency) {
                        item.pricingOption.currency = 'USD';
                    }
                    const price = parseFloat((_b = (_a = item.pricingOption) === null || _a === void 0 ? void 0 : _a.price) !== null && _b !== void 0 ? _b : '0'); // Convert the price string to a float
                    if (isNaN(price))
                        return 0; // Ensure we don't process NaN values
                    let seats = item.numberSeats || 0; // Get number of seats, default to 0 if not available
                    if (item.required && item.numberSeats === 0) {
                        // console.warn(`[eCommerce] The product: ${item.productName} is required but numberSeats is 0! Setting number seats to 1.`);
                        seats = 1; // If the product is required and numberSeats is 0, set seats to 1
                    }
                    const maximumQuantity = parseInt(item.maximumQuantity); // Parse maximum quantity to an integer
                    // Don't mess with the maximum quantity if it is set to -1 (unlimited)
                    if (maximumQuantity !== -1 && item.numberSeats > maximumQuantity) {
                        // console.warn(`[eCommerce] The numberSeats (${item.numberSeats}) exceeds the maximum quantity (${maximumQuantity}) for product: ${item.productName}`);
                        seats = maximumQuantity; // If numberSeats exceeds maximum, set seats to maximum
                    }
                    return price * seats; // Multiply price by number of seats
                })
                    .reduce((sum, itemTotal) => sum + itemTotal, 0); // Sum up all the totals
                return total;
            }
            else {
                // For bundles, we need to sum the prices of all composite products since they are all required
                const total = compositeProducts
                    .map((item) => { var _a, _b; return parseFloat((_b = (_a = item.pricingOption) === null || _a === void 0 ? void 0 : _a.price) !== null && _b !== void 0 ? _b : '0'); }) // Convert the price string to a float
                    .reduce((sum, price) => sum + price, 0);
                return total;
            }
        }
        return null;
    };
    // Function to determine the parameters for adding to cart based on the product type
    const determineAddToCartParameters = ({ cartItem, product, compositeProducts }) => {
        var _a, _b;
        if (productType === ProductType.BUNDLE) {
            return (_a = compositeProducts === null || compositeProducts === void 0 ? void 0 : compositeProducts.items) === null || _a === void 0 ? void 0 : _a.map((composite) => {
                var _a;
                return ({
                    productPricingId: (_a = composite === null || composite === void 0 ? void 0 : composite.pricingOption) === null || _a === void 0 ? void 0 : _a.id,
                    quantity: product === null || product === void 0 ? void 0 : product.quantityToAdd,
                    cartItem,
                });
            });
        }
        if (productType === ProductType.DYNAMIC_KIT) {
            return (_b = compositeProducts === null || compositeProducts === void 0 ? void 0 : compositeProducts.items) === null || _b === void 0 ? void 0 : _b.map((composite) => {
                var _a;
                return ({
                    productPricingId: (_a = composite === null || composite === void 0 ? void 0 : composite.pricingOption) === null || _a === void 0 ? void 0 : _a.id,
                    quantity: composite === null || composite === void 0 ? void 0 : composite.quantityToAdd,
                    cartItem,
                });
            });
        }
        // By default, return the product pricing ID and quantity to add for the regular product type
        return {
            productPricingId: (product === null || product === void 0 ? void 0 : product.selectedPricingOption) ? product === null || product === void 0 ? void 0 : product.selectedPricingOption.id : '',
            quantity: product === null || product === void 0 ? void 0 : product.quantityToAdd,
            cartItem,
        };
    };
    /**
     * If the selected pricing option, number of seats, or the shopping cart changes we need to recalculate the delta of products to add.
     * Quantity to add represents the quantity for the top-level product, and is the difference between the number of seats selected and
     * the number of seats already in the cart for the selected pricing option.
     *
     */
    useEffect(() => {
    }, [shoppingCart, numberSeats, selectedPricingOption, taxonomyProduct, taxonomyProduct === null || taxonomyProduct === void 0 ? void 0 : taxonomyProduct.sku]);
    /**
     * When the selected pricing option / cart are updated (including on load) we need to update the number of seats
     * to match the number of seats in the cart for the selected pricing option. The number of seats is what is displayed in the
     * drop-down or selector and not necessarily the number of products to be added (as there can already be products in the cart).
     *
     */
    useEffect(() => {
    }, [selectedPricingOption, shoppingCart]);
    /**
     * If the shopping cart or the currently selected pricing option changes, then we need to update the composite pricing options.
     * The pricing options for the bundle or dymamic kits (composite products) are set based on the selected pricing option for the
     * top-level product.
     *
     */
    useEffect(() => {
        // determine delta between cart and number seats
        if (taxonomyProduct) {
            try {
                // If we have a selected pricing option with a display term, and bundle pricing options are available..
                if ((selectedPricingOption === null || selectedPricingOption === void 0 ? void 0 : selectedPricingOption.displayTerm) && bundlePricingDictionary) {
                    setCompositeProductsPricingOptions(bundlePricingDictionary[selectedPricingOption.displayTerm] || []);
                    return;
                }
                // Note: this is to handle the scenario where all composites are returned, but without pricing options for the term for that country.
                // If a composite fails to fetch, we go into contact sales mode.
                setCompositeProductsPricingOptions(compositeProductsInfo === null || compositeProductsInfo === void 0 ? void 0 : compositeProductsInfo.map((product) => ({ productName: product.name })));
            }
            catch (error) {
                console.error(error);
            }
        }
    }, [shoppingCart, selectedPricingOption, bundlePricingDictionary]);
    /**
     * Setup the subscriptions to the disw-util modules. Note that these events are emitted via the modules being initialized via
     * window.disw.init which you'll find in the DOM. Modules like the country module will be initialized, the country will be detected
     * and the event emitted to inform subscribers such as us here.
     *
     */
    useEffect(() => {
        // We need the shopping cart info to figure out if there are currently seats in the cart to hand to the front-end to allow them to set the drop-down on load
        const unsubscribeCart = disw.cart.subscribeToCart((shoppingCart, errorContext) => {
            if (shoppingCart) {
                setShoppingCart(shoppingCart);
            }
        });
        // Subscribe to country updates to update the country/cart if a country was selected
        const unsubscribeCountry = disw.country.subscribeToCountry((countryObject, closeSelected) => {
            // If the user selected a country, and didn't just close the modal, set the country
            if (!closeSelected) {
                // Fetch product info only if the product sku is present and product buy now url is not.
                if (taxonomyProduct && taxonomyProduct.sku && !(taxonomyProduct === null || taxonomyProduct === void 0 ? void 0 : taxonomyProduct.buyNowUrl)) {
                    // If the country changes, we need to re-fetch the product information and update the pricing options which are specific to the product
                    setSelectedPricingOption(undefined);
                    setPricingOptions(undefined);
                    // fetch product
                    fetchProductInfo(taxonomyProduct.sku);
                }
            }
        });
        return () => {
            unsubscribeCountry();
            unsubscribeCart();
        };
    }, []);
    // Setup the transformer for standardizing all of the objects returned for compatibility with ECBE.
    const [transformer] = useState(() => new DigitalExchangeTransformer());
    const processCompositeProductsItems = (compositeProductsInfo, compositeProductsPricingOptions) => {
        if (!compositeProductsInfo || compositeProductsInfo.length === 0) {
            return [];
        }
        if (!compositeProductsPricingOptions || compositeProductsPricingOptions.length === 0) {
            return [];
        }
        return compositeProductsInfo === null || compositeProductsInfo === void 0 ? void 0 : compositeProductsInfo.map((compositeProduct, index) => {
            // Find the pricing option for the composite product based on the index
            const productPricingOptions = compositeProductsPricingOptions === null || compositeProductsPricingOptions === void 0 ? void 0 : compositeProductsPricingOptions[index];
            // Find the number of seats for the composite product from the compositeNumberSeatsMap
            let compositeNumberSeats = (productPricingOptions === null || productPricingOptions === void 0 ? void 0 : productPricingOptions.id) ? compositeNumberSeatsMap[productPricingOptions.id] || 0 : 0; // Default to 0 if not set
            if (compositeProduct.required && compositeNumberSeats === 0) {
                // If the product is required and numberSeats is 0, set seats to 1
                compositeNumberSeats = 1; // If the product is required and numberSeats is 0, set seats to 1
            }
            // Find the quantity to add for the composite product based on the shopping cart and the number of seats selected
            // const compositeQuantityToAdd = determineQuantityToAdd(productPricingOptions?.productName ?? '', shoppingCart, productPricingOptions?.id ?? '', compositeNumberSeats);
            // Prepare the composite product data to be returned to the consumer
            return {
                // The name of the composite product
                productName: (productPricingOptions === null || productPricingOptions === void 0 ? void 0 : productPricingOptions.productName) || '',
                // The pricing option for the composite product
                pricingOption: transformer.transformPricingOption(productPricingOptions) || {},
                // Number of seats in the drop-down for a specific composite product
                numberSeats: compositeNumberSeats || 0,
                // Function to select the number of seats (selected in the drop-down, numeric stepper or otherwise)
                // Uses the product id to update the number of seats for the specific composite product
                setNumberSeats: (seats) => (productPricingOptions === null || productPricingOptions === void 0 ? void 0 : productPricingOptions.id) && updateCompositeNumberSeats(productPricingOptions.id, seats),
                // Represents the number of seats currently in the cart minus the drop-down selection for the composite product
                quantityToAdd: 0,
                // The minimum quantity for the composite product (if required or the product is a bundle, set to 1, otherwise use the minimum quantity from the API)
                minimumQuantity: (compositeProduct.required || productType === ProductType.BUNDLE ? '1' : compositeProduct.minimumQuantity) || '0',
                // The maximum quantity for the composite product (uses the maximum quantity from the API)
                maximumQuantity: (compositeProduct === null || compositeProduct === void 0 ? void 0 : compositeProduct.productSku) === 'SIMATIEDL' ? '1' : compositeProduct.maximumQuantity || '-1',
                // Tracks if the composite product is required to be in the bundle/kit (inferred from quantity > 0)
                required: compositeProduct.required,
            };
        });
    };
    /**
     * Any of the JSON objects returned from the API are transformed to a generic object that is to be used in the front-end.
     * We squash any unnecessary properties, and rename for clarity / consistency with our upcoming platform shift.
     *
     */
    const hookResponse = {
        // Should be transformed
        product: Object.assign(Object.assign({}, transformer.transformProduct(product)), { 
            // Number of seats in the drop-down (initially it will be the 1 or the number of current licenses in the cart for the pricing term)
            numberSeats,
            // Function to select the number of seats (selected in the drop-down, numeric stepper or otherwise)
            setNumberSeats,
            // Represents the number of seats currently in the cart minus the drop-down selection (user has 3 licenses in cart, sets number seats to 4, quantity to add is 1)
            quantityToAdd, 
            // Indicate if the product is current loading or not
            productLoading: (taxonomyProduct === null || taxonomyProduct === void 0 ? void 0 : taxonomyProduct.sku) ? productLoading : false, 
            // Transformed selected pricing option (selected from the list of available pricing options)
            selectedPricingOption: transformer.transformPricingOption(selectedPricingOption), 
            // Transformed pricing options
            pricingOptions: pricingOptions === null || pricingOptions === void 0 ? void 0 : pricingOptions.map((option) => transformer.transformPricingOption(option)), 
            // Transformed payment options
            paymentOptions: paymentOptions === null || paymentOptions === void 0 ? void 0 : paymentOptions.map((option) => transformer.transformPaymentOption(option)) }),
        compositeProducts: Object.assign({}, transformer.transformCartCompositeProducts({
            /**
             * Generic array of composite products including their pricing options
             * Below are the properties of each item in the composite products array:
             * productName: The name of the composite product
             * pricingOption: The pricing option for the composite product
             * numberSeats: Number of seats in the drop-down for a specific composite product
             * setNumberSeats: Function to select the number of seats (selected in the drop-down, numeric stepper or otherwise). Uses the product id to update the number of seats for the specific composite product
             * quantityToAdd: Represents the number of seats currently in the cart minus the drop-down selection for the composite product
             * minimumQuantity: The minimum quantity for the composite product (if required, set to 1, otherwise use the minimum quantity from the API)
             * maximumQuantity: The maximum quantity for the composite product (uses the maximum quantity from the API)
             * required: Tracks if the composite product is required to be in the bundle/kit (inferred from quantity > 0)
             */
            items: processCompositeProductsItems(compositeProductsInfo, compositeProductsPricingOptions),
            // Booleans to indicate state for UI handling
            compositeProductsLoading,
        })),
        // Note that pricing options are selected once for both the top-level product and child composites (bundles and kits).
        selectPricingOption,
        // Function to allow consumer to determine if the product has a pricing term available
        doesProductHavePricingTerm,
        // Function for adding up the prices of all composite products
        sumCompositeProductsPrices,
        // Function to determine the parameters for adding to cart based on the product type
        determineAddToCartParameters,
        // Return booleans to indicate if there are errors fetching the individual product or list of composites for UI handling
        errorInFetchingProduct,
        errorInFetchingComposites,
    };
    return hookResponse;
};

/**
 * A custom hook for managing the state of multiple products, similar to the `useProduct` hook but designed for handling multiple products.
 * This hook provides state management for product information, pricing options, seat quantities, and more.
 *
 * @param {Object} params - The parameters for the hook.
 * @param {TaxonomyProduct[]} params.products - The list of selected products to manage.
 * @param {DexShoppingCart} params.cartData - The shopping cart data used for managing product quantities and selections.
 * @param {string} params.country - The country code used for fetching product information and pricing.
 * @param {string} params.dataId - The data identifier used for analytics tracking.
 *
 * @returns {Object} - The state and handlers for managing multiple products.
 * @property {Array} productInfo - The information for each product.
 * @property {Array} selectedPricingOptions - The currently selected pricing options for each product.
 * @property {Array} availablePricingOptions - The available pricing options for each product.
 * @property {Array<boolean>} useAnnualPricing - Indicates whether annual pricing is selected for each product.
 * @property {Array<number>} numberSeats - The number of seats/licenses for each product.
 * @property {Function} setNumberSeats - A setter function for updating the number of seats/licenses.
 * @property {Array<number>} quantityToAdd - The quantity to add to the cart for each product.
 * @property {Array<boolean>} productLoading - Indicates whether product information is being loaded for each product.
 * @property {number} numberErrors - The number of errors encountered during product management.
 * @property {Function} setNumberErrors - A setter function for updating the number of errors.
 * @property {Array<number>} minLicenses - The minimum number of licenses allowed for each product.
 * @property {Array<number>} maxLicenses - The maximum number of licenses allowed for each product.
 * @property {Array} currentProducts - The current list of products being managed.
 * @property {Function} handleMonthlyYearlyAndThreeYearSelection - A handler for selecting monthly, yearly, or three-year pricing options.
 */
const useProducts = ({ products: selectedProducts, cartData, country, dataId, }) => {
    /**
     * We want to define a useProducts hook, which is very similar to the useProduct hook but is capable of managing multiple products state instead of only one.
     * Note that we don't need to refactor much of the logic, simply cut and paste logic between this layer and our @disw/ecommerce package.
     *
     * The hook should take in a list of products (selectedProducts) and manage the state for the set. It should return the properties in an array,
     * so that they can be referenced by index in the component.
     *
     */
    const [productInfo, setProductInfo] = useState(Array.from(Array(3)));
    const [selectedPricingOptions, setSelectedPricingOptions] = useState(Array.from(Array(3)));
    const [availablePricingOptions, setAvailablePricingOptions] = useState(Array.from(Array(3)));
    const [useAnnualPricing, setUseAnnualPricing] = useState([true, true, true]); // use Yearly by default
    const [numberSeats, setNumberSeats] = useState([1, 1, 1]);
    const [quantityToAdd, setQuantityToAdd] = useState([0, 0, 0]);
    const [productLoading, setProductLoading] = useState([false, false, false]);
    const [numberErrors, setNumberErrors] = useState(0);
    const [minLicenses, setMinLicenses] = useState(Array.from(Array(3)).fill(DEFAULT_MIN_LICENSES));
    const [maxLicenses, setMaxLicenses] = useState(Array.from(Array(3)).fill(DEFAULT_MAX_LICENSES));
    const [currentProducts, setCurrentProducts] = useState(Array.from(Array(3)));
    // I think the currentCountry is being doubly managed. We should use the country prop coming in.
    const [currentCountry, setCurrentCountry] = useState(null);
    const getArrayWithValueAtIndex = (array, index, value) => array.map((item, itemIndex) => (itemIndex === index ? value : item));
    /**
     * Update the number of seats from the items in the cart.
     *
     * TODO: move to useProducts hook
     *
     */
    function updateSeatsFromSelectedProducts() {
        // If we have cart data, update the number of seats
        if (cartData) {
            // Create a new array to hole the seats so that we only need to call setNumberSeats once
            const newNumberSeats = [...numberSeats];
            // Loop through each selectedProduct and update the seats for it with the number of licences in the cart, if there are any
            selectedProducts.forEach((product, productIndex) => {
                // const existingLicenses = findItemInCartByProductId(product.sku, cartData)?.quantity;
                // newNumberSeats[productIndex] = existingLicenses ? existingLicenses : numberSeats[productIndex];
            });
            setNumberSeats(newNumberSeats);
        }
    }
    /**
     * Update the number of seats when changes are made to the cart
     *
     * TODO: move to useProducts hook
     *
     */
    useEffect(() => {
        updateSeatsFromSelectedProducts();
    }, [cartData, selectedPricingOptions]);
    /*
     * Load the product pricing any time the selected products change, or country changes
     *
     * TODO: move to useProducts hook
     *
     */
    useEffect(() => {
        const trackingObjects = [];
        selectedProducts.forEach((product, productIndex) => {
            var _a;
            // Fetch the product info (product information & pricing) if it has a SKU
            if (product.sku) {
                if (country !== currentCountry || JSON.stringify(product) !== JSON.stringify(currentProducts[productIndex])) {
                    // need to set these values to null when the cart changes, else the delay in state changes will cause issues in UI
                    setSelectedPricingOptions((prev) => getArrayWithValueAtIndex(prev, productIndex, undefined));
                    setAvailablePricingOptions((prev) => getArrayWithValueAtIndex(prev, productIndex, undefined));
                    // fetch product
                    fetchProductInfo(product.sku, productIndex);
                }
            }
            // If the product does not contain a SKU replace the product price element with NULL
            // This will cause the product to be shown as "contact sales"
            if (!product.sku) {
                setProductInfo((prev) => getArrayWithValueAtIndex(prev, productIndex, undefined));
                setSelectedPricingOptions((prev) => getArrayWithValueAtIndex(prev, productIndex, undefined));
                setAvailablePricingOptions((prev) => getArrayWithValueAtIndex(prev, productIndex, undefined));
            }
            // Get the current URL
            const currentUrl = window.location.href;
            // Parse the URL using URL API
            const url = new URL(currentUrl);
            // Create a new tracking object
            const trackingObject = {
                name: product.name,
                sku: product.sku,
                image_url: (_a = product === null || product === void 0 ? void 0 : product.image) === null || _a === void 0 ? void 0 : _a.url,
                url: `${url.origin}${url.pathname}`,
                product_id: product.sku,
            };
            // Push the new tracking object to the array
            trackingObjects.push(trackingObject);
        });
        productListViewed({ productData: trackingObjects, contentId: dataId });
        // Reset pricing selection if country was updated
        if (country !== currentCountry) {
            setUseAnnualPricing([true, true, true]);
        }
        //These are to track the changes of country and products that are on display on the page.
        setCurrentProducts([...selectedProducts]);
        // TODO: I think setting the country here can go away if we instead use the country input prop, but we'll need to test.
        setCurrentCountry(country);
    }, [selectedProducts, country]);
    /**
     * TODO: Hoist this to the @disw/ecommerce layer, so that it is responsible for determining quantity to add
     *
     */
    useEffect(() => {
        // loop through selected products and set the newQuantitesToAdd
        const newQuantitiesToAdd = new Array(selectedProducts.length);
        selectedProducts.forEach((product, productIndex) => {
            // newQuantitiesToAdd[productIndex] = determineQuantityToAdd(
            //   product.sku,
            //   cartData,
            //   selectedPricingOptions[productIndex]?.id,
            //   numberSeats[productIndex]
            // );
        });
        setQuantityToAdd(newQuantitiesToAdd);
    }, [cartData, numberSeats, selectedProducts, selectedPricingOptions]);
    // TODO: Hoist this to the @disw/ecommerce layer, so that it is responsible for determining quantity to add
    const handleMonthlyYearlyAndThreeYearSelection = (displayTerm, productIndex) => {
        if (availablePricingOptions[productIndex]) {
            // Update selected pricing option
            setSelectedPricingOptions((prev) => getArrayWithValueAtIndex(prev, productIndex, availablePricingOptions[productIndex].find((obj) => {
                return obj.displayTerm === displayTerm;
            })));
        }
    };
    /**
     * Fetch the product information via SKU. Note that country is not passed explicitly, but is detected & passed to the API.
     * @param {*} productSKU - representing the product being queried.
     */
    const fetchProductInfo = (productSKU, productIndex) => {
        // Set the current product loading state
        setProductLoading((prev) => getArrayWithValueAtIndex(prev, productIndex, true));
        // cart.getProductInfo({productSku: productSKU, compareTable: true})
        //   .then((newProductInfo: DexProduct) => {
        //     if (newProductInfo) {
        //       if (productSKU === 'SIMATIEDL') {
        //         newProductInfo.maximumQuantity = "1";
        //       }
        //       setProductInfo((prev) => getArrayWithValueAtIndex(prev, productIndex, newProductInfo));
        //       const minQuantity = parseInt(newProductInfo.minimumQuantity ?? DEFAULT_MIN_LICENSES);
        //       const maxQuantity = parseInt(newProductInfo.maximumQuantity ?? DEFAULT_MAX_LICENSES);
        //       if (newProductInfo.pricingOptions) {
        //         // get all possible pricing options where autoRenew == true, by not supplying function with a term we get all types with this call
        //         const parsedPricingOptions = cart.getPricingOptions({ pricingOptions: newProductInfo.pricingOptions });
        //         // assign as an arry for code functionality reasons
        //         setAvailablePricingOptions((prev) =>
        //           getArrayWithValueAtIndex(
        //             prev,
        //             productIndex,
        //             Array.isArray(parsedPricingOptions) ? parsedPricingOptions : [parsedPricingOptions]
        //           )
        //         );
        //         // If it is an array, we know that there is a monthly/yearly option. Need to set value to be based on users choice/default
        //         if (parsedPricingOptions) {
        //           if (Array.isArray(parsedPricingOptions)) {
        //             // Handle the selection of Yearly or Monthly
        //             handleMonthlyOrYearlySelection(
        //               useAnnualPricing[productIndex] ? constants.ANNUAL_SUBSCRIPTION_DISPLAY_TERM : constants.MONTHLY_SUBSCRIPTION_DISPLAY_TERM,
        //               parsedPricingOptions,
        //               productIndex
        //             );
        //           } else {
        //             // Determine monthly or annual to keep state accurate
        //             setUseAnnualPricing((prev) =>
        //               getArrayWithValueAtIndex(prev, productIndex, parsedPricingOptions.term === constants.ANNUAL_SUBSCRIPTION_TERM)
        //             );
        //             // only one option, so we set to that
        //             setSelectedPricingOptions((prev) => getArrayWithValueAtIndex(prev, productIndex, parsedPricingOptions));
        //           }
        //         }
        //       }
        //       if (maxQuantity > 0) {
        //         setMaxLicenses((prev) => getArrayWithValueAtIndex(prev, productIndex, newProductInfo.maximumQuantity));
        //       }
        //       if (minQuantity > 1 && minQuantity < maxQuantity) {
        //         setMinLicenses((prev) => getArrayWithValueAtIndex(prev, productIndex, newProductInfo.minimumQuantity));
        //       }
        //       // If we fetch product information, we should set the drop-down initially to the number of licenses in the cart (if exists)
        //       // This handles the scenario where the user changes the product in the drop-down which triggers a refetch.
        //       updateSeatsFromSelectedProducts();
        //     }
        //   })
        //   .catch((error: unknown) => {
        //     // If an error is caught fetching product info, treat as such - clear values
        //     setProductInfo((prev) => getArrayWithValueAtIndex(prev, productIndex, undefined));
        //     setSelectedPricingOptions((prev) => getArrayWithValueAtIndex(prev, productIndex, undefined));
        //   })
        //   .finally(() => {
        //     // Regardless if success or fail, remove the loading indicator
        //     setProductLoading((prev) => getArrayWithValueAtIndex(prev, productIndex, false));
        //   });
    };
    return {
        productInfo,
        selectedPricingOptions,
        availablePricingOptions,
        useAnnualPricing,
        numberSeats,
        setNumberSeats,
        quantityToAdd,
        productLoading,
        numberErrors,
        setNumberErrors,
        minLicenses,
        maxLicenses,
        currentProducts,
        handleMonthlyYearlyAndThreeYearSelection,
    };
};

export { DEFAULT_MAX_LICENSES, DEFAULT_MIN_LICENSES, ShoppingCartProvider, addProductToCart, addProductToCartGuest, applyPromotionCode, applyPromotionCodeGuest, assignContactInfoToCart, assignECAAdmins, calculateSalesTaxForCart, checkForAccount, checkoutStepCompleted, checkoutStepViewed, clearCountryCache, constants, createShippingAddress, deleteShippingAddress, fetchCartData, fetchCartDataGuest, fetchCommerceCountryConfig, fetchProductInfo, formatPrice, getAllCountries, getCartCheckoutServiceUrl, getCartEnv, getCartPreRequisites, getCartReview, getContactInfoForAccount, getCurrentUserCartLocale, getCurrentUserCurrency, getECADetails, getECommerceSupportedCountries, getEcaInformation, getInvoices, getOrderDetails, getOrderReviewData, getOrderSummary, getPOAPayersByEmail, getPaymentDetails, getPaymentMethodsForCart, getSubscriptionList, getSuggestions, getTenantConfiguration, getTenantDetails, getUserInfo, orderCompleted, payWithPOA, poaCreditCheck, postFrontendEcommerceEvent, postRequestForQuote, preauthorizePayment, preserveCartDataGuest, productClicked, registerPaymentDetails, registerPurchaseOrderNumber, removeFromCart, removeItemFromCart, removeItemFromCartGuest, removePromotionCode, removePromotionCodeGuest, requiresECA, sendPaymentData, sendPaymentDetails, setDefaultShippingAddress, startCheckout, submitPayment, submitTenantInfo, submitUserInfo, toggleFullCart, toggleMiniCart, updateCartData, updatePaymentMethod, updateProductQuantity, updateProductQuantityGuest, updateShippingAddress, updateSubscription, useCheckoutCart, useCommerceButton, useProduct, useProductECBE, useProducts, useShoppingCart, useShoppingCartECBE, validateContactInfo, validateTenant };
